import { featuredEquipment, type Equipment } from "@/data/mockData";
import { buildApiUrl } from "@/lib/apiBase";
import { buildEquipmentLink } from "@/lib/equipmentLinks";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { getEquipmentImage } from "@/lib/equipmentImageCache";
import { createListingRecord, updateListingRecord } from "@/lib/listingsApi";
import { defaultMarketplaceCategoryConfig, normalizeMarketplaceCategoryConfig, type MarketplaceCategoryConfig } from "@/lib/marketplaceCategories";
import { fetchMyUserData, syncUserDataSnapshot, type UserDataSnapshot } from "@/lib/userDataApi";
import { fetchWorkflowState, mergeWorkflowState, type WorkflowStateSnapshot } from "@/lib/workflowStateApi";
import { verifyCertificateId } from "./elraVerification";

export type AppRole = "lessee" | "lessor" | "agent" | "provider" | "oem" | "admin" | "accountant" | "user";
export type AdStatus = "pending" | "live" | "paused" | "rejected";
export type AdType = "display" | "featured";

export interface AuthState {
  loggedIn: boolean;
  user: string;
  role: AppRole;
  token?: string;
  isPreview?: boolean;
  actualUser?: string;
  actualRole?: AppRole;
}

export interface AdCampaign {
  id: string;
  owner: string;
  title: string;
  adType: AdType;
  assetUrl: string;
  destinationUrl: string;
  durationDays: number;
  location: "homepage" | "marketplace" | "dashboard" | "sitewide";
  position: "leaderboard" | "square" | "inline" | "footer";
  creativeMode?: "image" | "text";
  subtitle?: string;
  ctaLabel?: string;
  spendCredits: number;
  status: AdStatus;
  createdAt: string;
  approvedAt?: string;
  remainingDurationDays?: number;
  lastEditedAt?: string;
}

export interface AdMetricEvent {
  id: string;
  campaignId: string;
  owner: string;
  type: "view" | "click";
  viewer: string;
  viewerRole: AppRole | "guest";
  location: string;
  device: "mobile" | "tablet" | "desktop";
  createdAt: string;
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  createdAt: string;
  coverImage?: string;
  seoTitle?: string;
  seoDescription?: string;
  tags?: string[];
  status?: "draft" | "published";
}

export interface SupportConfig {
  phone: string;
  email: string;
  whatsapp: string;
}

export interface SupportRequest {
  id: string;
  name: string;
  email: string;
  phone?: string;
  issueType: string;
  message: string;
  user?: string;
  status: "New" | "In progress" | "Resolved";
  createdAt: string;
  adminNote?: string;
}

export interface WebsiteSettings {
  websiteName: string;
  websiteLogo: string;
  websiteIcon: string;
  footerLogo: string;
  favicon: string;
  siteTitle: string;
  metaTags: string;
  websiteDescription: string;
  googleAnalyticsCode: string;
  associations?: any[];
}

export interface FavoriteEquipment extends Equipment {
  user: string;
  savedAt: string;
}

export type MembershipTier = "Standard" | "Bronze" | "Silver" | "Gold" | "Platinum";
export type ApprovalStatus = "approved" | "pending";

export interface PaymentGatewayConfig {
  provider: "paystack";
  enabled: boolean;
  publicKey: string;
  secretKey: string;
  secretKeyMasked?: string;
  callbackUrl: string;
}

export interface PlatformSettings {
  commission: string;
  supportEmail: string;
  reviewWindow: string;
  escrowEnabled: boolean;
  pricing: {
    Bronze: { price: string; cycle: "month" | "year" };
    Silver: { price: string; cycle: "month" | "year" };
    Gold: { price: string; cycle: "month" | "year" };
    Platinum: { price: string; cycle: "month" | "year" };
  };
  paymentGateway: PaymentGatewayConfig;
  offlinePayment: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    instructions: string;
  };
  emailSettings: {
    enabled: boolean;
    mailer: "smtp";
    scheme: string;
    smtpHost: string;
    smtpPort: string;
    encryption: "none" | "ssl" | "tls";
    secure: boolean;
    smtpUser: string;
    smtpPass: string;
    smtpPassMasked?: string;
    fromName: string;
    fromEmail: string;
    replyTo: string;
    notifications: {
      kyc: boolean;
      transactions: boolean;
      activities: boolean;
    };
  };
  rewards: {
    welcomeBonusEnabled: boolean;
    welcomeBoostCredits: number;
    premiumDiscountPercent: number;
    welcomeWindowDays: number;
    referralBonusEnabled: boolean;
    referralWalletAmount: number;
    dailyVisitBonusEnabled: boolean;
    dailyVisitCreditAmount: number;
    dailyVisitGlobalLimit: number;
  };
  marketplaceCategories: MarketplaceCategoryConfig;
}

export interface MembershipPackage {
  price: string;
  cycle: "month" | "year";
  includedCredits: number;
  equipmentLimit: string;
  serviceLimit: string;
}

export interface UserProfile {
  user: string;
  fullName?: string;
  email?: string;
  phone?: string;
  location?: string;
  business?: string;
  nin?: string;
  dateOfBirth?: string;
  bvn?: string;
  governmentIdUrl?: string;
  businessDocumentUrl?: string;
  avatarUrl?: string;
  avatarType?: "person" | "company-logo";
  avatarVerified?: boolean;
  role?: AppRole;
  referredBy?: string;
  membershipTier?: MembershipTier;
  approvalStatus?: ApprovalStatus;
  listingAccessDisabled?: boolean;
  registeredAt?: string;
  welcomeDiscountUsed?: boolean;
  payoutDetails?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    businessName?: string;
  };
  banned?: boolean;
  deleted?: boolean;
}

export interface StorefrontCustomization {
  bannerUrl?: string;
  updatedAt: string;
}

export interface WalletEntry {
  id: string;
  user: string;
  type: "deposit" | "credit-purchase" | "commission" | "payout-request" | "payout-reversal" | "escrow-release" | "escrow-refund" | "bonus" | "membership-payment" | "verification-payment";
  direction: "credit" | "debit";
  amount: number;
  note: string;
  createdAt: string;
}

export interface EscrowRecord {
  id: string;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  customer: string;
  requestType?: "lease" | "purchase" | "service";
  amount: string;
  status: "Held" | "Awaiting Release" | "Under Review" | "Released" | "Refunded";
  createdAt: string;
  requestId?: string;
  ownerDeliveryStatus?: "Pending" | "Delivered";
  customerReceiptStatus?: "Pending" | "Received";
  deliveryUpdatedAt?: string;
  releaseAfter?: string;
  adminAttentionRequired?: boolean;
  attentionNotifiedAt?: string;
}

export interface PayoutRequest {
  id: string;
  agent: string;
  amount: string;
  status: "Pending" | "Processing" | "Approved" | "On Hold" | "Rejected" | "Paid";
  requestedAt: string;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  businessName?: string;
  walletType?: "main" | "escrow" | "referral";
  note?: string;
  adminNote?: string;
}

export interface MembershipProfile {
  user: string;
  tier: MembershipTier;
  price: string;
  includedCredits: number;
  equipmentLimit: string;
  serviceLimit: string;
  activeSince: string;
  expiresAt?: string;
  cancellationStatus?: "none" | "scheduled";
  cancellationRequestedAt?: string;
  cancellationEffectiveAt?: string;
}

export interface ReferralProfile {
  owner: string;
  code: string;
  link: string;
  signUps: number;
  upgrades: number;
  earnedCredits: number;
  walletRewards?: number;
  referredUsers: string[];
  rewardedUsers?: string[];
  referredUserDetails?: Array<{
    user: string;
    fullName?: string;
    email?: string;
    registeredAt?: string;
    kycCompleted: boolean;
    rewardQualified: boolean;
    approvalGranted?: boolean;
  }>;
}

export interface MembershipActivationRequest {
  id: string;
  user: string;
  tier: MembershipTier;
  amount: string;
  cycle: "month" | "year";
  requestedAt: string;
  paymentStatus: "Pending" | "Confirmed" | "Rejected";
  adminStatus: "Pending" | "Approved" | "Rejected";
  reference: string;
}

export interface PaymentRecord {
  id: string;
  user: string;
  reference: string;
  purpose: "membership" | "wallet-topup" | "transaction";
  amount: number;
  amountLabel: string;
  status: "Pending" | "Completed" | "Failed";
  provider: "Paystack" | "Admin" | "Bank Transfer";
  note: string;
  tier?: MembershipTier;
  createdAt: string;
}

export interface TransactionReceiptAttachment {
  url: string;
  fileName: string;
  reference: string;
  amountLabel?: string;
}

export interface OfflinePaymentProof {
  id: string;
  user: string;
  reference: string;
  amount: number;
  amountLabel: string;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  paymentType: "purchase" | "lease";
  pricingMode: "daily lease" | "lease to use" | "lease to own" | "purchase";
  proofUrl: string;
  proofName: string;
  note: string;
  status: "Pending" | "Approved" | "Rejected";
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  requestId?: string;
}

export interface NotificationItem {
  id: string;
  user: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning";
  link?: string;
  read: boolean;
  createdAt: string;
}

export interface RentalAgreement {
  id: string;
  requestId: string;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  customer: string;
  possessionHolder: string;
  status: "Active" | "Completed" | "Pending";
  agreementType: string;
  baseAmount: string;
  negotiatedAmount: string;
  startDate: string;
  endDate: string;
  leaseTerms: string;
  ownerPhone: string;
  customerPhone: string;
  deliveryLocation: string;
  ownerDeliveryStatus?: "Pending" | "Delivered";
  customerReceiptStatus?: "Pending" | "Received";
  deliveryUpdatedAt?: string;
  adminAttentionRequired?: boolean;
}

export interface LeaseAgreementCertificate {
  id: string;
  agreementId: string;
  user: string;
  equipmentTitle: string;
  certificateName: string;
  certificateNumber: string;
  certificateUrl?: string;
  verificationStatus: "Verified" | "Not Verified";
  verificationMessage: string;
  verificationUrl?: string;
  expiryDate?: string;
  verifiedAt?: string;
  status: "Under review" | "Approved" | "Rejected";
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  adminNote?: string;
}

export interface UserReport {
  id: string;
  agreementId: string;
  equipmentTitle: string;
  reportedBy: string;
  targetUser: string;
  reason: string;
  severity: "Minor" | "Distress" | "Critical";
  complaint: string;
  status: "Open" | "Reviewing" | "Resolved" | "Escalated to Agent";
  createdAt: string;
  assignedAgent?: string;
}

const AUTH_KEY = "elra_auth";
const ROLE_KEY = "elra_role";
const USER_KEY = "elra_user";
const AUTH_TOKEN_KEY = "elra_auth_token";
const DRAFTS_KEY = "elra_listing_drafts";
const CHAT_KEY = "elra_chat_threads";
const ADS_KEY = "elra_ad_campaigns";
const AD_ANALYTICS_KEY = "elra_ad_analytics_events";
const AD_CREDITS_KEY = "elra_ad_credits";
const BLOG_KEY = "elra_blog_posts";
const SUPPORT_KEY = "elra_support_config";
const SUPPORT_REQUESTS_KEY = "elra_support_requests";
const WEBSITE_SETTINGS_KEY = "elra_website_settings";
const ACCREDITATION_KEY = "elra_user_accreditation";
const USER_KYC_KEY = "elra_user_kyc";
const EQUIPMENT_VERIFICATION_KEY = "elra_equipment_verification";
const USER_PROFILES_KEY = "elra_user_profiles";
const FAVORITES_KEY = "elra_favorite_equipment";
const TRANSACTION_REQUESTS_KEY = "elra_transaction_requests";
const MEMBERSHIP_KEY = "elra_membership_profiles";
const REFERRAL_KEY = "elra_referral_profiles";
const WALLET_KEY = "elra_wallet_balances";
const WALLET_HISTORY_KEY = "elra_wallet_history";
const ESCROW_KEY = "elra_escrow_records";
const PAYOUT_KEY = "elra_payout_requests";
const SERVICE_REVIEW_KEY = "elra_service_reviews";
const EQUIPMENT_REVIEW_KEY = "elra_equipment_reviews";
const MARKETPLACE_REQUESTS_KEY = "elra_marketplace_requests";
const MARKETPLACE_REQUEST_THREADS_KEY = "elra_marketplace_request_threads";
const STOREFRONT_CUSTOMIZATION_KEY = "elra_storefront_customizations";
const PLATFORM_SETTINGS_KEY = "elra_platform_settings";
const NOTIFICATIONS_KEY = "elra_notifications";
const RENTAL_AGREEMENTS_KEY = "elra_rental_agreements";
const LEASE_AGREEMENT_CERTIFICATES_KEY = "elra_lease_agreement_certificates";
const USER_REPORTS_KEY = "elra_user_reports";
const MEMBERSHIP_REQUESTS_KEY = "elra_membership_requests";
const PAYMENT_RECORDS_KEY = "elra_payment_records";
const OFFLINE_PAYMENT_PROOFS_KEY = "elra_offline_payment_proofs";
const DAILY_VISIT_PROGRESS_KEY = "elra_daily_visit_progress";
const DAILY_VISIT_GLOBAL_KEY = "elra_daily_visit_global";
const LOCAL_AUTH_USERS_KEY = "elra_local_auth_users";
const PRODUCTION_LOCAL_RESET_KEY = "elra_production_local_reset_v1";

const PERSISTENT_BROWSER_KEYS = new Set([
  AUTH_KEY,
  ROLE_KEY,
  USER_KEY,
  AUTH_TOKEN_KEY,
  LOCAL_AUTH_USERS_KEY,
]);

const DATABASE_BACKED_BROWSER_KEYS = new Set([
  FAVORITES_KEY,
  USER_PROFILES_KEY,
  MEMBERSHIP_KEY,
  REFERRAL_KEY,
  WALLET_KEY,
  WALLET_HISTORY_KEY,
  AD_CREDITS_KEY,
  PAYMENT_RECORDS_KEY,
  OFFLINE_PAYMENT_PROOFS_KEY,
  NOTIFICATIONS_KEY,
  ESCROW_KEY,
  PAYOUT_KEY,
  MEMBERSHIP_REQUESTS_KEY,
  TRANSACTION_REQUESTS_KEY,
  RENTAL_AGREEMENTS_KEY,
  CHAT_KEY,
  LEASE_AGREEMENT_CERTIFICATES_KEY,
  USER_REPORTS_KEY,
  MARKETPLACE_REQUEST_THREADS_KEY,
  MARKETPLACE_REQUESTS_KEY,
  EQUIPMENT_VERIFICATION_KEY,
  DRAFTS_KEY,
  ACCREDITATION_KEY,
  USER_KYC_KEY,
  PLATFORM_SETTINGS_KEY,
  WEBSITE_SETTINGS_KEY,
  STOREFRONT_CUSTOMIZATION_KEY,
  SERVICE_REVIEW_KEY,
  EQUIPMENT_REVIEW_KEY,
  BLOG_KEY,
  SUPPORT_KEY,
  SUPPORT_REQUESTS_KEY,
  ADS_KEY,
  AD_ANALYTICS_KEY,
  DAILY_VISIT_PROGRESS_KEY,
  DAILY_VISIT_GLOBAL_KEY,
]);

const ALWAYS_VOLATILE_BROWSER_KEYS = new Set([
  WALLET_HISTORY_KEY,
  PAYMENT_RECORDS_KEY,
  OFFLINE_PAYMENT_PROOFS_KEY,
  NOTIFICATIONS_KEY,
  ESCROW_KEY,
  PAYOUT_KEY,
  MEMBERSHIP_REQUESTS_KEY,
  TRANSACTION_REQUESTS_KEY,
  RENTAL_AGREEMENTS_KEY,
  LEASE_AGREEMENT_CERTIFICATES_KEY,
  USER_REPORTS_KEY,
  MARKETPLACE_REQUESTS_KEY,
  EQUIPMENT_VERIFICATION_KEY,
  AD_ANALYTICS_KEY,
  SUPPORT_REQUESTS_KEY,
]);

const WALLET_HISTORY_LIMIT = 50;
const PAYMENT_RECORD_LIMIT = 120;
const OFFLINE_PAYMENT_PROOF_LIMIT = 120;
const AD_ANALYTICS_EVENT_LIMIT = 1500;

const volatileBrowserStorage = new Map<string, string>();

const safeLocalStorage = {
  getItem(key: string) {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string) {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Ignore storage failures (private mode, disabled storage, quota, etc.)
    }
  },
  removeItem(key: string) {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  },
  key(index: number) {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage.key(index);
    } catch {
      return null;
    }
  },
  length() {
    if (typeof window === "undefined") return 0;
    try {
      return window.localStorage.length;
    } catch {
      return 0;
    }
  },
};

const shouldPersistBrowserKey = (key: string) => (
  !ALWAYS_VOLATILE_BROWSER_KEYS.has(key)
  && (
    PERSISTENT_BROWSER_KEYS.has(key)
    || !import.meta.env.PROD
    || !DATABASE_BACKED_BROWSER_KEYS.has(key)
  )
);

const browserStorage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
  getItem(key) {
    if (typeof window === "undefined") {
      return null;
    }

    if (shouldPersistBrowserKey(key)) {
      return safeLocalStorage.getItem(key);
    }

    if (volatileBrowserStorage.has(key)) {
      return volatileBrowserStorage.get(key) ?? null;
    }

    const migratedValue = safeLocalStorage.getItem(key);

    if (migratedValue !== null) {
      volatileBrowserStorage.set(key, migratedValue);
      safeLocalStorage.removeItem(key);
    }

    return migratedValue;
  },
  setItem(key, value) {
    if (typeof window === "undefined") {
      return;
    }

    if (shouldPersistBrowserKey(key)) {
      safeLocalStorage.setItem(key, value);
      return;
    }

    volatileBrowserStorage.set(key, value);
    safeLocalStorage.removeItem(key);
  },
  removeItem(key) {
    if (typeof window === "undefined") {
      return;
    }

    volatileBrowserStorage.delete(key);
    safeLocalStorage.removeItem(key);
  },
};

const clearBrowserLaunchResidue = (preserveAdminSession = true) => {
  if (typeof window === "undefined") {
    return 0;
  }

  const storedAuth = safeLocalStorage.getItem(AUTH_KEY);
  const storedRole = safeLocalStorage.getItem(ROLE_KEY);
  const storedUser = safeLocalStorage.getItem(USER_KEY);
  const storedToken = safeLocalStorage.getItem(AUTH_TOKEN_KEY);
  const shouldPreserveAdminSession = preserveAdminSession && storedRole === "admin";
  let removed = 0;

  for (let index = safeLocalStorage.length() - 1; index >= 0; index -= 1) {
    const key = safeLocalStorage.key(index);
    if (!key || !key.startsWith("elra_")) continue;
    if (key === PRODUCTION_LOCAL_RESET_KEY) continue;
    safeLocalStorage.removeItem(key);
    removed += 1;
  }

  volatileBrowserStorage.clear();

  if (shouldPreserveAdminSession) {
    if (storedAuth !== null) safeLocalStorage.setItem(AUTH_KEY, storedAuth);
    if (storedRole !== null) safeLocalStorage.setItem(ROLE_KEY, storedRole);
    if (storedUser !== null) safeLocalStorage.setItem(USER_KEY, storedUser);
    if (storedToken !== null) safeLocalStorage.setItem(AUTH_TOKEN_KEY, storedToken);
  }

  safeLocalStorage.setItem(PRODUCTION_LOCAL_RESET_KEY, new Date().toISOString());
  return removed;
};

const applyProductionLocalReset = () => {
  if (typeof window === "undefined" || !import.meta.env.PROD) {
    return;
  }

  if (safeLocalStorage.getItem(PRODUCTION_LOCAL_RESET_KEY)) {
    return;
  }

  clearBrowserLaunchResidue(true);
};

applyProductionLocalReset();

type LegacyBrowserAuthUser = {
  user: string;
  role: AppRole;
};

const safeParse = <T,>(value: string | null, fallback: T): T => {
  if (!value) return fallback;

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const canSyncUserData = (user: string, token?: string) => {
  const auth = getAuthState();
  const authToken = token || auth.token;
  const normalizedUser = String(user || "").trim().toLowerCase();
  const actingUser = String(auth.actualUser || auth.user || "").trim().toLowerCase();

  return Boolean(authToken) && Boolean(normalizedUser) && (auth.role === "admin" || actingUser === normalizedUser);
};

const buildUserDataSnapshot = (user: string): UserDataSnapshot => ({
  profile: {
    ...getUserProfile(user),
    membershipTier: getMembershipProfile(user).tier,
  } as unknown,
  state: {
    membership: getMembershipProfile(user) as unknown,
    referral: (getReferralProfiles()[user] || undefined) as unknown,
    walletBalance: getWalletBalance(user),
    walletHistory: getWalletTransactions(user) as unknown[],
    paymentRecords: getPaymentRecords().filter((item) => item.user === user) as unknown[],
    offlinePaymentProofs: getOfflinePaymentProofStore().filter((item) => item.user === user || item.owner === user) as unknown[],
    adCredits: getAdCredits(user),
    favorites: getStoredFavorites().filter((item) => item.user === user) as unknown[],
    serviceReviews: getAllStoredServiceReviews().filter((item) => item.user === user) as unknown[],
    equipmentReviews: getAllStoredEquipmentReviews().filter((item) => item.user === user) as unknown[],
    storefrontCustomizations: getStorefrontCustomizationStore() as Record<string, unknown>,
    dailyVisitProgress: getDailyVisitProgressStore()[user] as unknown,
  },
});

const applyUserDataSnapshot = (snapshot?: UserDataSnapshot | null) => {
  if (typeof window === "undefined" || !snapshot) return;

  const snapshotProfile = snapshot.profile as Partial<UserProfile> | undefined;
  const snapshotMembership = snapshot.state?.membership as Partial<MembershipProfile> | undefined;
  const userKey = String(snapshotProfile?.user || snapshotMembership?.user || "").trim().toLowerCase();
  if (!userKey) return;

  if (snapshotProfile) {
    const profiles = safeParse<Record<string, UserProfile>>(browserStorage.getItem(USER_PROFILES_KEY), {});
    profiles[userKey] = {
      ...(profiles[userKey] || {}),
      ...(snapshotProfile as UserProfile),
      user: userKey,
    };
    browserStorage.setItem(USER_PROFILES_KEY, JSON.stringify(profiles));
  }

  if (snapshot.state?.membership && Object.keys(snapshot.state.membership).length > 0) {
    const membership = getMembershipProfiles();
    membership[userKey] = snapshot.state.membership as unknown as MembershipProfile;
    browserStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(membership));
  }

  if (snapshot.state?.referral && Object.keys(snapshot.state.referral).length > 0) {
    const referrals = getReferralProfiles();
    referrals[userKey] = snapshot.state.referral as unknown as ReferralProfile;
    browserStorage.setItem(REFERRAL_KEY, JSON.stringify(referrals));
  }

  if (typeof snapshot.state?.walletBalance === "number") {
    const balances = safeParse<Record<string, number>>(browserStorage.getItem(WALLET_KEY), {});
    balances[userKey] = snapshot.state.walletBalance;
    browserStorage.setItem(WALLET_KEY, JSON.stringify(balances));
  }

  if (Array.isArray(snapshot.state?.walletHistory)) {
    const entries = getWalletHistory().filter((entry) => entry.user !== userKey);
    browserStorage.setItem(WALLET_HISTORY_KEY, JSON.stringify([...(snapshot.state.walletHistory as unknown as WalletEntry[]), ...entries].slice(0, WALLET_HISTORY_LIMIT)));
  }

  if (Array.isArray(snapshot.state?.paymentRecords)) {
    const records = getPaymentRecords().filter((item) => item.user !== userKey);
    savePaymentRecords([...(snapshot.state.paymentRecords as unknown as PaymentRecord[]), ...records]);
  }

  if (Array.isArray(snapshot.state?.offlinePaymentProofs)) {
    const proofs = getOfflinePaymentProofStore().filter((item) => item.user !== userKey && item.owner !== userKey);
    saveOfflinePaymentProofStore([...(snapshot.state.offlinePaymentProofs as unknown as OfflinePaymentProof[]), ...proofs]);
  }

  if (typeof snapshot.state?.adCredits === "number") {
    const credits = safeParse<Record<string, number>>(browserStorage.getItem(AD_CREDITS_KEY), {});
    credits[userKey] = snapshot.state.adCredits;
    browserStorage.setItem(AD_CREDITS_KEY, JSON.stringify(credits));
  }

  if (Array.isArray((snapshot.state as Record<string, unknown> | undefined)?.favorites)) {
    const existing = getStoredFavorites().filter((item) => item.user !== userKey);
    browserStorage.setItem(FAVORITES_KEY, JSON.stringify([...
      ((snapshot.state as Record<string, unknown>).favorites as FavoriteEquipment[]),
      ...existing,
    ]));
  }

  if (Array.isArray((snapshot.state as Record<string, unknown> | undefined)?.serviceReviews)) {
    const existing = getAllStoredServiceReviews().filter((item) => item.user !== userKey);
    saveServiceReviewStore([...
      ((snapshot.state as Record<string, unknown>).serviceReviews as ServiceReview[]),
      ...existing,
    ]);
  }

  if (Array.isArray((snapshot.state as Record<string, unknown> | undefined)?.equipmentReviews)) {
    const existing = getAllStoredEquipmentReviews().filter((item) => item.user !== userKey);
    saveEquipmentReviewStore([...
      ((snapshot.state as Record<string, unknown>).equipmentReviews as EquipmentReview[]),
      ...existing,
    ]);
  }

  const storefrontCustomizations = (snapshot.state as Record<string, unknown> | undefined)?.storefrontCustomizations;
  if (storefrontCustomizations && typeof storefrontCustomizations === "object") {
    const current = getStorefrontCustomizationStore();
    browserStorage.setItem(STOREFRONT_CUSTOMIZATION_KEY, JSON.stringify({
      ...current,
      ...(storefrontCustomizations as Record<string, StorefrontCustomization>),
    }));
  }

  const dailyVisitProgress = (snapshot.state as Record<string, unknown> | undefined)?.dailyVisitProgress;
  if (dailyVisitProgress && typeof dailyVisitProgress === "object") {
    const store = getDailyVisitProgressStore();
    store[userKey] = dailyVisitProgress as DailyVisitProgress;
    saveDailyVisitProgressStore(store);
  }
};

export const syncUserDataForUser = async (user: string, token?: string) => {
  if (!canSyncUserData(user, token)) return null;
  const authToken = token || getAuthState().token;
  if (!authToken) return null;

  try {
    const response = await syncUserDataSnapshot(authToken, user, buildUserDataSnapshot(user));
    applyUserDataSnapshot(response.snapshot);
    return response.snapshot;
  } catch {
    return null;
  }
};

export const hydrateCurrentUserDataFromServer = async (token = getAuthState().token) => {
  if (!token) return null;

  const cacheKey = `user-data:${token}`;

  if (requestSyncCache.has(cacheKey) && requestSyncCache.get(cacheKey)!.expiresAt > Date.now()) {
    return requestSyncCache.get(cacheKey)!.value as UserDataSnapshot | null;
  }

  if (requestSyncPromiseCache.has(cacheKey)) {
    return requestSyncPromiseCache.get(cacheKey) as Promise<UserDataSnapshot | null>;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetchMyUserData(token);
      applyUserDataSnapshot(response.snapshot);
      setRequestSyncCache(cacheKey, response.snapshot, REQUEST_SYNC_CACHE_TTL_MS);
      return response.snapshot;
    } catch {
      return null;
    } finally {
      requestSyncPromiseCache.delete(cacheKey);
    }
  })();

  requestSyncPromiseCache.set(cacheKey, requestPromise as Promise<unknown>);
  return requestPromise;
};

const NOTIFICATIONS_UPDATED_EVENT = "elra-notifications-updated";
const WEBSITE_SETTINGS_UPDATED_EVENT = "elra-website-settings-updated";
const ADS_UPDATED_EVENT = "elra-ads-updated";
const AD_ANALYTICS_UPDATED_EVENT = "elra-ad-analytics-updated";
const PLATFORM_SETTINGS_UPDATED_EVENT = "elra-platform-settings-updated";
const MEMBERSHIP_PROFILE_UPDATED_EVENT = "elra-membership-profile-updated";
const WORKFLOW_STATE_UPDATED_EVENT = "elra-workflow-state-updated";
const PLATFORM_API_BASE = buildApiUrl("/api/platform");
const REQUEST_SYNC_CACHE_TTL_MS = 5000;
const requestSyncCache = new Map<string, { expiresAt: number; value: unknown }>();
const requestSyncPromiseCache = new Map<string, Promise<unknown>>();

const setRequestSyncCache = (key: string, value: unknown, ttlMs = REQUEST_SYNC_CACHE_TTL_MS) => {
  requestSyncCache.set(key, {
    expiresAt: Date.now() + ttlMs,
    value,
  });
};

const invalidateSessionSyncCache = (token = getAuthState().token) => {
  if (!token) return;

  requestSyncCache.delete(`user-data:${token}`);
  requestSyncCache.delete(`workflow:${token}`);
  requestSyncCache.delete(`session:${token}`);
  requestSyncPromiseCache.delete(`user-data:${token}`);
  requestSyncPromiseCache.delete(`workflow:${token}`);
  requestSyncPromiseCache.delete(`session:${token}`);
};

const emitWebsiteSettingsUpdated = (settings?: WebsiteSettings) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(WEBSITE_SETTINGS_UPDATED_EVENT, {
    detail: { settings },
  }));
};

const emitPlatformSettingsUpdated = (settings?: PlatformSettings) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PLATFORM_SETTINGS_UPDATED_EVENT, {
    detail: { settings },
  }));
};

const emitMembershipProfileUpdated = (user?: string, profile?: MembershipProfile) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MEMBERSHIP_PROFILE_UPDATED_EVENT, {
    detail: { user, profile },
  }));
};

const emitWorkflowStateUpdated = (snapshot?: WorkflowStateSnapshot) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(WORKFLOW_STATE_UPDATED_EVENT, {
    detail: { snapshot },
  }));
};

const emitAdsUpdated = (ads?: AdCampaign[]) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ADS_UPDATED_EVENT, {
    detail: { ads },
  }));
};

const emitAdAnalyticsUpdated = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AD_ANALYTICS_UPDATED_EVENT));
};

export const syncSupportConfigFromServer = async () => {
  if (typeof window === "undefined") return defaultSupportConfig;

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/support-config`);
    if (!response.ok) {
      throw new Error("Unable to fetch support configuration.");
    }

    const payload = await response.json();
    const next = {
      ...defaultSupportConfig,
      ...(payload.support || {}),
    };
    browserStorage.setItem(SUPPORT_KEY, JSON.stringify(next));
    return next;
  } catch {
    return getSupportConfig();
  }
};

const getStoredNotifications = () => {
  if (typeof window === "undefined") return [] as NotificationItem[];
  return [...safeParse<NotificationItem[]>(browserStorage.getItem(NOTIFICATIONS_KEY), [])];
};

const getStoredChatThreads = () => {
  if (typeof window === "undefined") return {} as Record<string, ChatMessage[]>;
  return safeParse<Record<string, ChatMessage[]>>(browserStorage.getItem(CHAT_KEY), {});
};

const getStoredLeaseAgreementCertificates = () => {
  if (typeof window === "undefined") return [] as LeaseAgreementCertificate[];
  return [...safeParse<LeaseAgreementCertificate[]>(browserStorage.getItem(LEASE_AGREEMENT_CERTIFICATES_KEY), [])];
};

const getStoredUserReports = () => {
  if (typeof window === "undefined") return [] as UserReport[];
  return [...safeParse<UserReport[]>(browserStorage.getItem(USER_REPORTS_KEY), [])];
};

const getStoredMarketplaceRequestConversations = () => {
  if (typeof window === "undefined") return [] as MarketplaceRequestConversation[];
  return safeParse<MarketplaceRequestConversation[]>(browserStorage.getItem(MARKETPLACE_REQUEST_THREADS_KEY), []);
};

const isSeededDemoId = (value: string) => {
  const id = String(value || "").trim().toLowerCase();
  return id.startsWith("demo-")
    || id.startsWith("sup-demo-")
    || id.startsWith("ver-demo-")
    || id.startsWith("srv-demo-")
    || id.startsWith("eqr-demo-");
};

type ListingDraftRecord = Record<string, string> & { id: string };

const defaultDemoListingDrafts: ListingDraftRecord[] = [];

const mergeDemoRecords = <T extends { id: string }>(defaults: T[], stored: T[]) => {
  const merged = new Map<string, T>();
  defaults.forEach((item) => merged.set(item.id, item));
  stored.forEach((item) => merged.set(item.id, item));
  return [...merged.values()];
};

const getStoredMarketplaceRequests = () => {
  if (typeof window === "undefined") return defaultMarketplaceRequests;
  const stored = safeParse<MarketplaceRequest[]>(browserStorage.getItem(MARKETPLACE_REQUESTS_KEY), [])
    .filter((item) => !isSeededDemoId(item.id));
  return mergeDemoRecords(defaultMarketplaceRequests, stored);
};

const getStoredEquipmentVerificationRequests = () => {
  if (typeof window === "undefined") return defaultVerificationRecords;
  const stored = safeParse<EquipmentVerificationRecord[]>(browserStorage.getItem(EQUIPMENT_VERIFICATION_KEY), [])
    .filter((item) => !isSeededDemoId(item.requestId));
  const merged = new Map<string, EquipmentVerificationRecord>();
  defaultVerificationRecords.forEach((item) => merged.set(item.requestId, item));
  stored.forEach((item) => merged.set(item.requestId, item));
  return [...merged.values()];
};

const getStoredListingDrafts = () => {
  if (typeof window === "undefined") return defaultDemoListingDrafts;
  const stored = safeParse<Array<Record<string, string>>>(browserStorage.getItem(DRAFTS_KEY), []);
  const normalizedStored: ListingDraftRecord[] = stored.map((draft) => ({
    ...draft,
    id: String(draft.id || draft.equipmentId || draft.savedAt || ""),
  })).filter((draft) => !isSeededDemoId(draft.id));
  return mergeDemoRecords(defaultDemoListingDrafts, normalizedStored);
};

const getStoredAccreditationProfiles = () => {
  if (typeof window === "undefined") return [] as UserAccreditationProfile[];
  return Object.values(safeParse<Record<string, UserAccreditationProfile>>(browserStorage.getItem(ACCREDITATION_KEY), {}));
};

const getStoredUserKycProfiles = () => {
  if (typeof window === "undefined") return [] as UserKycProfile[];
  return Object.values(safeParse<Record<string, UserKycProfile>>(browserStorage.getItem(USER_KYC_KEY), {}));
};

const emitNotificationsUpdated = (item?: NotificationItem, action: "push" | "read" = "push") => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, {
    detail: { item, action },
  }));
};

const buildWorkflowStateSnapshot = (): WorkflowStateSnapshot => {
  if (typeof window === "undefined") return {};

  return {
    notifications: getStoredNotifications(),
    escrows: safeParse<EscrowRecord[]>(browserStorage.getItem(ESCROW_KEY), defaultEscrowRecords),
    payouts: safeParse<PayoutRequest[]>(browserStorage.getItem(PAYOUT_KEY), defaultPayoutRequests),
    membershipRequests: safeParse<MembershipActivationRequest[]>(browserStorage.getItem(MEMBERSHIP_REQUESTS_KEY), []),
    transactionRequests: safeParse<TransactionRequest[]>(browserStorage.getItem(TRANSACTION_REQUESTS_KEY), defaultTransactionRequests),
    rentalAgreements: safeParse<RentalAgreement[]>(browserStorage.getItem(RENTAL_AGREEMENTS_KEY), []),
    chatThreads: getStoredChatThreads(),
    leaseAgreementCertificates: getStoredLeaseAgreementCertificates(),
    userReports: getStoredUserReports(),
    marketplaceRequestConversations: getStoredMarketplaceRequestConversations(),
    marketplaceRequests: getStoredMarketplaceRequests(),
    equipmentVerificationRequests: getStoredEquipmentVerificationRequests(),
    listingDrafts: getStoredListingDrafts(),
    accreditationProfiles: getStoredAccreditationProfiles(),
    userKycProfiles: getStoredUserKycProfiles(),
    dailyVisitGlobalState: getDailyVisitGlobalState(),
  };
};

const applyWorkflowStateSnapshot = (snapshot?: WorkflowStateSnapshot | null) => {
  if (typeof window === "undefined" || !snapshot) return;

  if (Array.isArray(snapshot.notifications)) {
    browserStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(snapshot.notifications.slice(0, 80)));
  }

  if (Array.isArray(snapshot.escrows)) {
    browserStorage.setItem(ESCROW_KEY, JSON.stringify(snapshot.escrows));
  }

  if (Array.isArray(snapshot.payouts)) {
    browserStorage.setItem(PAYOUT_KEY, JSON.stringify(snapshot.payouts));
  }

  if (Array.isArray(snapshot.membershipRequests)) {
    browserStorage.setItem(MEMBERSHIP_REQUESTS_KEY, JSON.stringify(snapshot.membershipRequests));
  }

  if (Array.isArray(snapshot.transactionRequests)) {
    browserStorage.setItem(TRANSACTION_REQUESTS_KEY, JSON.stringify(snapshot.transactionRequests));
  }

  if (Array.isArray(snapshot.rentalAgreements)) {
    browserStorage.setItem(RENTAL_AGREEMENTS_KEY, JSON.stringify(snapshot.rentalAgreements));
  }

  if (snapshot.chatThreads && typeof snapshot.chatThreads === "object" && !Array.isArray(snapshot.chatThreads)) {
    browserStorage.setItem(CHAT_KEY, JSON.stringify(snapshot.chatThreads));
  }

  if (Array.isArray(snapshot.leaseAgreementCertificates)) {
    browserStorage.setItem(LEASE_AGREEMENT_CERTIFICATES_KEY, JSON.stringify(snapshot.leaseAgreementCertificates));
  }

  if (Array.isArray(snapshot.userReports)) {
    browserStorage.setItem(USER_REPORTS_KEY, JSON.stringify(snapshot.userReports));
  }

  if (Array.isArray(snapshot.marketplaceRequestConversations)) {
    browserStorage.setItem(MARKETPLACE_REQUEST_THREADS_KEY, JSON.stringify(snapshot.marketplaceRequestConversations));
  }

  if (Array.isArray(snapshot.marketplaceRequests)) {
    browserStorage.setItem(MARKETPLACE_REQUESTS_KEY, JSON.stringify(snapshot.marketplaceRequests));
  }

  if (Array.isArray(snapshot.equipmentVerificationRequests)) {
    browserStorage.setItem(EQUIPMENT_VERIFICATION_KEY, JSON.stringify(snapshot.equipmentVerificationRequests));
  }

  if (Array.isArray(snapshot.listingDrafts)) {
    browserStorage.setItem(DRAFTS_KEY, JSON.stringify(snapshot.listingDrafts));
  }

  if (Array.isArray(snapshot.accreditationProfiles)) {
    const profiles = Object.fromEntries(
      (snapshot.accreditationProfiles as UserAccreditationProfile[])
        .filter((profile) => profile?.user)
        .map((profile) => [profile.user, profile]),
    );
    browserStorage.setItem(ACCREDITATION_KEY, JSON.stringify(profiles));
  }

  if (Array.isArray(snapshot.userKycProfiles)) {
    const profiles = Object.fromEntries(
      (snapshot.userKycProfiles as UserKycProfile[])
        .filter((profile) => profile?.user)
        .map((profile) => [profile.user, profile]),
    );
    browserStorage.setItem(USER_KYC_KEY, JSON.stringify(profiles));
  }

  if (snapshot.dailyVisitGlobalState && typeof snapshot.dailyVisitGlobalState === "object" && !Array.isArray(snapshot.dailyVisitGlobalState)) {
    browserStorage.setItem(DAILY_VISIT_GLOBAL_KEY, JSON.stringify(snapshot.dailyVisitGlobalState));
  }
};

export const syncWorkflowState = async (payload?: WorkflowStateSnapshot) => {
  const auth = getAuthState();
  if (!auth.token) return null;

  try {
    const response = await mergeWorkflowState(auth.token, payload || buildWorkflowStateSnapshot());
    // Do NOT apply response.state back to localStorage here.
    // Applying a stale PATCH response can overwrite correct local state due to
    // race conditions between concurrent fire-and-forget PATCHes (e.g. a
    // notification PATCH arriving after a KYC PATCH and carrying an older
    // server snapshot).  hydrateWorkflowStateFromServer (GET) is the sole
    // mechanism for pulling authoritative state from the server.
    return response.state;
  } catch {
    return null;
  }
};

export const hydrateWorkflowStateFromServer = async (token = getAuthState().token) => {
  if (!token) return null;

  const cacheKey = `workflow:${token}`;

  if (requestSyncCache.has(cacheKey) && requestSyncCache.get(cacheKey)!.expiresAt > Date.now()) {
    return requestSyncCache.get(cacheKey)!.value as WorkflowStateSnapshot | null;
  }

  if (requestSyncPromiseCache.has(cacheKey)) {
    return requestSyncPromiseCache.get(cacheKey) as Promise<WorkflowStateSnapshot | null>;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetchWorkflowState(token);
      applyWorkflowStateSnapshot(response.state);
      setRequestSyncCache(cacheKey, response.state, REQUEST_SYNC_CACHE_TTL_MS);
      return response.state;
    } catch {
      return null;
    } finally {
      requestSyncPromiseCache.delete(cacheKey);
    }
  })();

  requestSyncPromiseCache.set(cacheKey, requestPromise as Promise<unknown>);
  return requestPromise;
};

export const hydrateSessionDataFromServer = async (token = getAuthState().token) => {
  if (!token) return { userData: null, workflow: null };

  const cacheKey = `session:${token}`;

  if (requestSyncCache.has(cacheKey) && requestSyncCache.get(cacheKey)!.expiresAt > Date.now()) {
    return requestSyncCache.get(cacheKey)!.value as { userData: UserDataSnapshot | null; workflow: WorkflowStateSnapshot | null };
  }

  if (requestSyncPromiseCache.has(cacheKey)) {
    return requestSyncPromiseCache.get(cacheKey) as Promise<{ userData: UserDataSnapshot | null; workflow: WorkflowStateSnapshot | null }>;
  }

  const requestPromise = (async () => {
    const [userData, workflow] = await Promise.all([
      hydrateCurrentUserDataFromServer(token),
      hydrateWorkflowStateFromServer(token),
    ]);

    const result = { userData, workflow };
    setRequestSyncCache(cacheKey, result, REQUEST_SYNC_CACHE_TTL_MS);
    requestSyncPromiseCache.delete(cacheKey);
    return result;
  })();

  requestSyncPromiseCache.set(cacheKey, requestPromise as Promise<unknown>);
  return requestPromise;
};

export const getNotificationsForUser = (user = getAuthState().user, role = getAuthState().role) => {
  if (!user) return [] as NotificationItem[];
  return getStoredNotifications().filter((item) => item.user === user || (role === "admin" && item.user === "Admin"));
};

export const getUnreadNotificationCount = (user = getAuthState().user, role = getAuthState().role) => (
  getNotificationsForUser(user, role).filter((item) => !item.read).length
);

export const markNotificationAsRead = (id: string) => {
  if (typeof window === "undefined") return;
  const notifications = getStoredNotifications().map((item) => item.id === id ? { ...item, read: true } : item);
  browserStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
  emitNotificationsUpdated(notifications.find((item) => item.id === id), "read");
  void syncWorkflowState({ notifications });
};

const getEmailNotificationCategory = (item: Pick<NotificationItem, "title" | "message" | "link">) => {
  const text = `${item.title} ${item.message} ${item.link || ""}`.toLowerCase();
  if (/(kyc|verification|accreditation|tier 1|tier 2)/.test(text)) return "kyc" as const;
  if (/(payment|escrow|transfer|wallet|transaction|invoice|membership)/.test(text)) return "transactions" as const;
  return "activities" as const;
};

const queueNotificationEmail = (item: NotificationItem) => {
  if (typeof window === "undefined") return;

  const settings = getPlatformSettings();
  if (!settings.emailSettings.enabled) return;

  const category = getEmailNotificationCategory(item);
  if (!settings.emailSettings.notifications[category]) return;

  const recipientProfile = getUserProfile(item.user);
  const recipientEmail = recipientProfile.email || (String(item.user).toLowerCase() === "admin" ? settings.supportEmail : "");
  if (!recipientEmail) return;

  const recipientName = recipientProfile.business || recipientProfile.fullName || item.user;
  const actionUrl = item.link
    ? `${window.location.origin}${item.link.startsWith("/") ? item.link : `/${item.link}`}`
    : `${window.location.origin}/dashboard`;

  fetch(`${PLATFORM_API_BASE}/mail/notify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      to: recipientEmail,
      recipientName,
      title: item.title,
      subject: `Equipment Marketplace • ${item.title}`,
      message: item.message,
      category,
      type: item.type,
      actionUrl,
      actionLabel: category === "transactions" ? "Open payment center" : category === "kyc" ? "Open verification status" : "Open dashboard",
    }),
  }).catch(() => undefined);
};

export const pushNotification = ({
  user,
  title,
  message,
  type = "info",
  link,
  skipServerSync = false,
}: {
  user: string;
  title: string;
  message: string;
  type?: NotificationItem["type"];
  link?: string;
  skipServerSync?: boolean;
}) => {
  if (typeof window === "undefined" || !user) return;
  const notifications = getStoredNotifications();
  const nextNotification: NotificationItem = {
    id: `NOTIF-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    user,
    title,
    message,
    type,
    link,
    read: false,
    createdAt: new Date().toISOString(),
  };
  notifications.unshift(nextNotification);
  browserStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications.slice(0, 80)));
  emitNotificationsUpdated(nextNotification, "push");
  queueNotificationEmail(nextNotification);
  if (!skipServerSync) {
    void syncWorkflowState({ notifications: notifications.slice(0, 80) });
  }
};

const ensureAdCredits = (user: string) => {
  if (typeof window === "undefined" || !user) return 0;

  const credits = safeParse<Record<string, number>>(browserStorage.getItem(AD_CREDITS_KEY), {});

  if (typeof credits[user] !== "number") {
    credits[user] = 5;
    browserStorage.setItem(AD_CREDITS_KEY, JSON.stringify(credits));
  }

  return credits[user];
};

const ensureWalletBalance = (user: string) => {
  if (typeof window === "undefined" || !user) return 0;

  const balances = safeParse<Record<string, number>>(browserStorage.getItem(WALLET_KEY), {});

  if (typeof balances[user] !== "number") {
    balances[user] = 0;
    browserStorage.setItem(WALLET_KEY, JSON.stringify(balances));
  }

  return balances[user];
};

const getWalletHistory = () => {
  if (typeof window === "undefined") return [] as WalletEntry[];
  return safeParse<WalletEntry[]>(browserStorage.getItem(WALLET_HISTORY_KEY), []);
};

export const getWalletTransactions = (user = getAuthState().user) => getWalletHistory().filter((entry) => entry.user === user);

const saveWalletHistory = (entries: WalletEntry[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(WALLET_HISTORY_KEY, JSON.stringify(entries.slice(0, WALLET_HISTORY_LIMIT)));

  if (entries[0]?.user) {
    void syncUserDataForUser(entries[0].user);
  }
};

export const addWalletEntry = (payload: Omit<WalletEntry, "id" | "createdAt">) => {
  const entries = getWalletHistory();
  entries.unshift({
    ...payload,
    id: `WAL-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
  });
  saveWalletHistory(entries.slice(0, WALLET_HISTORY_LIMIT));
};

const parseCurrencyAmount = (value?: string) => {
  if (!value) return 0;
  const numeric = Number(value.replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatNaira = (value: number) => `₦${Math.round(value).toLocaleString()}`;
const formatAdvertWalletValue = (value: number) => formatNaira(value);
const DAY_MS = 24 * 60 * 60 * 1000;
const DAILY_VISIT_THRESHOLD_MS = 60 * 60 * 1000;

type DailyVisitProgress = {
  user: string;
  accumulatedMs: number;
  lastClaimAt?: string;
  popupUnlockedAt?: string;
};

type DailyVisitGlobalState = {
  windowStartedAt: string;
  claimsIssued: number;
};

const getDailyVisitProgressStore = () => {
  if (typeof window === "undefined") return {} as Record<string, DailyVisitProgress>;
  return safeParse<Record<string, DailyVisitProgress>>(browserStorage.getItem(DAILY_VISIT_PROGRESS_KEY), {});
};

const saveDailyVisitProgressStore = (payload: Record<string, DailyVisitProgress>) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(DAILY_VISIT_PROGRESS_KEY, JSON.stringify(payload));
};

const getDailyVisitGlobalState = (now = new Date()) => {
  if (typeof window === "undefined") {
    return { windowStartedAt: now.toISOString(), claimsIssued: 0 } as DailyVisitGlobalState;
  }

  const saved = safeParse<DailyVisitGlobalState>(browserStorage.getItem(DAILY_VISIT_GLOBAL_KEY), {
    windowStartedAt: now.toISOString(),
    claimsIssued: 0,
  });

  if (now.getTime() - new Date(saved.windowStartedAt).getTime() >= DAY_MS) {
    const reset = { windowStartedAt: now.toISOString(), claimsIssued: 0 };
    saveDailyVisitGlobalState(reset);
    return reset;
  }

  return saved;
};

const saveDailyVisitGlobalState = (payload: DailyVisitGlobalState) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(DAILY_VISIT_GLOBAL_KEY, JSON.stringify(payload));
  void syncWorkflowState({ dailyVisitGlobalState: payload as unknown as Record<string, unknown> });
};

const hasRegisteredEquipmentUpload = (user: string) => (
  getAllListingDrafts().some((item) => item.owner === user && ["uploaded", "pending", "approved"].includes(String(item.status || "").toLowerCase()) && item.elraRegistrationId?.trim())
);

export const getWelcomeBonusState = (user = getAuthState().user) => {
  const settings = getPlatformSettings();
  const profile = getUserProfile(user);
  const kyc = getUserKycProfile(user);
  const registeredAt = new Date(profile.registeredAt || new Date().toISOString()).getTime();
  const expiresAt = new Date(registeredAt + (settings.rewards.welcomeWindowDays * DAY_MS)).toISOString();
  const kycCompleted = kyc.autoApproved || kyc.tierOne.status === "approved" || kyc.tierTwo.status === "approved";
  const withinWindow = Date.now() <= new Date(expiresAt).getTime();

  return {
    enabled: settings.rewards.welcomeBonusEnabled,
    awarded: Boolean(kyc.welcomeBonusGranted),
    credits: settings.rewards.welcomeBoostCredits,
    discountPercent: settings.rewards.premiumDiscountPercent,
    expiresAt,
    canUseDiscount: settings.rewards.welcomeBonusEnabled && kycCompleted && withinWindow && !profile.welcomeDiscountUsed,
  };
};

const maybeAwardWelcomeBonus = (user: string) => {
  const settings = getPlatformSettings();
  const profile = getUserKycProfile(user);
  const kycCompleted = profile.autoApproved || profile.tierOne.status === "approved" || profile.tierTwo.status === "approved";

  if (!settings.rewards.welcomeBonusEnabled || !kycCompleted || profile.welcomeBonusGranted) {
    return profile;
  }

  addAdCredits(user, settings.rewards.welcomeBoostCredits);

  const updated = saveUserKycProfile({
    ...profile,
    welcomeBonusGranted: true,
  });

  pushNotification({
    user,
    title: "Welcome bonus unlocked",
    message: `You received ${settings.rewards.welcomeBoostCredits} boost credits after completing KYC.`,
    type: "success",
    link: "/dashboard/accreditation",
  });

  return updated;
};

const maybeRewardQualifiedReferral = (user: string) => {
  const settings = getPlatformSettings();
  const profile = getUserProfile(user);
  const kyc = getUserKycProfile(user);
  const kycCompleted = kyc.autoApproved || kyc.tierOne.status === "approved" || kyc.tierTwo.status === "approved";

  if (!settings.rewards.referralBonusEnabled || !profile.referredBy || !kycCompleted) {
    return null;
  }

  const referrerProfile = getAllReferralProfiles().find((item) => item.code === profile.referredBy || item.owner === profile.referredBy);
  if (!referrerProfile) return null;

  const rewardState = getUserReferralProfile(referrerProfile.owner);
  if (rewardState.rewardedUsers?.includes(user)) return rewardState;

  fundWallet(referrerProfile.owner, settings.rewards.referralWalletAmount, `Referral wallet bonus for ${user}`, "bonus");

  const recordProfiles = getReferralProfiles();
  const existingReferrer = recordProfiles[referrerProfile.owner] || referrerProfile;
  recordProfiles[referrerProfile.owner] = {
    ...existingReferrer,
    walletRewards: (existingReferrer.walletRewards || 0) + settings.rewards.referralWalletAmount,
    rewardedUsers: [...new Set([...(existingReferrer.rewardedUsers || []), user])],
    referredUserDetails: (existingReferrer.referredUserDetails || []).map((detail) => (
      detail.user === user || detail.email === user
        ? { ...detail, kycCompleted: true, rewardQualified: true }
        : detail
    )),
  };

  if (typeof window !== "undefined") {
    browserStorage.setItem(REFERRAL_KEY, JSON.stringify(recordProfiles));
  }

  pushNotification({
    user: referrerProfile.owner,
    title: "Referral bonus paid",
    message: `${formatNaira(settings.rewards.referralWalletAmount)} has been added to your wallet because ${user} completed KYC.`,
    type: "success",
    link: "/dashboard/referrals",
  });

  return getUserReferralProfile(referrerProfile.owner);
};

export const getDiscountedMembershipAmount = (tier: MembershipTier, user = getAuthState().user) => {
  const plan = getPlatformSettings().pricing[tier];
  const baseAmount = parseCurrencyAmount(plan.price);
  const welcome = getWelcomeBonusState(user);
  const accreditation = getUserAccreditationProfile(user);
  const hasVerifiedElraCertificate = tier !== "Standard"
    && Boolean(accreditation.certificateId)
    && accreditation.accreditationStatus === "Verified";

  const discountPercent = hasVerifiedElraCertificate
    ? 50
    : (tier !== "Standard" && welcome.canUseDiscount ? welcome.discountPercent : 0);

  if (discountPercent > 0) {
    const discountedAmount = Math.max(0, Math.round(baseAmount * ((100 - discountPercent) / 100)));
    return {
      amount: discountedAmount,
      label: formatNaira(discountedAmount),
      discounted: true,
      percent: discountPercent,
      originalAmount: baseAmount,
      originalLabel: plan.price,
    };
  }

  return {
    amount: baseAmount,
    label: formatNaira(baseAmount),
    discounted: false,
    percent: 0,
    originalAmount: baseAmount,
    originalLabel: plan.price,
  };
};

export const getDailyVisitBonusState = (user = getAuthState().user) => {
  const settings = getPlatformSettings();
  const store = getDailyVisitProgressStore();
  const progress = store[user] || { user, accumulatedMs: 0 };
  const now = Date.now();
  const global = getDailyVisitGlobalState(new Date(now));
  const lastClaimAt = progress.lastClaimAt ? new Date(progress.lastClaimAt).getTime() : 0;
  const claimCooldownComplete = !lastClaimAt || (now - lastClaimAt >= DAY_MS);

  return {
    enabled: settings.rewards.dailyVisitBonusEnabled,
    accumulatedMs: progress.accumulatedMs,
    progressPercent: Math.min(100, Math.round((progress.accumulatedMs / DAILY_VISIT_THRESHOLD_MS) * 100)),
    remainingMs: Math.max(0, DAILY_VISIT_THRESHOLD_MS - progress.accumulatedMs),
    claimable: settings.rewards.dailyVisitBonusEnabled && claimCooldownComplete && progress.accumulatedMs >= DAILY_VISIT_THRESHOLD_MS && global.claimsIssued < settings.rewards.dailyVisitGlobalLimit,
    rewardAmount: settings.rewards.dailyVisitCreditAmount,
    rewardCredits: settings.rewards.dailyVisitCreditAmount / 1000,
    lastClaimAt: progress.lastClaimAt,
    claimsRemainingToday: Math.max(0, settings.rewards.dailyVisitGlobalLimit - global.claimsIssued),
  };
};

export const recordDailyVisitProgress = (user = getAuthState().user, milliseconds = 0) => {
  if (typeof window === "undefined" || !user || milliseconds <= 0) return getDailyVisitBonusState(user);

  const store = getDailyVisitProgressStore();
  const existing = store[user] || { user, accumulatedMs: 0 };
  const lastClaimAt = existing.lastClaimAt ? new Date(existing.lastClaimAt).getTime() : 0;
  const resetWindow = lastClaimAt && (Date.now() - lastClaimAt >= DAY_MS) ? 0 : existing.accumulatedMs;

  store[user] = {
    ...existing,
    accumulatedMs: Math.min(DAILY_VISIT_THRESHOLD_MS, resetWindow + milliseconds),
  };

  saveDailyVisitProgressStore(store);
  return getDailyVisitBonusState(user);
};

export const claimDailyVisitBonus = (user = getAuthState().user) => {
  const settings = getPlatformSettings();
  const state = getDailyVisitBonusState(user);

  if (!settings.rewards.dailyVisitBonusEnabled) {
    return { ok: false, error: "Daily visit bonus is currently disabled." };
  }

  if (!state.claimable) {
    return { ok: false, error: "Daily visit bonus is not ready yet." };
  }

  const store = getDailyVisitProgressStore();
  const progress = store[user] || { user, accumulatedMs: DAILY_VISIT_THRESHOLD_MS };
  const global = getDailyVisitGlobalState();

  if (global.claimsIssued >= settings.rewards.dailyVisitGlobalLimit) {
    return { ok: false, error: "The daily advert wallet reward limit has been reached. Please try again after reset." };
  }

  addAdCredits(user, settings.rewards.dailyVisitCreditAmount / 1000);
  store[user] = {
    ...progress,
    accumulatedMs: 0,
    lastClaimAt: new Date().toISOString(),
    popupUnlockedAt: new Date().toISOString(),
  };
  saveDailyVisitProgressStore(store);
  saveDailyVisitGlobalState({
    ...global,
    claimsIssued: global.claimsIssued + 1,
  });

  pushNotification({
    user,
    title: "Daily visit reward claimed",
    message: `${formatAdvertWalletValue(settings.rewards.dailyVisitCreditAmount)} advert wallet value has been added to your account.`,
    type: "success",
    link: "/dashboard/wallet",
  });

  return { ok: true };
};

const getPaymentRecords = () => {
  if (typeof window === "undefined") return [] as PaymentRecord[];
  return [...safeParse<PaymentRecord[]>(browserStorage.getItem(PAYMENT_RECORDS_KEY), [])];
};

export const getAllPaymentRecords = () => getPaymentRecords();

const savePaymentRecords = (records: PaymentRecord[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(PAYMENT_RECORDS_KEY, JSON.stringify(records.slice(0, PAYMENT_RECORD_LIMIT)));

  if (records[0]?.user) {
    void syncUserDataForUser(records[0].user);
  }
};

export const updatePaymentRecord = (
  reference: string,
  payload: Partial<Pick<PaymentRecord, "status" | "note" | "amount" | "provider" | "tier">>,
) => {
  if (typeof window === "undefined" || !reference) return null;

  const records = getPaymentRecords();
  const target = records.find((item) => item.reference === reference);
  if (!target) return null;

  const updated = {
    ...target,
    ...payload,
    amount: Number.isFinite(Number(payload.amount)) ? Number(payload.amount) : target.amount,
    amountLabel: formatNaira(Number.isFinite(Number(payload.amount)) ? Number(payload.amount) : target.amount),
  } satisfies PaymentRecord;

  savePaymentRecords(records.map((item) => (item.reference === reference ? updated : item)));
  void syncUserDataForUser(updated.user);
  return updated;
};

export const markPaymentAttemptFailed = ({
  user,
  reference,
  purpose,
  amount,
  note,
  tier,
  provider = "Paystack",
}: {
  user: string;
  reference: string;
  purpose?: PaymentRecord["purpose"];
  amount?: number;
  note: string;
  tier?: MembershipTier;
  provider?: PaymentRecord["provider"];
}) => {
  if (!reference) return null;

  const existing = getPaymentRecords().find((item) => item.reference === reference);
  if (existing?.status === "Completed") {
    return existing;
  }

  return recordPaymentAttempt({
    user: user || existing?.user || "",
    reference,
    purpose: purpose || existing?.purpose || "transaction",
    amount: Math.max(0, Number(amount ?? existing?.amount ?? 0)),
    note,
    tier: tier || existing?.tier,
    provider: provider || existing?.provider || "Paystack",
    status: "Failed",
  });
};

export const removePaymentRecord = (reference: string) => {
  if (typeof window === "undefined" || !reference) return false;
  const records = getPaymentRecords();
  const target = records.find((item) => item.reference === reference);
  const nextRecords = records.filter((item) => item.reference !== reference);
  if (nextRecords.length === records.length) return false;
  savePaymentRecords(nextRecords);
  if (target?.user) {
    void syncUserDataForUser(target.user);
  }
  return true;
};

export const recordPaymentAttempt = (payload: {
  user: string;
  reference: string;
  purpose: PaymentRecord["purpose"];
  amount: number;
  note: string;
  tier?: MembershipTier;
  provider?: PaymentRecord["provider"];
  status?: PaymentRecord["status"];
}) => {
  if (typeof window === "undefined" || !payload.user || !payload.reference) return null;

  const records = getPaymentRecords();
  const existing = records.find((item) => item.reference === payload.reference);
  const next: PaymentRecord = {
    id: existing?.id || `PAYREC-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: existing?.createdAt || new Date().toISOString(),
    provider: payload.provider || existing?.provider || "Paystack",
    tier: payload.tier || existing?.tier,
    ...existing,
    user: payload.user,
    reference: payload.reference,
    purpose: payload.purpose,
    amount: payload.amount,
    amountLabel: formatNaira(payload.amount),
    status: payload.status || existing?.status || "Pending",
    note: payload.note,
  };

  const remaining = records.filter((item) => item.reference !== payload.reference);
  remaining.unshift(next);
  savePaymentRecords(remaining);
  return next;
};

export const getUserPaymentRecords = (user = getAuthState().user) => getPaymentRecords().filter((item) => item.user === user);

const getOfflinePaymentProofStore = () => {
  if (typeof window === "undefined") return [] as OfflinePaymentProof[];
  return [...safeParse<OfflinePaymentProof[]>(browserStorage.getItem(OFFLINE_PAYMENT_PROOFS_KEY), [])];
};

const saveOfflinePaymentProofStore = (records: OfflinePaymentProof[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(OFFLINE_PAYMENT_PROOFS_KEY, JSON.stringify(records.slice(0, OFFLINE_PAYMENT_PROOF_LIMIT)));

  if (records[0]?.user) {
    void syncUserDataForUser(records[0].user);
  }
};

const buildTransactionReceiptHtml = ({
  reference,
  amountLabel,
  equipmentTitle,
  requestType,
  payer,
  payee,
  channel,
}: {
  reference: string;
  amountLabel: string;
  equipmentTitle: string;
  requestType: "purchase" | "lease" | "service";
  payer: string;
  payee: string;
  channel: string;
}) => `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Transaction Receipt ${reference}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; padding: 28px; color: #111827; background: #f8fafc; }
      .card { max-width: 760px; margin: 0 auto; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 24px; }
      .brand { font-size: 22px; font-weight: 800; color: #ea580c; margin-bottom: 14px; }
      .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
      .item { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
      .label { font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: #64748b; }
      .value { margin-top: 6px; font-size: 14px; font-weight: 600; color: #0f172a; }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="brand">Equipment Marketplace Receipt</div>
      <p>Receipt for ${requestType} transaction processing.</p>
      <div class="meta">
        <div class="item"><div class="label">Reference</div><div class="value">${reference}</div></div>
        <div class="item"><div class="label">Amount</div><div class="value">${amountLabel}</div></div>
        <div class="item"><div class="label">Equipment</div><div class="value">${equipmentTitle}</div></div>
        <div class="item"><div class="label">Channel</div><div class="value">${channel}</div></div>
        <div class="item"><div class="label">Payer</div><div class="value">${payer}</div></div>
        <div class="item"><div class="label">Payee</div><div class="value">${payee}</div></div>
      </div>
      <p style="margin-top:16px;color:#475569;font-size:12px;">Generated at ${new Date().toISOString()}.</p>
    </div>
  </body>
</html>`;

const buildTransactionReceiptDataUrl = (payload: {
  reference: string;
  amountLabel: string;
  equipmentTitle: string;
  requestType: "purchase" | "lease" | "service";
  payer: string;
  payee: string;
  channel: string;
}) => {
  const html = buildTransactionReceiptHtml(payload);
  return `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
};

const appendTransactionReceiptMessage = ({
  request,
  attachment,
}: {
  request: TransactionRequest;
  attachment: TransactionReceiptAttachment;
}) => {
  const thread = getChatThread(request.id, []);
  if (thread.some((message) => message.receipt?.reference === attachment.reference)) {
    return;
  }

  saveChatThread(request.id, [
    ...thread,
    {
      sender: "system",
      text: `Transaction receipt added (${attachment.reference}). Preview or download it below.`,
      createdAt: new Date().toISOString(),
      receipt: attachment,
    },
  ]);
};

export const getOfflinePaymentProofs = () => getOfflinePaymentProofStore();

const finalizeMarketplaceTransactionPayment = ({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId,
  equipmentTitle,
  owner,
  requestType,
  pricingMode,
  channel = "Paystack",
  requestId,
}: {
  user: string;
  reference: string;
  amount: number;
  paidAmount?: number;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  requestType: "purchase" | "lease" | "service";
  pricingMode: "daily lease" | "lease to use" | "lease to own" | "purchase" | "service";
  channel?: PaymentRecord["provider"];
  requestId?: string;
}) => {
  if (!user || !reference || amount <= 0 || !equipmentRefId || !equipmentTitle || !owner) {
    return { ok: false, error: "Valid marketplace payment details are required." };
  }

  const existing = getUserPaymentRecords(user).find((item) => item.reference === reference && item.status === "Completed");
  if (existing) return { ok: true, alreadyProcessed: true, payment: existing };

  const payment = recordPaymentAttempt({
    user,
    reference,
    purpose: "transaction",
    amount: Math.max(0, paidAmount || amount),
    note: `${requestType === "service" ? "Service" : requestType === "lease" ? "Lease" : "Escrow purchase"} payment completed via ${channel} for ${equipmentTitle}.`,
    provider: channel,
    status: "Completed",
  });

  const existingRequest = requestId ? getAllTransactionRequests().find((item) => item.id === requestId) : undefined;
  const request = existingRequest || createTransactionRequest({
    requestType,
    pricingMode,
    equipmentRefId,
    equipmentTitle,
    owner,
    buyer: user,
    buyerPhone: getUserProfile(user).phone,
    buyerEmail: getUserProfile(user).email,
    baseAmount: formatNaira(amount),
    requestedAmount: formatNaira(amount),
    notes: `${requestType === "service" ? "Service" : requestType === "lease" ? "Lease" : "Purchase"} completed through the on-platform ${channel} settlement flow.`,
  });

  if (request && request.status !== "Accepted") {
    updateTransactionRequestStatus(request.id, "Accepted");
  }

  if (request) {
    const escrowExists = getEscrowRecords().some((item) => item.requestId === request.id || (item.customer === user && item.equipmentRefId === equipmentRefId && item.status !== "Refunded"));
    if (!escrowExists) {
      createEscrowRecord({
        requestId: request.id,
        equipmentRefId,
        equipmentTitle,
        owner,
        customer: user,
        requestType,
        amount: formatNaira(amount),
      });
    }

    appendTransactionReceiptMessage({
      request,
      attachment: {
        url: buildTransactionReceiptDataUrl({
          reference,
          amountLabel: formatNaira(Math.max(0, paidAmount || amount)),
          equipmentTitle,
          requestType,
          payer: user,
          payee: owner,
          channel,
        }),
        fileName: `transaction-receipt-${reference}.html`,
        reference,
        amountLabel: formatNaira(Math.max(0, paidAmount || amount)),
      },
    });
  }

  pushNotification({
    user,
    title: channel === "Bank Transfer"
      ? (requestType === "lease" ? "Transfer proof approved" : "Transfer proof approved")
      : (requestType === "service" ? "Service payment confirmed" : requestType === "lease" ? "Lease payment confirmed" : "Escrow purchase confirmed"),
    message: channel === "Bank Transfer"
      ? `Your bank-transfer proof for ${equipmentTitle} has been approved and your ${requestType} is now confirmed in escrow.`
      : requestType === "service"
        ? `Your secure service payment for ${equipmentTitle} has been confirmed and placed in escrow.`
      : requestType === "lease"
        ? `Your marketplace lease payment for ${equipmentTitle} has been confirmed and is now protected in escrow.`
        : `Your payment for ${equipmentTitle} was confirmed and added to your purchases with escrow protection.`,
    type: "success",
    link: requestType === "service" ? "/dashboard/service-requests" : requestType === "lease" ? "/dashboard/rentals" : "/dashboard/payments",
  });

  pushNotification({
    user: owner,
    title: channel === "Bank Transfer"
      ? "Offline payment approved"
      : (requestType === "service" ? "New service payment" : requestType === "lease" ? "New lease payment" : "New escrow purchase"),
    message: channel === "Bank Transfer"
      ? `${user}'s bank-transfer proof for ${equipmentTitle} has been approved and the settlement is now protected in escrow.`
      : requestType === "service"
        ? `${user} completed a protected ${channel} service payment for ${equipmentTitle}.`
      : requestType === "lease"
        ? `${user} completed a protected marketplace lease payment for ${equipmentTitle}.`
        : `${user} completed a protected ${channel} purchase for ${equipmentTitle}.`,
    type: "info",
    link: getRequestDashboardPath(requestType),
  });

  return { ok: true, payment, request };
};

export const submitOfflinePaymentProof = ({
  user,
  reference,
  amount,
  equipmentRefId,
  equipmentTitle,
  owner,
  paymentType,
  pricingMode,
  proofUrl,
  proofName,
  note,
}: {
  user: string;
  reference: string;
  amount: number;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  paymentType: "purchase" | "lease";
  pricingMode: "daily lease" | "lease to use" | "lease to own" | "purchase";
  proofUrl: string;
  proofName: string;
  note: string;
}) => {
  if (typeof window === "undefined") return { ok: false, error: "Offline proof upload is only available in the browser." };
  if (!user || !reference || amount <= 0 || !proofUrl.trim()) return { ok: false, error: "Upload a valid payment proof before continuing." };

  const request = createTransactionRequest({
    requestType: paymentType,
    pricingMode,
    equipmentRefId,
    equipmentTitle,
    owner,
    buyer: user,
    buyerPhone: getUserProfile(user).phone,
    buyerEmail: getUserProfile(user).email,
    baseAmount: formatNaira(amount),
    requestedAmount: formatNaira(amount),
    notes: `${note} Offline bank transfer proof submitted and awaiting admin confirmation.`,
  });

  if (!request) {
    return { ok: false, error: "You cannot submit payment proof for your own listing." };
  }

  recordPaymentAttempt({
    user,
    reference,
    purpose: "transaction",
    amount,
    note: `Offline ${paymentType} payment proof submitted for ${equipmentTitle}. Awaiting admin review.`,
    provider: "Bank Transfer",
    status: "Pending",
  });

  const submissions = getOfflinePaymentProofStore();
  const next: OfflinePaymentProof = {
    id: `OFFPAY-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    user,
    reference,
    amount,
    amountLabel: formatNaira(amount),
    equipmentRefId,
    equipmentTitle,
    owner,
    paymentType,
    pricingMode,
    proofUrl,
    proofName: proofName || "payment-proof",
    note,
    status: "Pending",
    submittedAt: new Date().toISOString(),
    requestId: request.id,
  };
  submissions.unshift(next);
  saveOfflinePaymentProofStore(submissions);

  appendTransactionReceiptMessage({
    request,
    attachment: {
      url: proofUrl,
      fileName: proofName || `offline-payment-proof-${reference}`,
      reference,
      amountLabel: formatNaira(amount),
    },
  });

  pushNotification({
    user: "Admin",
    title: "Offline payment proof submitted",
    message: `${user} submitted a ${paymentType} transfer receipt for ${equipmentTitle}.`,
    type: "info",
    link: "/admin/platform",
  });

  pushNotification({
    user,
    title: "Payment proof submitted",
    message: `Your ${paymentType} payment proof for ${equipmentTitle} is pending admin review.`,
    type: "success",
    link: "/dashboard/payments",
  });

  return { ok: true, submission: next, request };
};

export const reviewOfflinePaymentProof = (submissionId: string, decision: "Approved" | "Rejected") => {
  const submissions = getOfflinePaymentProofStore();
  const target = submissions.find((item) => item.id === submissionId);
  if (!target) return { ok: false, error: "Offline payment proof not found." };

  const updated = submissions.map((item) => item.id === submissionId ? {
    ...item,
    status: decision,
    reviewedAt: new Date().toISOString(),
    reviewedBy: getAuthState().user || "Admin",
  } : item);
  saveOfflinePaymentProofStore(updated);

  if (decision === "Approved") {
    const result = finalizeMarketplaceTransactionPayment({
      user: target.user,
      reference: target.reference,
      amount: target.amount,
      equipmentRefId: target.equipmentRefId,
      equipmentTitle: target.equipmentTitle,
      owner: target.owner,
      requestType: target.paymentType,
      pricingMode: target.pricingMode,
      channel: "Bank Transfer",
      requestId: target.requestId,
    });

    if (!result.ok) return result;
    return { ok: true, submission: updated.find((item) => item.id === submissionId), payment: result.payment };
  }

  recordPaymentAttempt({
    user: target.user,
    reference: target.reference,
    purpose: "transaction",
    amount: target.amount,
    note: `Offline payment proof for ${target.equipmentTitle} was rejected after review.`,
    provider: "Bank Transfer",
    status: "Failed",
  });

  if (target.requestId) {
    updateTransactionRequestStatus(target.requestId, "Rejected");
  }

  pushNotification({
    user: target.user,
    title: "Transfer proof rejected",
    message: `Your transfer proof for ${target.equipmentTitle} was rejected. Kindly contact support and re-submit if needed.`,
    type: "warning",
    link: "/support",
  });

  return { ok: true, submission: updated.find((item) => item.id === submissionId) };
};

export const processMembershipPaymentSuccess = ({
  user,
  reference,
  amount,
  tier,
  channel = "Paystack",
}: {
  user: string;
  reference: string;
  amount: number;
  tier?: MembershipTier;
  channel?: PaymentRecord["provider"];
}) => {
  if (!user || !reference || amount <= 0) return { ok: false, error: "Valid payment details are required." };

  const existing = getUserPaymentRecords(user).find((item) => item.reference === reference && item.status === "Completed");
  if (existing) return { ok: true, alreadyProcessed: true, payment: existing };

  const request = getMembershipUpgradeRequests().find((item) => item.reference === reference || (item.user === user && item.adminStatus === "Pending"));
  const resolvedTier = tier || request?.tier;
  if (!resolvedTier) return { ok: false, error: "Membership request not found for this payment." };

  const payment = recordPaymentAttempt({
    user,
    reference,
    purpose: "membership",
    amount,
    note: `${resolvedTier} membership paid successfully via ${channel}.`,
    tier: resolvedTier,
    provider: channel,
    status: "Completed",
  });

  if (request) {
    updateMembershipUpgradeRequest(request.id, { paymentStatus: "Confirmed" });
  } else {
    upgradeMembership(user, resolvedTier);
  }

  pushNotification({
    user,
    title: "Membership payment confirmed",
    message: `${resolvedTier} is now active on your account after successful payment confirmation.`,
    type: "success",
    link: "/dashboard/payments",
  });

  return { ok: true, payment };
};

export const processWalletTopUpSuccess = ({
  user,
  reference,
  amount,
  channel = "Paystack",
}: {
  user: string;
  reference: string;
  amount: number;
  channel?: PaymentRecord["provider"];
}) => {
  if (!user || !reference || amount <= 0) return { ok: false, error: "Valid wallet payment details are required." };

  const existing = getUserPaymentRecords(user).find((item) => item.reference === reference && item.status === "Completed");
  if (existing) return { ok: true, alreadyProcessed: true, payment: existing, balance: getWalletBalance(user) };

  const balance = fundWallet(user, amount, `Wallet top-up via ${channel}`);
  const payment = recordPaymentAttempt({
    user,
    reference,
    purpose: "wallet-topup",
    amount,
    note: `Wallet funded successfully through ${channel}.`,
    provider: channel,
    status: "Completed",
  });

  pushNotification({
    user,
    title: "Wallet funded",
    message: `${formatNaira(amount)} has been added to your wallet balance.`,
    type: "success",
    link: "/dashboard/wallet",
  });

  return { ok: true, payment, balance };
};

export const processTransactionPaymentSuccess = ({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId,
  equipmentTitle,
  owner,
  channel = "Paystack",
}: {
  user: string;
  reference: string;
  amount: number;
  paidAmount?: number;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  channel?: PaymentRecord["provider"];
}) => finalizeMarketplaceTransactionPayment({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId,
  equipmentTitle,
  owner,
  requestType: "purchase",
  pricingMode: "purchase",
  channel,
});

export const processLeasePaymentSuccess = ({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId,
  equipmentTitle,
  owner,
  pricingMode,
  channel = "Paystack",
}: {
  user: string;
  reference: string;
  amount: number;
  paidAmount?: number;
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  pricingMode: "daily lease" | "lease to use" | "lease to own" | "purchase";
  channel?: PaymentRecord["provider"];
}) => finalizeMarketplaceTransactionPayment({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId,
  equipmentTitle,
  owner,
  requestType: "lease",
  pricingMode,
  channel,
});

export const processServicePaymentSuccess = ({
  user,
  reference,
  amount,
  paidAmount,
  serviceRefId,
  serviceTitle,
  owner,
  channel = "Paystack",
}: {
  user: string;
  reference: string;
  amount: number;
  paidAmount?: number;
  serviceRefId: string;
  serviceTitle: string;
  owner: string;
  channel?: PaymentRecord["provider"];
}) => finalizeMarketplaceTransactionPayment({
  user,
  reference,
  amount,
  paidAmount,
  equipmentRefId: serviceRefId,
  equipmentTitle: serviceTitle,
  owner,
  requestType: "service",
  pricingMode: "service",
  channel,
});

export const getMarketplaceEscrowCheckoutQuote = ({
  baseAmount,
  requestType,
  channel = "Paystack",
}: {
  baseAmount: number;
  requestType: "purchase" | "lease" | "service";
  channel?: PaymentRecord["provider"];
}) => {
  const sanitizedBase = Math.max(0, Math.round(Number(baseAmount) || 0));
  if (sanitizedBase <= 0) {
    return {
      baseAmount: 0,
      surchargeRate: 0,
      surchargeAmount: 0,
      totalAmount: 0,
    };
  }

  if (channel !== "Paystack") {
    return {
      baseAmount: sanitizedBase,
      surchargeRate: 0,
      surchargeAmount: 0,
      totalAmount: sanitizedBase,
    };
  }

  const surchargeRate = requestType === "service" ? 0 : 0.005;
  const surchargeAmount = Math.round(sanitizedBase * surchargeRate);

  return {
    baseAmount: sanitizedBase,
    surchargeRate,
    surchargeAmount,
    totalAmount: sanitizedBase + surchargeAmount,
  };
};

export const adminFundWalletByLookup = (identifier: string, amount: number, note = "Admin wallet funding") => {
  const user = findUserByEmailOrCertificate(identifier) || identifier.trim();
  if (!user) return { ok: false, error: "No user matched that lookup." };
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: "Enter a valid funding amount." };

  const balance = fundWallet(user, amount, note);
  const payment = recordPaymentAttempt({
    user,
    reference: `ADMIN-FUND-${Date.now()}`,
    purpose: "wallet-topup",
    amount,
    note,
    provider: "Admin",
    status: "Completed",
  });

  pushNotification({
    user,
    title: "Wallet credited by admin",
    message: `${formatNaira(amount)} was added to your wallet by the admin team.`,
    type: "success",
    link: "/dashboard/wallet",
  });

  return { ok: true, user, balance, payment };
};

export const getReferralActivity = (user = getAuthState().user) => {
  const profile = getUserReferralProfile(user);
  const rewardAmount = getPlatformSettings().rewards.referralWalletAmount;

  if ((profile.referredUserDetails || []).length > 0) {
    return (profile.referredUserDetails || []).map((item, index) => ({
      id: `${profile.code}-${item.user}-${index}`,
      user: item.fullName || item.email || item.user,
      event: item.kycCompleted
        ? "Registered and completed KYC"
        : item.approvalGranted
          ? "Approved by admin – KYC pending"
          : "Registered with your referral code",
      reward: item.rewardQualified
        ? formatNaira(rewardAmount)
        : item.approvalGranted
          ? `₦${rewardAmount.toLocaleString()} (locked until KYC)`
          : "Pending admin approval",
      kycCompleted: item.kycCompleted,
      approvalGranted: item.approvalGranted ?? false,
      rewardQualified: item.rewardQualified,
      createdAt: item.registeredAt || new Date(Date.now() - index * 86400000).toISOString(),
    }));
  }

  return profile.referredUsers.map((name, index) => {
    const qualified = profile.rewardedUsers?.includes(name);
    return {
      id: `${profile.code}-${index}`,
      user: name,
      event: qualified ? "KYC and registered listing completed" : "Sign-up in progress",
      reward: qualified ? formatNaira(rewardAmount) : "Pending qualification",
      kycCompleted: qualified ?? false,
      approvalGranted: false,
      rewardQualified: qualified ?? false,
      createdAt: new Date(Date.now() - index * 86400000).toISOString(),
    };
  });
};

const defaultMembershipPackages: Record<MembershipTier, MembershipPackage> = {
  Standard: { price: "₦0", cycle: "year", includedCredits: 5, equipmentLimit: "5", serviceLimit: "10" },
  Bronze: { price: "₦21,500", cycle: "year", includedCredits: 20, equipmentLimit: "Unlimited", serviceLimit: "Unlimited" },
  Silver: { price: "₦49,500", cycle: "year", includedCredits: 20, equipmentLimit: "Unlimited", serviceLimit: "Unlimited" },
  Gold: { price: "₦99,950", cycle: "year", includedCredits: 20, equipmentLimit: "Unlimited", serviceLimit: "Unlimited" },
  Platinum: { price: "₦139,450", cycle: "year", includedCredits: 20, equipmentLimit: "Unlimited", serviceLimit: "Unlimited" },
};

const decodeBase64Value = (value: string, fallback = "") => {
  if (!value) return fallback;

  try {
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      return window.atob(value) || fallback;
    }
  } catch {
    // ignore decode errors and use fallback
  }

  return fallback;
};

const defaultEncodedPaystackPublicKeys = {
  test: "cGtfdGVzdF84Mzk4MDcxZDQ1ZWU2OTI5NDhkNTEwMzcwMjcyNTA3MDMyODcxMTdh",
  live: "cGtfbGl2ZV85MmQzMDE4ZjgyMjMxNTlhZTRiNjgyZjc0YmYxMDA2OTA3Y2M5N2Zm",
} as const;

const isLocalhost = typeof window !== "undefined" && 
  (window.location.hostname === "localhost" || 
   window.location.hostname === "127.0.0.1" || 
   window.location.protocol === "http:");

const defaultDecodedPaystackPublicKey = decodeBase64Value(
  isLocalhost ? defaultEncodedPaystackPublicKeys.test : defaultEncodedPaystackPublicKeys.live,
  isLocalhost ? "pk_test_8398071d45ee692948d51037027250703287117a" : "pk_live_92d3018f8223159ae4b682f74bf1006907cc97ff",
);

const defaultPaystackCallbackUrl = typeof window !== "undefined"
  ? `${window.location.origin}/dashboard/payments`
  : "https://marketplace.nelprac.com/dashboard/payments";
const envPaystackPublicKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || defaultDecodedPaystackPublicKey;
const envPaystackCallbackUrl = import.meta.env.VITE_PAYSTACK_CALLBACK_URL || defaultPaystackCallbackUrl;

const defaultPlatformSettings: PlatformSettings = {
  commission: "7.5%",
  supportEmail: "support@elra.ng",
  reviewWindow: "4 hours",
  escrowEnabled: false,
  pricing: {
    Bronze: { price: defaultMembershipPackages.Bronze.price, cycle: defaultMembershipPackages.Bronze.cycle },
    Silver: { price: defaultMembershipPackages.Silver.price, cycle: defaultMembershipPackages.Silver.cycle },
    Gold: { price: defaultMembershipPackages.Gold.price, cycle: defaultMembershipPackages.Gold.cycle },
    Platinum: { price: defaultMembershipPackages.Platinum.price, cycle: defaultMembershipPackages.Platinum.cycle },
  },
  paymentGateway: {
    provider: "paystack",
    enabled: Boolean(envPaystackPublicKey),
    publicKey: envPaystackPublicKey,
    secretKey: "",
    secretKeyMasked: "",
    callbackUrl: envPaystackCallbackUrl,
  },
  offlinePayment: {
    bankName: "Access Bank",
    accountName: "Equipment Marketplace Escrow",
    accountNumber: "0123456789",
    instructions: "Offline payment takes 3 - 24 hours to verify. Kindly contact support if you need help.",
  },
  emailSettings: {
    enabled: false,
    mailer: "smtp",
    scheme: "null",
    smtpHost: "smtp.elasticemail.com",
    smtpPort: "2525",
    encryption: "ssl",
    secure: false,
    smtpUser: "",
    smtpPass: "",
    smtpPassMasked: "",
    fromName: "Equipment Marketplace",
    fromEmail: "",
    replyTo: "support@elra.ng",
    notifications: {
      kyc: false,
      transactions: false,
      activities: false,
    },
  },
  rewards: {
    welcomeBonusEnabled: true,
    welcomeBoostCredits: 10,
    premiumDiscountPercent: 10,
    welcomeWindowDays: 7,
    referralBonusEnabled: true,
    referralWalletAmount: 500,
    dailyVisitBonusEnabled: true,
    dailyVisitCreditAmount: 100,
    dailyVisitGlobalLimit: 100,
  },
  marketplaceCategories: defaultMarketplaceCategoryConfig,
};

const normalizePricingSettings = (pricing?: Partial<PlatformSettings["pricing"]>): PlatformSettings["pricing"] => ({
  Bronze: {
    ...defaultPlatformSettings.pricing.Bronze,
    ...(pricing?.Bronze || {}),
    cycle: pricing?.Bronze?.cycle === "month" ? "month" : "year",
  },
  Silver: {
    ...defaultPlatformSettings.pricing.Silver,
    ...(pricing?.Silver || {}),
    cycle: pricing?.Silver?.cycle === "month" ? "month" : "year",
  },
  Gold: {
    ...defaultPlatformSettings.pricing.Gold,
    ...(pricing?.Gold || {}),
    cycle: pricing?.Gold?.cycle === "month" ? "month" : "year",
  },
  Platinum: {
    ...defaultPlatformSettings.pricing.Platinum,
    ...(pricing?.Platinum || {}),
    cycle: pricing?.Platinum?.cycle === "month" ? "month" : "year",
  },
});

const normalizeEmailSettings = (
  emailSettings?: Partial<PlatformSettings["emailSettings"]>,
): PlatformSettings["emailSettings"] => {
  const encryption = emailSettings?.encryption === "tls"
    ? "tls"
    : emailSettings?.encryption === "none"
      ? "none"
      : "ssl";

  return {
    ...defaultPlatformSettings.emailSettings,
    ...(emailSettings || {}),
    mailer: "smtp",
    scheme: emailSettings?.scheme?.trim() || defaultPlatformSettings.emailSettings.scheme,
    smtpHost: emailSettings?.smtpHost?.trim() || defaultPlatformSettings.emailSettings.smtpHost,
    smtpPort: emailSettings?.smtpPort?.trim() || defaultPlatformSettings.emailSettings.smtpPort,
    encryption,
    secure: typeof emailSettings?.secure === "boolean" ? emailSettings.secure : encryption === "ssl",
    smtpUser: emailSettings?.smtpUser || "",
    smtpPass: emailSettings?.smtpPass || "",
    smtpPassMasked: emailSettings?.smtpPassMasked || "",
    fromName: emailSettings?.fromName?.trim() || defaultPlatformSettings.emailSettings.fromName,
    fromEmail: emailSettings?.fromEmail?.trim() || "",
    replyTo: emailSettings?.replyTo?.trim() || defaultPlatformSettings.emailSettings.replyTo,
    enabled: Boolean(emailSettings?.enabled),
    notifications: {
      ...defaultPlatformSettings.emailSettings.notifications,
      ...(emailSettings?.notifications || {}),
    },
  };
};

export const getPlatformSettings = (): PlatformSettings => {
  if (typeof window === "undefined") return defaultPlatformSettings;

  const saved = safeParse<Partial<PlatformSettings>>(browserStorage.getItem(PLATFORM_SETTINGS_KEY), {});
  const mergedGateway = {
    ...defaultPlatformSettings.paymentGateway,
    ...(saved.paymentGateway || {}),
  };

  if (!mergedGateway.publicKey && envPaystackPublicKey) {
    mergedGateway.publicKey = envPaystackPublicKey;
  }

  if (!mergedGateway.callbackUrl && envPaystackCallbackUrl) {
    mergedGateway.callbackUrl = envPaystackCallbackUrl;
  }

  mergedGateway.enabled = Boolean(mergedGateway.enabled || mergedGateway.publicKey);

  return {
    ...defaultPlatformSettings,
    ...saved,
    pricing: normalizePricingSettings(saved.pricing),
    paymentGateway: mergedGateway,
    offlinePayment: {
      ...defaultPlatformSettings.offlinePayment,
      ...(saved.offlinePayment || {}),
    },
    emailSettings: normalizeEmailSettings(saved.emailSettings),
    rewards: {
      ...defaultPlatformSettings.rewards,
      ...(saved.rewards || {}),
    },
    marketplaceCategories: normalizeMarketplaceCategoryConfig(saved.marketplaceCategories),
  };
};

export const savePlatformSettings = (payload: Partial<PlatformSettings>) => {
  if (typeof window === "undefined") return defaultPlatformSettings;

  const current = getPlatformSettings();
  const next: PlatformSettings = {
    ...current,
    ...payload,
    pricing: normalizePricingSettings({
      ...current.pricing,
      ...(payload.pricing || {}),
    }),
    paymentGateway: {
      ...current.paymentGateway,
      ...(payload.paymentGateway || {}),
    },
    offlinePayment: {
      ...current.offlinePayment,
      ...(payload.offlinePayment || {}),
    },
    emailSettings: normalizeEmailSettings({
      ...current.emailSettings,
      ...(payload.emailSettings || {}),
    }),
    rewards: {
      ...current.rewards,
      ...(payload.rewards || {}),
    },
    marketplaceCategories: normalizeMarketplaceCategoryConfig(payload.marketplaceCategories || current.marketplaceCategories),
  };

  browserStorage.setItem(PLATFORM_SETTINGS_KEY, JSON.stringify(next));
  emitPlatformSettingsUpdated(next);
  return next;
};

export const maskGatewaySecret = (value: string) => {
  if (!value) return "Not set";
  if (value.length <= 8) return "•".repeat(value.length);
  return `${value.slice(0, 4)}${"•".repeat(Math.max(4, value.length - 8))}${value.slice(-4)}`;
};

export const encryptEquipmentUrl = (url: string) => {
  const value = String(url || "").trim();
  if (!value) return "";

  if (typeof window !== "undefined" && typeof window.btoa === "function") {
    return window.btoa(value);
  }

  return value;
};

export const createPaymentGatewayCheckoutPreview = ({
  amount,
  email,
  equipmentUrl,
}: {
  amount: number;
  email: string;
  equipmentUrl: string;
}) => {
  const settings = getPlatformSettings();
  const encryptedEquipment = encryptEquipmentUrl(equipmentUrl);
  const reference = `ELRA-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

  return {
    ok: settings.paymentGateway.enabled && Boolean(settings.paymentGateway.publicKey),
    provider: settings.paymentGateway.provider,
    reference,
    amount,
    email,
    callbackUrl: settings.paymentGateway.callbackUrl,
    encryptedEquipment,
    checkoutUrl: settings.paymentGateway.publicKey
      ? `https://checkout.paystack.com/${encodeURIComponent(reference)}`
      : "",
  };
};

export const getMembershipPackages = (): Record<MembershipTier, MembershipPackage> => {
  const settings = getPlatformSettings();

  return {
    Standard: { ...defaultMembershipPackages.Standard, equipmentLimit: "5", serviceLimit: "10" },
    Bronze: { ...defaultMembershipPackages.Bronze, ...settings.pricing.Bronze, cycle: "year", equipmentLimit: "Unlimited", serviceLimit: "Unlimited", includedCredits: 20 },
    Silver: { ...defaultMembershipPackages.Silver, ...settings.pricing.Silver, cycle: "year", equipmentLimit: "Unlimited", serviceLimit: "Unlimited", includedCredits: 20 },
    Gold: { ...defaultMembershipPackages.Gold, ...settings.pricing.Gold, cycle: "year", equipmentLimit: "Unlimited", serviceLimit: "Unlimited", includedCredits: 20 },
    Platinum: { ...defaultMembershipPackages.Platinum, ...settings.pricing.Platinum, cycle: "year", equipmentLimit: "Unlimited", serviceLimit: "Unlimited", includedCredits: 20 },
  };
};

export const membershipPackages = getMembershipPackages();

export const getLegacyBrowserAuthUsers = (): LegacyBrowserAuthUser[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const localUsers = safeParse<Record<string, { password: string; role: AppRole }>>(
    browserStorage.getItem(LOCAL_AUTH_USERS_KEY),
    {},
  );

  return Object.entries(localUsers).map(([user, value]) => ({
    user,
    role: value.role,
  }));
};

export const clearLegacyBrowserAuthUsers = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  const existing = getLegacyBrowserAuthUsers();
  browserStorage.removeItem(LOCAL_AUTH_USERS_KEY);
  return existing.length;
};

export const clearLocalLaunchUserData = () => clearBrowserLaunchResidue(true);

export const getAuthState = (): AuthState => {
  if (typeof window === "undefined") {
    return { loggedIn: false, user: "", role: "user" };
  }

  const loggedIn = browserStorage.getItem(AUTH_KEY) === "true";
  const storedUser = browserStorage.getItem(USER_KEY) || "";
  const storedRole = (browserStorage.getItem(ROLE_KEY) as AppRole) || "user";
  const token = browserStorage.getItem(AUTH_TOKEN_KEY) || undefined;
  const searchParams = new URLSearchParams(window.location.search);
  const previewRole = searchParams.get("previewRole");
  const previewUser = searchParams.get("previewUser");
  const previewableRoles: AppRole[] = ["user", "lessee", "lessor", "agent", "provider", "oem"];

  if (storedRole === "admin" && previewRole && previewableRoles.includes(previewRole.toLowerCase() as AppRole)) {
    return {
      loggedIn,
      user: previewUser?.trim() || storedUser,
      role: previewRole.toLowerCase() as AppRole,
      token,
      isPreview: true,
      actualUser: storedUser,
      actualRole: storedRole,
    };
  }

  return {
    loggedIn,
    user: storedUser,
    role: storedRole,
    token,
  };
};

export const isLoggedIn = () => getAuthState().loggedIn;
export const isAdmin = () => getAuthState().role === "admin";

export const persistAuth = (auth: AuthState) => {
  if (typeof window === "undefined") return;

  browserStorage.setItem(AUTH_KEY, String(auth.loggedIn));
  browserStorage.setItem(ROLE_KEY, auth.role);
  browserStorage.setItem(USER_KEY, auth.user);

  if (auth.token) {
    browserStorage.setItem(AUTH_TOKEN_KEY, auth.token);
  } else {
    browserStorage.removeItem(AUTH_TOKEN_KEY);
  }

  if (auth.loggedIn && auth.user) {
    ensureAdCredits(auth.user);
    saveUserProfile({ user: auth.user, role: auth.role });
  }
};

export const saveUserProfile = (profile: UserProfile) => {
  if (typeof window === "undefined" || !profile.user) return;
  const profiles = safeParse<Record<string, UserProfile>>(browserStorage.getItem(USER_PROFILES_KEY), {});
  const existing: Partial<UserProfile> = profiles[profile.user] ?? {};
  const resolvedRole = profile.role ?? existing.role ?? getAuthState().role;

  profiles[profile.user] = {
    ...existing,
    membershipTier: existing.membershipTier ?? profile.membershipTier ?? "Standard",
    approvalStatus: profile.approvalStatus ?? existing.approvalStatus ?? (resolvedRole === "oem" ? "pending" : "approved"),
    registeredAt: existing.registeredAt ?? profile.registeredAt ?? new Date().toISOString(),
    welcomeDiscountUsed: existing.welcomeDiscountUsed ?? profile.welcomeDiscountUsed ?? false,
    ...profile,
  };

  browserStorage.setItem(USER_PROFILES_KEY, JSON.stringify(profiles));
  ensureWalletBalance(profile.user);
  void syncUserDataForUser(profile.user);
};

export const getAllUserProfiles = () => {
  if (typeof window === "undefined") return [] as UserProfile[];
  const profiles = safeParse<Record<string, UserProfile>>(browserStorage.getItem(USER_PROFILES_KEY), {});
  return Object.values(profiles)
    .filter((profile) => !profile.deleted)
    .map((profile) => ({
      ...profile,
      membershipTier: getMembershipProfile(profile.user).tier,
    }));
};

export const getUserProfile = (user = getAuthState().user): UserProfile => {
  if (typeof window === "undefined") return { user };
  const profiles = safeParse<Record<string, UserProfile>>(browserStorage.getItem(USER_PROFILES_KEY), {});
  const auth = getAuthState();
  const role = user === auth.user ? auth.role : "user";
  return profiles[user] ?? {
    user,
    fullName: user,
    role,
    avatarType: "person",
    avatarVerified: false,
    membershipTier: "Standard",
    approvalStatus: role === "oem" ? "pending" : "approved",
    registeredAt: new Date().toISOString(),
    welcomeDiscountUsed: false,
  };
};

export const findUserByEmailOrCertificate = (identifier: string) => {
  const query = identifier.trim().toLowerCase();
  if (!query) return "";

  const direct = getAllUserProfiles().find((profile) =>
    profile.user.toLowerCase() === query ||
    profile.email?.toLowerCase() === query,
  );

  if (direct) return direct.user;

  const normalizedCertificate = query.startsWith("lic-") ? query : `lic-${query}`;
  const certificateMatch = getAllAccreditationProfiles().find((profile) => {
    const saved = profile.certificateId.trim().toLowerCase();
    return saved === query || saved === normalizedCertificate;
  });

  return certificateMatch?.user || "";
};

export const adminSetUserRole = (user: string, role: AppRole) => {
  saveUserProfile({ user, role });
};

export const adminBanUser = (user: string, banned: boolean) => {
  saveUserProfile({ user, banned });
};

export const adminDeleteUser = (user: string) => {
  saveUserProfile({ user, deleted: true });
};

const getMembershipProfiles = () => {
  if (typeof window === "undefined") return {} as Record<string, MembershipProfile>;
  return safeParse<Record<string, MembershipProfile>>(browserStorage.getItem(MEMBERSHIP_KEY), {});
};

const membershipTierOrder: MembershipTier[] = ["Standard", "Bronze", "Silver", "Gold", "Platinum"];

const normalizeMembershipTier = (tier: unknown): MembershipTier => (
  membershipTierOrder.includes(tier as MembershipTier) ? (tier as MembershipTier) : "Standard"
);

const getMembershipRank = (tier: MembershipTier) => membershipTierOrder.indexOf(tier);

const buildUniqueMembershipToken = (prefix: string) => {
  const random = Math.random().toString(36).slice(2, 10).toUpperCase();
  return `${prefix}-${Date.now()}-${random}`;
};

const getMembershipDurationMs = (tier: MembershipTier) => {
  const normalizedTier = normalizeMembershipTier(tier);
  if (normalizedTier === "Standard") return 0;
  const membershipPackage = getMembershipPackages()[normalizedTier];
  return (membershipPackage.cycle === "year" ? 365 : 30) * DAY_MS;
};

const buildStandardMembershipProfile = (user: string): MembershipProfile => {
  const packages = getMembershipPackages();
  return {
    user,
    tier: "Standard",
    price: packages.Standard.price,
    includedCredits: packages.Standard.includedCredits,
    equipmentLimit: packages.Standard.equipmentLimit,
    serviceLimit: packages.Standard.serviceLimit,
    activeSince: new Date().toISOString(),
    cancellationStatus: "none",
  };
};

export const getMembershipProfile = (user = getAuthState().user): MembershipProfile => {
  const saved = getMembershipProfiles();
  const fallback = buildStandardMembershipProfile(user);
  const stored = saved[user];

  if (!stored) return fallback;

  const normalizedTier = normalizeMembershipTier(stored.tier);
  const activeSince = stored.activeSince || new Date().toISOString();
  const expiresAt = normalizedTier === "Standard"
    ? undefined
    : stored.expiresAt || new Date(new Date(activeSince).getTime() + getMembershipDurationMs(normalizedTier)).toISOString();

  const normalized: MembershipProfile = {
    ...fallback,
    ...stored,
    tier: normalizedTier,
    activeSince,
    expiresAt,
    cancellationStatus: stored.cancellationStatus || "none",
  };

  if (
    normalized.tier !== "Standard"
    && normalized.cancellationStatus === "scheduled"
    && normalized.cancellationEffectiveAt
    && Date.now() >= new Date(normalized.cancellationEffectiveAt).getTime()
  ) {
    const next = buildStandardMembershipProfile(user);
    saveUserProfile({
      user,
      membershipTier: "Standard",
      avatarVerified: getUserAccreditationProfile(user).accreditationStatus === "Verified",
    });

    if (typeof window !== "undefined") {
      const profiles = getMembershipProfiles();
      profiles[user] = next;
      browserStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(profiles));
    }

    return next;
  }

  if (typeof window !== "undefined") {
    const shouldPersist = !stored.expiresAt || stored.cancellationStatus !== normalized.cancellationStatus;
    if (shouldPersist) {
      const profiles = getMembershipProfiles();
      profiles[user] = normalized;
      browserStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(profiles));
    }
  }

  return normalized;
};

export const getAllMembershipProfiles = () => Object.keys(getMembershipProfiles()).map((user) => getMembershipProfile(user));

export const saveMembershipProfile = (profile: MembershipProfile) => {
  if (typeof window === "undefined") return profile;
  const saved = getMembershipProfiles();
  saved[profile.user] = {
    ...profile,
    tier: normalizeMembershipTier(profile.tier),
  };
  browserStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(saved));
  saveUserProfile({ user: profile.user, membershipTier: normalizeMembershipTier(profile.tier) });
  emitMembershipProfileUpdated(profile.user, saved[profile.user]);
  void syncUserDataForUser(profile.user);
  return saved[profile.user];
};

const getReferralProfiles = () => {
  if (typeof window === "undefined") return {} as Record<string, ReferralProfile>;
  return safeParse<Record<string, ReferralProfile>>(browserStorage.getItem(REFERRAL_KEY), {});
};

export const getUserReferralProfile = (user = getAuthState().user): ReferralProfile => {
  const saved = getReferralProfiles();
  if (saved[user]) return saved[user];

  const code = `ELRA-${user.replace(/[^a-z0-9]/gi, "").toUpperCase().slice(0, 8) || "USER"}`;
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "http://localhost:8080";
  const next: ReferralProfile = {
    owner: user,
    code,
    link: `${baseUrl}/login?ref=${encodeURIComponent(code)}`,
    signUps: 0,
    upgrades: 0,
    earnedCredits: 0,
    walletRewards: 0,
    referredUsers: [],
    rewardedUsers: [],
    referredUserDetails: [],
  };

  if (typeof window !== "undefined") {
    const profiles = getReferralProfiles();
    profiles[user] = next;
    browserStorage.setItem(REFERRAL_KEY, JSON.stringify(profiles));
  }

  return next;
};

export const getAllReferralProfiles = () => Object.values(getReferralProfiles());

export const getWalletBalance = (user = getAuthState().user) => ensureWalletBalance(user);

export const setWalletBalance = (user: string, amount: number) => {
  if (typeof window === "undefined" || !user) return;
  const balances = safeParse<Record<string, number>>(browserStorage.getItem(WALLET_KEY), {});
  balances[user] = Math.max(0, amount);
  browserStorage.setItem(WALLET_KEY, JSON.stringify(balances));
  void syncUserDataForUser(user);
};

export const fundWallet = (user: string, amount: number, note = "Wallet top-up", entryType: WalletEntry["type"] = "deposit") => {
  if (!user || amount <= 0) return 0;
  const next = getWalletBalance(user) + amount;
  setWalletBalance(user, next);
  addWalletEntry({ user, type: entryType, direction: "credit", amount, note });
  return next;
};

export const logoutUser = () => {
  if (typeof window === "undefined") return;

  browserStorage.removeItem(AUTH_KEY);
  browserStorage.removeItem(ROLE_KEY);
  browserStorage.removeItem(USER_KEY);
  browserStorage.removeItem(AUTH_TOKEN_KEY);
};

export const getAdCredits = (user = getAuthState().user) => ensureAdCredits(user);

export const setAdCredits = (user: string, amount: number) => {
  if (typeof window === "undefined" || !user) return;

  const credits = safeParse<Record<string, number>>(browserStorage.getItem(AD_CREDITS_KEY), {});
  credits[user] = Math.max(0, amount);
  browserStorage.setItem(AD_CREDITS_KEY, JSON.stringify(credits));
};

export const addAdCredits = (user: string, amount: number) => {
  if (!user || amount <= 0) return;
  setAdCredits(user, getAdCredits(user) + amount);
};

export const buyCredits = (user: string, amount: number) => {
  if (!user || amount <= 0) return { ok: false, error: "Enter a valid advert wallet amount." };

  const cost = amount * 1000;
  const balance = getWalletBalance(user);

  if (balance < cost) {
    return { ok: false, error: `Insufficient wallet balance. You need ${formatNaira(cost)}.` };
  }

  setWalletBalance(user, balance - cost);
  addAdCredits(user, amount);
  addWalletEntry({ user, type: "credit-purchase", direction: "debit", amount: cost, note: `Advert wallet top-up of ${formatNaira(cost)}` });

  return { ok: true, balance: getWalletBalance(user), credits: getAdCredits(user), spent: cost };
};

export const registerReferralSignup = (newUser: string, referralCode: string) => {
  if (typeof window === "undefined" || !referralCode.trim() || !newUser.trim()) return;

  const profiles = getReferralProfiles();
  const ownerEntry = Object.entries(profiles).find(([, profile]) => profile.code === referralCode.trim().toUpperCase());

  if (!ownerEntry || ownerEntry[0] === newUser) return;

  const [owner, profile] = ownerEntry;
  if (!profile.referredUsers.includes(newUser)) {
    profile.referredUsers.push(newUser);
    profile.signUps += 1;
    profile.referredUserDetails = [
      {
        user: newUser,
        email: newUser,
        registeredAt: new Date().toISOString(),
        kycCompleted: false,
        rewardQualified: false,
      },
      ...(profile.referredUserDetails || []).filter((item) => item.user !== newUser && item.email !== newUser),
    ];
    profiles[owner] = profile;
    browserStorage.setItem(REFERRAL_KEY, JSON.stringify(profiles));
    saveUserProfile({ user: newUser, referredBy: profile.code });
    void syncUserDataForUser(owner);
    void syncUserDataForUser(newUser);
  }
};

export const recordReferralApproval = (user: string) => {
  if (typeof window === "undefined") return;

  const userProfile = getUserProfile(user);
  if (!userProfile.referredBy) return;

  const profiles = getReferralProfiles();
  const ownerEntry = Object.entries(profiles).find(([, profile]) => profile.code === userProfile.referredBy || profile.owner === userProfile.referredBy);
  if (!ownerEntry) return;

  const [owner, profile] = ownerEntry;
  const alreadyGranted = (profile.referredUserDetails || []).some((item) => (item.user === user || item.email === user) && item.approvalGranted);
  if (alreadyGranted) return;

  profile.referredUserDetails = (profile.referredUserDetails || []).map((item) =>
    item.user === user || item.email === user ? { ...item, approvalGranted: true } : item,
  );

  if (!(profile.referredUserDetails || []).some((item) => item.user === user || item.email === user)) {
    profile.referredUserDetails = [
      { user, email: user, registeredAt: new Date().toISOString(), kycCompleted: false, rewardQualified: false, approvalGranted: true },
      ...(profile.referredUserDetails || []),
    ];
  }

  if (!profile.referredUsers.includes(user)) {
    profile.referredUsers.push(user);
    profile.signUps += 1;
  }

  profiles[owner] = profile;
  browserStorage.setItem(REFERRAL_KEY, JSON.stringify(profiles));
  void syncUserDataForUser(owner);

  pushNotification({
    user: owner,
    title: "Referred user approved",
    message: `Your referred user (${user}) was approved by admin. Complete KYC to unlock your ₦${getPlatformSettings().rewards.referralWalletAmount.toLocaleString()} referral reward.`,
    type: "success",
    link: "/dashboard/referrals",
  });
};

export const rewardReferralUpgrade = (user: string) => {
  if (typeof window === "undefined" || !getPlatformSettings().rewards.referralBonusEnabled) return;
  const userProfile = getUserProfile(user);
  if (!userProfile.referredBy) return;

  const profiles = getReferralProfiles();
  const ownerEntry = Object.entries(profiles).find(([, profile]) => profile.code === userProfile.referredBy);
  if (!ownerEntry) return;

  const [owner, profile] = ownerEntry;
  profile.upgrades += 1;

  const premiumPlanAmount = parseCurrencyAmount(getMembershipProfile(user).price || "₦0");
  const configuredMinimum = getPlatformSettings().rewards.referralWalletAmount;
  const rewardAmount = premiumPlanAmount > 0 ? Math.round(premiumPlanAmount * 0.05) : configuredMinimum;

  profile.walletRewards = (profile.walletRewards || 0) + rewardAmount;
  profile.earnedCredits += 10;
  profiles[owner] = profile;
  browserStorage.setItem(REFERRAL_KEY, JSON.stringify(profiles));
  fundWallet(owner, rewardAmount, `Referral reward from ${user}'s premium upgrade`, "commission");
  void syncUserDataForUser(owner);
  pushNotification({
    user: owner,
    title: "Referral reward paid",
    message: `${formatNaira(rewardAmount)} has been added to your wallet for ${user}'s premium upgrade.`,
    type: "success",
    link: "/dashboard/wallet",
  });
};

export const getAllCreditBalances = () => {
  if (typeof window === "undefined") return {} as Record<string, number>;
  return safeParse<Record<string, number>>(browserStorage.getItem(AD_CREDITS_KEY), {});
};

const defaultEscrowRecords: EscrowRecord[] = [];

const saveEscrowRecords = (records: EscrowRecord[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(ESCROW_KEY, JSON.stringify(records));
  void syncWorkflowState({ escrows: records });
};

const resolveEscrowReviewStatus = (status: EscrowRecord["status"]): EscrowRecord["status"] => {
  if (status === "Released" || status === "Refunded") {
    return status;
  }

  return "Under Review";
};

const syncEscrowAttention = (records: EscrowRecord[]) => {
  const now = Date.now();
  let changed = false;

  const next: EscrowRecord[] = records.map((item): EscrowRecord => {
    const onePartyWaiting = (item.ownerDeliveryStatus === "Delivered" && item.customerReceiptStatus !== "Received")
      || (item.customerReceiptStatus === "Received" && item.ownerDeliveryStatus !== "Delivered");
    const updatedAt = new Date(item.deliveryUpdatedAt || item.createdAt).getTime();

    if (onePartyWaiting && !item.attentionNotifiedAt && now - updatedAt >= DAY_MS) {
      changed = true;
      pushNotification({
        user: "admin",
        title: "Escrow needs review",
        message: `${item.equipmentTitle} is waiting on a delivery update from one party.`,
        type: "warning",
        link: "/admin/platform",
      });

      return {
        ...item,
        status: resolveEscrowReviewStatus(item.status),
        adminAttentionRequired: true,
        attentionNotifiedAt: new Date().toISOString(),
      };
    }

    return item;
  });

  if (changed) saveEscrowRecords(next);
  return next;
};

export const getEscrowRecords = (user?: string) => {
  if (typeof window === "undefined") return defaultEscrowRecords;
  const records = syncEscrowAttention(safeParse<EscrowRecord[]>(browserStorage.getItem(ESCROW_KEY), defaultEscrowRecords));
  return user ? records.filter((item) => item.owner === user || item.customer === user) : records;
};

export const createEscrowRecord = (payload: Omit<EscrowRecord, "id" | "createdAt" | "status">) => {
  const records = getEscrowRecords();
  const next: EscrowRecord = {
    ...payload,
    id: `ESC-${Date.now()}`,
    status: "Held",
    createdAt: new Date().toISOString(),
    ownerDeliveryStatus: payload.ownerDeliveryStatus || "Pending",
    customerReceiptStatus: payload.customerReceiptStatus || "Pending",
    adminAttentionRequired: false,
  };
  records.unshift(next);
  saveEscrowRecords(records);

  pushNotification({
    user: next.owner,
    title: "Escrow opened",
    message: `${next.equipmentTitle} is now protected in escrow until delivery is confirmed.`,
    type: "info",
    link: "/dashboard/rentals",
  });

  pushNotification({
    user: next.customer,
    title: "Escrow opened",
    message: `Your payment for ${next.equipmentTitle} is being held safely in escrow.`,
    type: "success",
    link: "/dashboard/rentals",
  });

  return next;
};

export const updateEscrowDeliveryStatus = (id: string, actor: "owner" | "customer" | "admin", status: "Delivered" | "Received") => {
  const records = getEscrowRecords();
  const target = records.find((item) => item.id === id);
  if (!target) return { ok: false, error: "Escrow record not found." };

  const timestamp = new Date().toISOString();
  const deliveryLabel = target.requestType === "service" ? "service" : "equipment";
  const updatedRecord: EscrowRecord = {
    ...target,
    ownerDeliveryStatus: (actor === "owner" || (actor === "admin" && status === "Delivered")) ? "Delivered" : target.ownerDeliveryStatus || "Pending",
    customerReceiptStatus: (actor === "customer" || (actor === "admin" && status === "Received")) ? "Received" : target.customerReceiptStatus || "Pending",
    deliveryUpdatedAt: timestamp,
    adminAttentionRequired: false,
    attentionNotifiedAt: undefined,
  };

  const shouldReleaseNow = updatedRecord.ownerDeliveryStatus === "Delivered"
    && updatedRecord.customerReceiptStatus === "Received"
    && target.status !== "Released";

  if (shouldReleaseNow) {
    updatedRecord.status = "Awaiting Release";
    updatedRecord.releaseAfter = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  } else if (updatedRecord.status === "Under Review") {
    updatedRecord.status = "Held";
  }

  const next: EscrowRecord[] = records.map((item) => item.id === id ? updatedRecord : item);
  saveEscrowRecords(next);

  const agreements = getRentalAgreements("", "admin").map((item) => (
    item.requestId === updatedRecord.requestId
      ? {
          ...item,
          ownerDeliveryStatus: updatedRecord.ownerDeliveryStatus,
          customerReceiptStatus: updatedRecord.customerReceiptStatus,
          deliveryUpdatedAt: updatedRecord.deliveryUpdatedAt,
          adminAttentionRequired: updatedRecord.adminAttentionRequired,
        }
      : item
  ));
  saveRentalAgreements(agreements);

  pushNotification({
    user: updatedRecord.owner,
    title: "Escrow delivery updated",
    message: `${updatedRecord.equipmentTitle} ${deliveryLabel} is now marked as ${status.toLowerCase()}.`,
    type: "info",
    link: "/dashboard/rentals",
  });

  pushNotification({
    user: updatedRecord.customer,
    title: "Escrow delivery updated",
    message: `${updatedRecord.equipmentTitle} ${deliveryLabel} is now marked as ${status.toLowerCase()}.`,
    type: "info",
    link: "/dashboard/rentals",
  });

  if (shouldReleaseNow) {
    pushNotification({
      user: updatedRecord.owner,
      title: "Escrow awaiting release",
      message: `Escrow for ${updatedRecord.equipmentTitle} is now awaiting release (30 days).`,
      type: "info",
      link: "/dashboard/wallet",
    });

    pushNotification({
      user: updatedRecord.customer,
      title: "Escrow awaiting release",
      message: `Escrow for ${updatedRecord.equipmentTitle} is now awaiting release.`,
      type: "info",
      link: "/dashboard/rentals",
    });
  }

  return { ok: true, record: updatedRecord };
};

export const updateEscrowStatus = (id: string, status: EscrowRecord["status"], options?: { adminOverride?: boolean }) => {
  const records = getEscrowRecords();
  const target = records.find((item) => item.id === id);
  if (!target) return { ok: false, error: "Escrow record not found." };

  if (target.status === status) {
    return { ok: true, record: target };
  }

  if (status === "Released" && target.releaseAfter && Date.now() < new Date(target.releaseAfter).getTime() && !options?.adminOverride) {
    return { ok: false, error: `Escrow funds can only be withdrawn after ${new Date(target.releaseAfter).toLocaleDateString()}.` };
  }

  const nextRecord: EscrowRecord = {
    ...target,
    status,
    adminAttentionRequired: false,
  };
  const updated: EscrowRecord[] = records.map((item) => item.id === id ? nextRecord : item);
  saveEscrowRecords(updated);

  if (status === "Released") {
    const amount = parseCurrencyAmount(target.amount);
    const platformFeeRate = target.requestType === "service" ? 0.05 : 0;
    const platformFee = Math.round(amount * platformFeeRate);
    const ownerNet = Math.max(0, amount - platformFee);
    fundWallet(target.owner, ownerNet, `Escrow released for ${target.equipmentTitle}`, "escrow-release");
    pushNotification({ user: target.owner, title: "Escrow released", message: platformFeeRate > 0 ? `${formatNaira(ownerNet)} has been released to your wallet for ${target.equipmentTitle} after the ${(platformFeeRate * 100).toFixed(0)}% ELRA platform fee of ${formatNaira(platformFee)}.` : `${formatNaira(ownerNet)} has been released to your wallet for ${target.equipmentTitle}.`, type: "success", link: "/dashboard/wallet" });
    pushNotification({ user: target.customer, title: "Escrow closed", message: `Escrow for ${target.equipmentTitle} has been released to the owner.`, type: "info", link: "/dashboard/rentals" });
  }

  if (status === "Refunded") {
    const amount = parseCurrencyAmount(target.amount);
    fundWallet(target.customer, amount, `Escrow refunded for ${target.equipmentTitle}`, "escrow-refund");
    pushNotification({ user: target.customer, title: "Escrow refunded", message: `${formatNaira(amount)} has been returned to your wallet for ${target.equipmentTitle}.`, type: "success", link: "/dashboard/wallet" });
    pushNotification({ user: target.owner, title: "Escrow refunded", message: `Escrow for ${target.equipmentTitle} was refunded to the customer.`, type: "warning", link: "/dashboard/rentals" });
  }

  return { ok: true, record: nextRecord };
};

const defaultPayoutRequests: PayoutRequest[] = [];

export const getAllPayoutRequests = () => {
  if (typeof window === "undefined") return defaultPayoutRequests;
  return safeParse<PayoutRequest[]>(browserStorage.getItem(PAYOUT_KEY), defaultPayoutRequests);
};

const savePayoutRequests = (requests: PayoutRequest[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(PAYOUT_KEY, JSON.stringify(requests));
  void syncWorkflowState({ payouts: requests });
};

export const requestAgentPayout = (user: string, amount: number, details?: Partial<PayoutRequest>) => {
  if (!user || amount <= 0) return { ok: false, error: "Enter a valid payout amount." };

  const walletType = details?.walletType || "main";
  const availableBalance = walletType === "escrow"
    ? getEscrowRecords(user)
      .filter((item) => item.owner === user && item.status === "Awaiting Release")
      .reduce((sum, item) => sum + parseCurrencyAmount(item.amount), 0)
    : getWalletBalance(user);

  if (availableBalance < amount) {
    return { ok: false, error: `Insufficient ${walletType} wallet balance for payout.` };
  }

  if (walletType !== "escrow") {
    setWalletBalance(user, getWalletBalance(user) - amount);
  }

  addWalletEntry({ user, type: "payout-request", direction: "debit", amount, note: `Payout request submitted from ${walletType} wallet` });

  const requests = getAllPayoutRequests();
  requests.unshift({
    id: `PAY-${Date.now()}`,
    agent: user,
    amount: formatNaira(amount),
    status: "Pending",
    requestedAt: new Date().toISOString(),
    bankName: details?.bankName,
    accountName: details?.accountName,
    accountNumber: details?.accountNumber,
    businessName: details?.businessName,
    walletType,
    note: details?.note,
  });
  savePayoutRequests(requests);

  pushNotification({
    user: "Admin",
    title: "New payout request",
    message: `${user} requested ${formatNaira(amount)} from the ${walletType} wallet payout queue.`,
    type: "info",
    link: "/admin/users",
  });

  return { ok: true };
};

export const updatePayoutRequestStatus = (id: string, status: PayoutRequest["status"], adminNote = "") => {
  const requests = getAllPayoutRequests();
  const target = requests.find((item) => item.id === id);
  const updated = requests.map((item) => item.id === id ? { ...item, status, adminNote: adminNote || item.adminNote || "" } : item);
  savePayoutRequests(updated);

  if (target && status === "Rejected") {
    const amount = parseCurrencyAmount(target.amount);
    fundWallet(target.agent, amount, "Payout request reversed");
    addWalletEntry({ user: target.agent, type: "payout-reversal", direction: "credit", amount, note: "Payout request rejected and returned" });
  }
};

export const getWalletSummary = (user = getAuthState().user) => {
  const credits = getAdCredits(user);
  const membership = getMembershipProfile(user);
  const referral = getUserReferralProfile(user);
  const derivedReferralRewards = (referral.referredUserDetails || []).filter((item) => item.rewardQualified).length * getPlatformSettings().rewards.referralWalletAmount;
  const walletBalance = getWalletBalance(user);
  const escrowRecords = getEscrowRecords(user);
  const escrowHeldNumber = escrowRecords
    .filter((item) => item.status !== "Released" && item.status !== "Refunded")
    .reduce((sum, item) => sum + parseCurrencyAmount(item.amount), 0);
  const escrowAvailableNumber = escrowRecords
    .filter((item) => item.status === "Awaiting Release" && item.releaseAfter && Date.now() >= new Date(item.releaseAfter).getTime())
    .reduce((sum, item) => sum + parseCurrencyAmount(item.amount), 0);
  const pendingPayout = getAllPayoutRequests().filter((item) => item.agent === user && item.status === "Pending").reduce((sum, item) => sum + parseCurrencyAmount(item.amount), 0);

  return {
    credits,
    creditsValue: formatNaira(credits * 1000),
    membership: membership.tier,
    equipmentLimit: membership.equipmentLimit,
    serviceLimit: membership.serviceLimit,
    isStandard: membership.tier === "Standard",
    referralCode: referral.code,
    referralLink: referral.link,
    referralCredits: referral.earnedCredits,
    referralWalletRewards: formatNaira(Math.max(referral.walletRewards || 0, derivedReferralRewards)),
    walletBalance: formatNaira(walletBalance),
    walletBalanceNumber: walletBalance,
    escrowHeld: formatNaira(escrowHeldNumber),
    escrowHeldNumber,
    escrowAvailable: formatNaira(escrowAvailableNumber),
    escrowAvailableNumber,
    pendingPayout: formatNaira(pendingPayout),
    pendingPayoutNumber: pendingPayout,
    history: getWalletHistory().filter((entry) => entry.user === user).slice(0, 8),
    payments: getUserPaymentRecords(user).slice(0, 8),
  };
};

export const getAgentWalletSummary = (user = getAuthState().user) => {
  const wallet = getWalletSummary(user);
  const history = getWalletHistory().filter((entry) => entry.user === user);
  const totalEarned = history
    .filter((entry) => entry.direction === "credit")
    .reduce((sum, entry) => sum + entry.amount, 0);

  return {
    ...wallet,
    totalEarned: formatNaira(totalEarned),
    history,
  };
};

export const setUserMembershipTier = (user: string, tier: MembershipTier) => {
  const packages = getMembershipPackages();
  const next = packages[tier];
  const discounted = getDiscountedMembershipAmount(tier, user);
  const activeSince = new Date().toISOString();
  const expiresAt = tier === "Standard" ? undefined : new Date(Date.now() + getMembershipDurationMs(tier)).toISOString();
  const currentUserProfile = getUserProfile(user);
  const accreditation = getUserAccreditationProfile(user);
  const kyc = getUserKycProfile(user);
  const hasVerifiedKyc = kyc.autoApproved || (kyc.tierOne.status === "approved" && kyc.tierTwo.status === "approved");
  const naturallyVerified = accreditation.accreditationStatus === "Verified" || hasVerifiedKyc || currentUserProfile.approvalStatus === "approved";
  const premiumVerified = tier !== "Standard";
  const profile = saveMembershipProfile({
    user,
    tier,
    price: tier === "Standard" ? next.price : discounted.label,
    includedCredits: next.includedCredits,
    equipmentLimit: next.equipmentLimit,
    serviceLimit: next.serviceLimit,
    activeSince,
    expiresAt,
    cancellationStatus: "none",
    cancellationRequestedAt: undefined,
    cancellationEffectiveAt: undefined,
  });

  saveUserProfile({
    ...currentUserProfile,
    user,
    membershipTier: tier,
    approvalStatus: premiumVerified ? "approved" : (naturallyVerified ? "approved" : "pending"),
    avatarVerified: premiumVerified ? true : naturallyVerified,
    listingAccessDisabled: premiumVerified ? false : currentUserProfile.listingAccessDisabled,
  });

  if (premiumVerified) {
    const currentKyc = getUserKycProfile(user);
    saveUserKycProfile({
      ...currentKyc,
      autoApproved: true,
      tierOne: {
        ...currentKyc.tierOne,
        status: "approved",
        email: currentKyc.tierOne.email || currentUserProfile.email || "",
        emailVerified: true,
        submittedAt: currentKyc.tierOne.submittedAt || new Date().toISOString(),
        adminNote: currentKyc.tierOne.adminNote || "Approved automatically because a premium membership is active.",
      },
      tierTwo: {
        ...currentKyc.tierTwo,
        status: "approved",
        submittedAt: currentKyc.tierTwo.submittedAt || new Date().toISOString(),
        adminNote: currentKyc.tierTwo.adminNote || "Approved automatically because a premium membership is active.",
      },
    });
  }

  pushNotification({
    user,
    title: "Membership updated",
    message: `${tier} membership is now active on your account${tier !== "Standard" ? " and your verified member badge is now visible." : "."}`,
    type: "success",
    link: "/dashboard/payments",
  });

  return profile;
};

export const upgradeMembership = (user: string, tier: MembershipTier) => {
  const packages = getMembershipPackages();
  const welcome = getWelcomeBonusState(user);
  const profile = setUserMembershipTier(user, tier);

  if (tier !== "Standard") {
    addAdCredits(user, packages[tier].includedCredits);
    rewardReferralUpgrade(user);

    if (welcome.canUseDiscount) {
      saveUserProfile({ user, welcomeDiscountUsed: true });
      pushNotification({
        user,
        title: "Welcome discount applied",
        message: `${welcome.discountPercent}% off your first premium upgrade has been applied successfully.`,
        type: "success",
        link: "/dashboard/payments",
      });
    }
  }

  return profile;
};

export const getMembershipUpgradeRequests = () => {
  if (typeof window === "undefined") return [] as MembershipActivationRequest[];
  return [...safeParse<MembershipActivationRequest[]>(browserStorage.getItem(MEMBERSHIP_REQUESTS_KEY), [])];
};

const saveMembershipUpgradeRequests = (requests: MembershipActivationRequest[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(MEMBERSHIP_REQUESTS_KEY, JSON.stringify(requests));
  void syncWorkflowState({ membershipRequests: requests });
};

export const requestMembershipUpgrade = (user: string, tier: MembershipTier) => {
  if (typeof window === "undefined" || !user) {
    return { ok: false, error: "You need to sign in first." };
  }

  const currentMembership = getMembershipProfile(user);
  const currentRank = getMembershipRank(currentMembership.tier);
  const requestedRank = getMembershipRank(tier);
  const existingPending = getMembershipUpgradeRequests().find((item) => item.user === user && item.adminStatus === "Pending");

  if (tier === "Standard") {
    return { ok: false, error: "Standard is already the fallback plan. Upgrade to a premium plan or keep your current membership." };
  }

  if (existingPending?.tier === tier) {
    return { ok: false, error: `A ${tier} membership request is already pending for your account.` };
  }

  if (currentMembership.tier !== "Standard" && requestedRank <= currentRank) {
    return {
      ok: false,
      error: `You already have an active ${currentMembership.tier} membership. You can only upgrade to a higher plan or cancel it at expiry.`,
    };
  }

  const settings = getPlatformSettings();
  const packageInfo = getMembershipPackages()[tier];
  const discounted = getDiscountedMembershipAmount(tier, user);
  const numericAmount = discounted.amount;
  const request: MembershipActivationRequest = {
    id: buildUniqueMembershipToken("MEM"),
    user,
    tier,
    amount: discounted.label,
    cycle: packageInfo.cycle,
    requestedAt: new Date().toISOString(),
    paymentStatus: settings.paymentGateway.enabled ? "Pending" : "Pending",
    adminStatus: "Pending",
    reference: buildUniqueMembershipToken("ELRA-MEM"),
  };

  const requests = getMembershipUpgradeRequests().filter((item) => !(item.user === user && item.adminStatus === "Pending"));
  requests.unshift(request);
  saveMembershipUpgradeRequests(requests);
  recordPaymentAttempt({
    user,
    reference: request.reference,
    purpose: "membership",
    amount: numericAmount,
    note: `${tier} membership request created and awaiting successful payment confirmation.`,
    tier,
    provider: "Paystack",
    status: "Pending",
  });

  pushNotification({
    user,
    title: "Membership request submitted",
    message: `${tier} request has been created and will activate only after payment confirmation or admin approval.`,
    type: "info",
    link: "/dashboard/payments",
  });

  pushNotification({
    user: "Admin",
    title: "Membership approval pending",
    message: `${user} requested the ${tier} package.`,
    type: "warning",
    link: "/admin/platform",
  });

  return { ok: true, request };
};

const persistMembershipRequestsToServer = async () => {
  if (!getAuthState().token) return null;

  const state = await syncWorkflowState({ membershipRequests: getMembershipUpgradeRequests() });
  if (!state) {
    throw new Error("Unable to persist membership requests to the server.");
  }

  return state;
};

const persistMembershipProfileToServer = async (user: string, token?: string) => {
  if (!canSyncUserData(user, token)) return null;

  const snapshot = await syncUserDataForUser(user, token);
  if (!snapshot) {
    throw new Error("Unable to persist the membership change to the server.");
  }

  return snapshot;
};

export const requestMembershipUpgradePersisted = async (user: string, tier: MembershipTier) => {
  const result = requestMembershipUpgrade(user, tier);
  if (!result.ok) return result;

  await persistMembershipRequestsToServer();
  return result;
};

export const requestMembershipCancellation = (user = getAuthState().user) => {
  if (typeof window === "undefined" || !user) {
    return { ok: false, error: "You need to sign in first." };
  }

  const currentMembership = getMembershipProfile(user);

  if (currentMembership.tier === "Standard") {
    return { ok: false, error: "No active premium membership was found for cancellation." };
  }

  if (currentMembership.cancellationStatus === "scheduled") {
    return {
      ok: false,
      error: `Cancellation is already scheduled for ${new Date(currentMembership.cancellationEffectiveAt || currentMembership.expiresAt || Date.now()).toLocaleDateString()}.`,
    };
  }

  const effectiveAt = currentMembership.expiresAt || new Date(new Date(currentMembership.activeSince).getTime() + getMembershipDurationMs(currentMembership.tier)).toISOString();

  saveMembershipProfile({
    ...currentMembership,
    expiresAt: effectiveAt,
    cancellationStatus: "scheduled",
    cancellationRequestedAt: new Date().toISOString(),
    cancellationEffectiveAt: effectiveAt,
  });

  const requests = getMembershipUpgradeRequests();
  const updatedRequests = requests.map((item) => (
    item.user === user && item.adminStatus === "Pending"
      ? { ...item, adminStatus: "Rejected" as const, paymentStatus: item.paymentStatus === "Confirmed" ? item.paymentStatus : "Rejected" as const }
      : item
  ));
  saveMembershipUpgradeRequests(updatedRequests);

  pushNotification({
    user,
    title: "Membership cancellation scheduled",
    message: `${currentMembership.tier} will remain active until ${new Date(effectiveAt).toLocaleDateString()} and will then cancel automatically.`,
    type: "info",
    link: "/dashboard/payments",
  });

  return { ok: true, effectiveAt };
};

export const updateMembershipUpgradeRequest = (
  id: string,
  payload: Partial<Pick<MembershipActivationRequest, "paymentStatus" | "adminStatus">>,
) => {
  const requests = getMembershipUpgradeRequests();
  const target = requests.find((item) => item.id === id);
  if (!target) return null;

  const updatedRequests = requests.map((item) => item.id === id ? { ...item, ...payload } : item);
  saveMembershipUpgradeRequests(updatedRequests);

  const updated = updatedRequests.find((item) => item.id === id)!;

  const wasPreviouslyActivated = target.adminStatus === "Approved" || target.paymentStatus === "Confirmed";

  if (!wasPreviouslyActivated && (updated.adminStatus === "Approved" || updated.paymentStatus === "Confirmed")) {
    upgradeMembership(updated.user, updated.tier);
  }

  if (updated.paymentStatus === "Confirmed") {
    recordPaymentAttempt({
      user: updated.user,
      reference: updated.reference,
      purpose: "membership",
      amount: parseCurrencyAmount(updated.amount),
      note: `${updated.tier} membership payment confirmed.`,
      tier: updated.tier,
      provider: "Paystack",
      status: "Completed",
    });
  }

  if (payload.adminStatus === "Rejected" || payload.paymentStatus === "Rejected") {
    recordPaymentAttempt({
      user: updated.user,
      reference: updated.reference,
      purpose: "membership",
      amount: parseCurrencyAmount(updated.amount),
      note: `${updated.tier} membership request was rejected or payment failed.`,
      tier: updated.tier,
      provider: "Paystack",
      status: "Failed",
    });

    pushNotification({
      user: updated.user,
      title: "Membership request not approved",
      message: `${updated.tier} request needs a new payment or admin review.`,
      type: "warning",
      link: "/pricing",
    });
  }

  return updated;
};

export const updateMembershipUpgradeRequestPersisted = async (
  id: string,
  payload: Partial<Pick<MembershipActivationRequest, "paymentStatus" | "adminStatus">>,
  token?: string,
) => {
  const updated = updateMembershipUpgradeRequest(id, payload);
  if (!updated) return null;

  await persistMembershipRequestsToServer();

  if (updated.adminStatus === "Approved" || updated.paymentStatus === "Confirmed") {
    await persistMembershipProfileToServer(updated.user, token);
  }

  return updated;
};

export const setUserMembershipTierPersisted = async (user: string, tier: MembershipTier, token?: string) => {
  const profile = setUserMembershipTier(user, tier);
  await persistUserKycProfilesToServer();
  await persistMembershipProfileToServer(user, token);
  return profile;
};

export const payMembershipFromWallet = async ({
  user,
  tier,
  amount,
  token,
}: {
  user: string;
  tier: MembershipTier;
  amount: number;
  token?: string;
}): Promise<{ ok: boolean; error?: string }> => {
  if (!user || !tier || amount <= 0) return { ok: false, error: "Valid membership payment details are required." };

  const balance = getWalletBalance(user);
  if (balance < amount) {
    return { ok: false, error: `Insufficient wallet balance. You need ${formatNaira(amount)} but your balance is ${formatNaira(balance)}.` };
  }

  const reference = `WALLET-MBR-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

  // Create the upgrade request first so processMembershipPaymentSuccess can resolve the tier
  const requestResult = await requestMembershipUpgradePersisted(user, tier);
  const usedReference = requestResult.ok ? requestResult.request.reference : reference;

  // Deduct wallet before finalizing — roll back on failure
  setWalletBalance(user, balance - amount);
  addWalletEntry({ user, type: "membership-payment", direction: "debit", amount, note: `${tier} membership paid from wallet balance.` });

  const result = await processMembershipPaymentSuccessPersisted({
    user,
    reference: usedReference,
    amount,
    tier,
    channel: "Wallet",
    token,
  });

  if (!result.ok) {
    // Roll back the deduction
    setWalletBalance(user, getWalletBalance(user) + amount);
    return { ok: false, error: result.error || "Unable to confirm the membership payment from wallet." };
  }

  return { ok: true };
};

export const processMembershipPaymentSuccessPersisted = async ({
  user,
  reference,
  amount,
  tier,
  channel = "Paystack",
  token,
}: {
  user: string;
  reference: string;
  amount: number;
  tier?: MembershipTier;
  channel?: PaymentRecord["provider"];
  token?: string;
}) => {
  const result = processMembershipPaymentSuccess({
    user,
    reference,
    amount,
    tier,
    channel,
  });

  if (!result.ok) {
    return result;
  }

  await persistMembershipRequestsToServer();
  await persistUserKycProfilesToServer();
  await persistMembershipProfileToServer(user, token);

  return result;
};

export const adminGiveCreditsByLookup = (identifier: string, amount: number) => {
  const user = findUserByEmailOrCertificate(identifier);
  if (!user) return { ok: false, error: "No user matched that email or certificate ID." };
  addAdCredits(user, amount);
  return { ok: true, user };
};

export const getUserPromotableItems = (user = getAuthState().user) => {
  if (typeof window === "undefined" || !user) return [] as Array<{ id: string; label: string; type: "equipment" | "service" }>;

  const drafts = getStoredListingDrafts();
  return drafts
    .filter((draft) => draft.owner === user && ["uploaded", "pending", "approved"].includes(String(draft.status || "").toLowerCase()))
    .map((draft, index) => ({
      id: draft.savedAt || `${index}`,
      label: draft.title || `${draft.listingType === "service" ? "Service" : "Equipment"} ${index + 1}`,
      type: draft.listingType === "service" ? "service" : "equipment",
    }));
};

const parseLimitValue = (value: string) => value === "Unlimited" ? Number.POSITIVE_INFINITY : Number(value || 0);

export const getListingUsage = (user = getAuthState().user) => {
  if (typeof window === "undefined" || !user) {
    return { equipmentUploaded: 0, serviceUploaded: 0 };
  }

  const drafts = getStoredListingDrafts();
  const owned = drafts.filter((draft) => draft.owner === user && ["uploaded", "pending", "approved"].includes(String(draft.status || "").toLowerCase()));

  return {
    equipmentUploaded: owned.filter((draft) => (draft.listingType || "equipment") === "equipment").length,
    serviceUploaded: owned.filter((draft) => draft.listingType === "service").length,
  };
};

export const getMarketplaceAccessState = (user = getAuthState().user) => {
  const profile = getUserProfile(user);
  const membership = getMembershipProfile(user);
  const accreditation = getUserAccreditationProfile(user);
  const kyc = getUserKycProfile(user);
  const listingUsage = getListingUsage(user);
  const equipmentLimitNumber = parseLimitValue(membership.equipmentLimit);
  const serviceLimitNumber = parseLimitValue(membership.serviceLimit);
  const approvalStatus: ApprovalStatus = profile.role === "oem"
    ? (profile.approvalStatus === "approved" ? "approved" : "pending")
    : (profile.approvalStatus ?? "approved");
  const canOperate = !profile.banned && !profile.deleted && (profile.role !== "oem" || approvalStatus === "approved");
  const tierOneApproved = Boolean(kyc.autoApproved || kyc.tierOne.status === "approved");
  const tierTwoApproved = Boolean(kyc.autoApproved || kyc.tierTwo.status === "approved");
  const premiumMembershipActive = membership.tier !== "Standard";
  const adminOverride = profile.role === "admin";
  const effectiveEquipmentLimit = adminOverride || premiumMembershipActive ? Number.POSITIVE_INFINITY : equipmentLimitNumber;
  const effectiveServiceLimit = adminOverride || premiumMembershipActive ? Number.POSITIVE_INFINITY : serviceLimitNumber;
  const benefitsExhausted = membership.tier === "Standard" && (
    getAdCredits(user) <= 0 ||
    listingUsage.equipmentUploaded >= effectiveEquipmentLimit ||
    (profile.role === "provider" && listingUsage.serviceUploaded >= effectiveServiceLimit)
  );

  return {
    profile,
    membership,
    accreditation,
    kyc,
    approvalStatus,
    canOperate: adminOverride ? true : canOperate,
    tierOneApproved,
    tierTwoApproved,
    kycRequiredForEquipment: false,
    kycMessage: "",
    canListEquipment: (adminOverride || canOperate) && profile.role !== "agent" && listingUsage.equipmentUploaded < effectiveEquipmentLimit,
    canListServices: (adminOverride || canOperate) && profile.role !== "agent" && listingUsage.serviceUploaded < effectiveServiceLimit,
    equipmentLimitNumber: effectiveEquipmentLimit,
    serviceLimitNumber: effectiveServiceLimit,
    listingUsage,
    benefitsExhausted,
    upgradeMessage: membership.tier === "Standard"
      ? "Your free Standard benefits are exhausted. Upgrade your membership to continue."
      : "",
  };
};

export const getAllListingDrafts = () => {
  if (typeof window === "undefined") return defaultDemoListingDrafts;
  return [...getStoredListingDrafts()];
};

export const getAdminListingQueue = () => getAllListingDrafts()
  .filter((draft) => String(draft.status || "").toLowerCase() === "pending")
  .sort((left, right) => new Date(right.savedAt || 0).getTime() - new Date(left.savedAt || 0).getTime());

export const getUserListings = (user = getAuthState().user, listingType?: "equipment" | "service") => getAllListingDrafts()
  .filter((draft) => {
    const owned = String(draft.owner || "").toLowerCase() === String(user || "").toLowerCase();
    const matchesType = !listingType || String(draft.listingType || "equipment") === listingType;
    return owned && matchesType;
  })
  .sort((left, right) => new Date(right.savedAt || 0).getTime() - new Date(left.savedAt || 0).getTime());

export const updateListingModerationStatus = (savedAt: string, status: "approved" | "rejected", note = "") => {
  if (typeof window === "undefined") return null;

  const trimmedNote = String(note || "").trim();
  const drafts = getAllListingDrafts().map((draft) => draft.savedAt === savedAt
    ? {
        ...draft,
        status,
        adminDecisionNote: trimmedNote,
        moderationUpdatedAt: new Date().toISOString(),
      }
    : draft);
  browserStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  const updated = drafts.find((draft) => draft.savedAt === savedAt) ?? null;

  if (updated?.owner) {
    pushNotification({
      user: updated.owner,
      title: status === "approved" ? "Listing approved" : "Listing rejected",
      message: `${updated.title || "Your listing"} was ${status} by the admin team.`,
      type: status === "approved" ? "success" : "warning",
      link: "/dashboard",
    });
  }

  return updated;
};

const prepareListingDraft = (payload: Record<string, FormDataEntryValue | string>, auth = getAuthState()) => {
  if (typeof window === "undefined") return { ok: false as const, error: "Listings are only available in the browser." };

  if (!auth.loggedIn || !auth.user) {
    return { ok: false as const, error: "Please sign in before creating a listing." };
  }

  const listingType = String(payload.listingType || "equipment") as "equipment" | "service";
  const requestedStatus = String(payload.status || "draft").toLowerCase();
  const normalizedStatus = requestedStatus === "uploaded" ? "pending" : requestedStatus;
  const access = getMarketplaceAccessState(auth.user);
  const isAdmin = access.profile.role === "admin" || auth.role === "admin";

  if (!access.canOperate && !isAdmin) {
    return { ok: false as const, error: "Your OEM account is pending approval. Wait for admin verification before listing equipment." };
  }

  if (listingType === "equipment" && access.profile.role === "agent" && !isAdmin) {
    return { ok: false as const, error: "Agents cannot list equipment. Use the agent dashboard for affiliate links and verification tasks." };
  }

  if (listingType === "service" && access.profile.role === "agent" && !isAdmin) {
    return { ok: false as const, error: "Agents cannot list services. Use the agent dashboard for tasks and onboarding." };
  }

  if (listingType === "equipment" && !access.canListEquipment && !isAdmin) {
    return { ok: false as const, error: access.upgradeMessage || "Your equipment listing limit has been reached.", upgradeRequired: true };
  }

  if (listingType === "service" && !access.canListServices && !isAdmin) {
    return { ok: false as const, error: access.upgradeMessage || "Your service listing limit has been reached.", upgradeRequired: true };
  }

  const normalized = Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      typeof File !== "undefined" && value instanceof File ? value.name : String(value),
    ]),
  );
  normalized.status = normalizedStatus;
  const isUpdateIntent = Boolean(String(payload.id || payload.equipmentId || "").trim());
  const owner = String(payload.owner || (isUpdateIntent ? "" : auth.user));

  const savedAt = new Date().toISOString();
  const draftId = String(normalized.id || normalized.equipmentId || savedAt);
  const nextDraft = {
    ...normalized,
    id: draftId,
    owner,
    listingType,
    savedAt,
  };

  return { ok: true as const, owner, listingType, normalizedStatus, nextDraft, auth, isUpdateIntent };

};

const persistListingLocally = (nextDraft: Record<string, string>) => {
  const current = getStoredListingDrafts().filter((draft) => String(draft.id || "") !== String(nextDraft.id || ""));
  current.unshift(nextDraft);
  const nextDrafts = current.slice(0, 20);
  browserStorage.setItem(DRAFTS_KEY, JSON.stringify(nextDrafts));
};

const createListingSuccessNotification = (owner: string, listingType: "equipment" | "service", normalizedStatus: string, title?: string) => {
  pushNotification({
    user: owner,
    title: normalizedStatus === "pending"
      ? (listingType === "service" ? "Service submitted for review" : "Equipment listing submitted for review")
      : (listingType === "service" ? "Service saved" : "Equipment listing saved"),
    message: normalizedStatus === "pending"
      ? `${title || "Your listing"} is pending admin approval before going live.`
      : `${title || "Your listing"} has been saved successfully.`,
    type: "success",
    link: "/dashboard",
    skipServerSync: true,
  });
};

export const saveListingDraftStrict = async (payload: Record<string, FormDataEntryValue | string>) => {
  const prepared = prepareListingDraft(payload);
  if (!prepared.ok) {
    return prepared;
  }

  if (!prepared.auth.token) {
    return { ok: false as const, error: "Please sign in again. Secure listing save requires an active session token." };
  }

  try {
    const normalizedDraft = Object.fromEntries(
      Object.entries(prepared.nextDraft).map(([key, value]) => [key, String(value ?? "")]),
    );
    if (prepared.isUpdateIntent) {
      await updateListingRecord(prepared.auth.token, String(prepared.nextDraft.id || ""), normalizedDraft);
    } else {
      await createListingRecord(prepared.auth.token, normalizedDraft);
    }

    persistListingLocally(prepared.nextDraft);
    createListingSuccessNotification(prepared.owner, prepared.listingType, prepared.normalizedStatus, prepared.nextDraft.title);

    if (prepared.listingType === "equipment" && prepared.normalizedStatus === "pending" && String(prepared.nextDraft.elraRegistrationId || "").trim()) {
      maybeRewardQualifiedReferral(prepared.owner);
    }

    return { ok: true as const };
  } catch (error) {
    return { ok: false as const, error: error instanceof Error ? error.message : "Listing could not be saved to the server." };
  }
};

export const saveListingDraft = (payload: Record<string, FormDataEntryValue | string>) => {
  const prepared = prepareListingDraft(payload);
  if (!prepared.ok) {
    return prepared;
  }

  persistListingLocally(prepared.nextDraft);

  if (prepared.auth.token) {
    const normalizedDraft = Object.fromEntries(
      Object.entries(prepared.nextDraft).map(([key, value]) => [key, String(value ?? "")]),
    );
    const syncRequest = prepared.isUpdateIntent
      ? updateListingRecord(prepared.auth.token, String(prepared.nextDraft.id || ""), normalizedDraft)
      : createListingRecord(prepared.auth.token, normalizedDraft);

    void syncRequest.catch(() => {
      // Listing sync fallback intentionally removed.
      // Listings must be read/written via /api/listings endpoints.
    });
  }

  createListingSuccessNotification(prepared.owner, prepared.listingType, prepared.normalizedStatus, prepared.nextDraft.title);

  if (prepared.listingType === "equipment" && prepared.normalizedStatus === "pending" && String(prepared.nextDraft.elraRegistrationId || "").trim()) {
    maybeRewardQualifiedReferral(prepared.owner);
  }

  return { ok: true };
};

const getAdRemainingDays = (campaign: AdCampaign) => {
  if (campaign.status !== "live") {
    return Math.max(0, Math.ceil(campaign.remainingDurationDays ?? campaign.durationDays ?? 0));
  }

  const activeSince = new Date(campaign.approvedAt || campaign.createdAt).getTime();
  const msRemaining = activeSince + campaign.durationDays * DAY_MS - Date.now();
  return Math.max(0, Math.ceil(msRemaining / DAY_MS));
};

const normalizeAdCampaign = (campaign: AdCampaign): AdCampaign => {
  if (campaign.status === "live") {
    const remainingDurationDays = getAdRemainingDays(campaign);
    if (remainingDurationDays <= 0) {
      return {
        ...campaign,
        status: "paused",
        remainingDurationDays: 0,
      };
    }
  }

  return {
    ...campaign,
    creativeMode: campaign.creativeMode || "image",
    overlayColor: campaign.overlayColor || "#000000",
    subtitle: campaign.subtitle || "",
    ctaLabel: campaign.ctaLabel || "Learn more",
  };
};

const persistAds = (ads: AdCampaign[]) => {
  if (typeof window === "undefined") return [] as AdCampaign[];
  const normalized = ads.map(normalizeAdCampaign).slice(0, 200);
  browserStorage.setItem(ADS_KEY, JSON.stringify(normalized));
  adsSyncCache = {
    expiresAt: Date.now() + ADS_SYNC_CACHE_TTL_MS,
    ads: normalized,
  };
  emitAdsUpdated(normalized);
  return normalized;
};

const ADS_SYNC_CACHE_TTL_MS = 60_000;
let adsSyncCache: { expiresAt: number; ads: AdCampaign[] } = {
  expiresAt: 0,
  ads: [],
};
let adsSyncPromise: Promise<AdCampaign[]> | null = null;

const saveAdToServer = async (campaign: AdCampaign) => {
  try {
    await fetch(`${PLATFORM_API_BASE}/ads`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(campaign),
    });
  } catch {
    // Keep local fallback when the server is unavailable.
  }
};

const syncAdStatusToServer = async (id: string, status: AdStatus) => {
  try {
    await fetch(`${PLATFORM_API_BASE}/ads/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });
  } catch {
    // Keep local fallback when the server is unavailable.
  }
};

export const syncAdsFromServer = async () => {
  if (typeof window === "undefined") return [] as AdCampaign[];

  if (adsSyncCache.expiresAt > Date.now()) {
    return adsSyncCache.ads;
  }

  if (adsSyncPromise) {
    return adsSyncPromise;
  }

  const localAds = safeParse<AdCampaign[]>(browserStorage.getItem(ADS_KEY), []).map(normalizeAdCampaign);

  adsSyncPromise = (async () => {
    try {
      const response = await fetch(`${PLATFORM_API_BASE}/ads`, { cache: "no-store" });
      if (!response.ok) {
        throw new Error("Unable to fetch ads from the server.");
      }

      const payload = await response.json();
      const remoteAds = Array.isArray(payload.ads) ? payload.ads.map(normalizeAdCampaign) : [];
      const mergedAds = [...remoteAds];

      localAds.forEach((campaign) => {
        if (!mergedAds.some((item) => item.id === campaign.id)) {
          mergedAds.push(campaign);
          void saveAdToServer(campaign);
        }
      });

      return persistAds(mergedAds);
    } catch {
      return persistAds(localAds);
    } finally {
      adsSyncPromise = null;
    }
  })();

  return adsSyncPromise;
};

export const getAllAds = (): AdCampaign[] => {
  if (typeof window === "undefined") return [];
  const parsed = safeParse<AdCampaign[]>(browserStorage.getItem(ADS_KEY), []);
  const normalized = parsed.map(normalizeAdCampaign);

  if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
    persistAds(normalized);
  }

  return normalized;
};

export const getAdsForUser = (user = getAuthState().user) => getAllAds().filter((campaign) => campaign.owner === user);

export const getLiveAdsForPlacement = (location: string, position: string) => {
  const compatiblePositions = position === "footer"
    ? ["footer", "leaderboard"]
    : position === "leaderboard"
      ? ["leaderboard", "footer"]
      : [position];

  const liveAds = getAllAds()
    .filter((campaign) => campaign.status === "live" && !String(campaign.id || "").startsWith("ELRA-PUBLIC-PROMO-"))
    .sort(
      (a, b) => new Date(b.approvedAt || b.createdAt).getTime() - new Date(a.approvedAt || a.createdAt).getTime(),
    );

  const exactPlacementMatches = liveAds.filter(
    (campaign) =>
      (campaign.location === location || campaign.location === "sitewide") &&
      compatiblePositions.includes(campaign.position),
  );

  if (exactPlacementMatches.length > 0) {
    return exactPlacementMatches;
  }

  const positionFallbackMatches = liveAds.filter((campaign) => compatiblePositions.includes(campaign.position));
  if (positionFallbackMatches.length > 0) {
    return positionFallbackMatches;
  }

  if (liveAds.length > 0) {
    return liveAds;
  }

  return [];
};

const getStoredAdAnalytics = () => {
  if (typeof window === "undefined") return [] as AdMetricEvent[];
  return safeParse<AdMetricEvent[]>(browserStorage.getItem(AD_ANALYTICS_KEY), []);
};

const saveStoredAdAnalytics = (events: AdMetricEvent[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(AD_ANALYTICS_KEY, JSON.stringify(events.slice(0, AD_ANALYTICS_EVENT_LIMIT)));
  emitAdAnalyticsUpdated();
};

export const syncAdAnalyticsFromServer = async (owner?: string) => {
  if (typeof window === "undefined") return [] as AdMetricEvent[];

  try {
    const query = owner ? `?owner=${encodeURIComponent(owner)}` : "";
    const response = await fetch(`${PLATFORM_API_BASE}/ad-analytics${query}`);
    if (!response.ok) {
      throw new Error("Unable to fetch ad analytics.");
    }

    const payload = await response.json();
    const remoteEvents = Array.isArray(payload?.events) ? payload.events as AdMetricEvent[] : [];
    saveStoredAdAnalytics(remoteEvents);
    return remoteEvents;
  } catch {
    return getStoredAdAnalytics();
  }
};

const recordAdAnalyticsEvent = (campaignId: string, type: "view" | "click") => {
  if (typeof window === "undefined" || !campaignId) return;

  const campaign = getAllAds().find((item) => item.id === campaignId);
  if (!campaign) return;

  const auth = getAuthState();
  const viewer = auth.user || "Guest visitor";
  if (auth.isPreview || (auth.user && auth.user === campaign.owner)) {
    return;
  }

  if (type === "view" && typeof window.sessionStorage !== "undefined") {
    const throttleKey = `elra-ad-view-${campaignId}`;
    const lastSeenAt = Number(window.sessionStorage.getItem(throttleKey) || 0);
    if (Date.now() - lastSeenAt < 15000) {
      return;
    }
    window.sessionStorage.setItem(throttleKey, String(Date.now()));
  }

  const profile = auth.user ? getUserProfile(auth.user) : undefined;
  const device: AdMetricEvent["device"] = window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const location = profile?.location || (auth.user ? "Nigeria" : "Guest visitor");

  const nextEvent: AdMetricEvent = {
    id: `ADMET-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    campaignId,
    owner: campaign.owner,
    type,
    viewer,
    viewerRole: auth.loggedIn ? auth.role : "guest",
    location,
    device,
    createdAt: new Date().toISOString(),
  };

  saveStoredAdAnalytics([nextEvent, ...getStoredAdAnalytics()]);

  fetch(`${PLATFORM_API_BASE}/ad-analytics`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(nextEvent),
  }).catch(() => undefined);
};

export const recordAdView = (campaignId: string) => recordAdAnalyticsEvent(campaignId, "view");
export const recordAdClick = (campaignId: string) => recordAdAnalyticsEvent(campaignId, "click");

export const getAdAnalyticsSummary = (campaignId: string, owner?: string) => {
  const events = getStoredAdAnalytics().filter((item) => item.campaignId === campaignId && (!owner || item.owner === owner));
  const views = events.filter((item) => item.type === "view");
  const clicks = events.filter((item) => item.type === "click");
  const ctr = views.length ? Number(((clicks.length / views.length) * 100).toFixed(1)) : 0;

  const buildSummary = (values: AdMetricEvent[], key: "location" | "device" | "viewerRole") => {
    const groups = new Map<string, { name: string; views: number; clicks: number }>();

    events.forEach((event) => {
      const groupName = String(event[key] || "Unknown");
      const current = groups.get(groupName) || { name: groupName, views: 0, clicks: 0 };
      if (event.type === "view") current.views += 1;
      if (event.type === "click") current.clicks += 1;
      groups.set(groupName, current);
    });

    return Array.from(groups.values()).sort((a, b) => b.views - a.views || b.clicks - a.clicks);
  };

  const timeline = Array.from({ length: 7 }, (_, offset) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - offset));
    const key = date.toISOString().slice(0, 10);
    const dailyEvents = events.filter((event) => event.createdAt.slice(0, 10) === key);

    return {
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
      views: dailyEvents.filter((event) => event.type === "view").length,
      clicks: dailyEvents.filter((event) => event.type === "click").length,
    };
  });

  const recentVisitors = buildSummary(events, "viewerRole").map((item) => ({
    ...item,
    role: item.name,
  }));

  return {
    views: views.length,
    clicks: clicks.length,
    ctr,
    timeline,
    byLocation: buildSummary(events, "location"),
    byDevice: buildSummary(events, "device"),
    byRole: buildSummary(events, "viewerRole"),
    recentVisitors,
  };
};

export const getAdvertCampaignSpend = ({
  adType,
  durationDays,
  location,
  position,
}: {
  adType: AdType;
  durationDays: number;
  location: AdCampaign["location"];
  position: AdCampaign["position"];
}) => {
  const validDays = Math.max(1, Number(durationDays) || 1);

  if (adType === "featured") {
    const monthly = validDays >= 28;
    const spendCredits = monthly ? 21 : validDays;
    return {
      spendCredits,
      amount: spendCredits * 1000,
      amountLabel: formatNaira(spendCredits * 1000),
      pricingLabel: monthly ? "Featured placement monthly rate" : "Featured placement daily rate",
    };
  }

  const dailyRateNaira = location === "homepage" ? 2000 : 1000;
  const spendCredits = Math.max(1, Math.ceil((dailyRateNaira * validDays) / 1000));
  const amount = dailyRateNaira * validDays;

  return {
    spendCredits,
    amount,
    amountLabel: formatNaira(Math.round(amount)),
    pricingLabel: location === "homepage"
      ? `Homepage ad placement at ₦2,000/day for ${validDays} day(s)`
      : `Other ad spots at ₦1,000/day for ${validDays} day(s)`,
  };
};

export const submitAdCampaign = (payload: {
  title: string;
  adType: AdType;
  assetUrl: string;
  destinationUrl: string;
  durationDays: number;
  location: "homepage" | "marketplace" | "dashboard" | "sitewide";
  position: "leaderboard" | "square" | "inline" | "footer";
  creativeMode?: "image" | "text";
  subtitle?: string;
  ctaLabel?: string;
}) => {
  if (typeof window === "undefined") {
    return { ok: false, error: "Ads are only available in the browser." };
  }

  const auth = getAuthState();
  const owner = auth.user || "Guest";
  const access = getMarketplaceAccessState(owner);

  if (payload.adType === "featured" && !["lessor", "oem", "provider"].includes(auth.role)) {
    return {
      ok: false,
      error: "This account can only run banner or text call-to-action adverts. Featured listing boosts are reserved for equipment owners, OEMs, and service providers with live listings.",
    };
  }

  if (!access.canOperate) {
    return {
      ok: false,
      error: "Your OEM account is pending approval. Wait for admin verification before continuing.",
      requiresApproval: true,
    };
  }

  const availableCredits = ensureAdCredits(owner);
  const spendPreview = getAdvertCampaignSpend({
    adType: payload.adType,
    durationDays: payload.durationDays,
    location: payload.location,
    position: payload.position,
  });
  const spendCredits = spendPreview.spendCredits;

  if (availableCredits < spendCredits) {
    return {
      ok: false,
      error: access.membership.tier === "Standard"
        ? `${access.upgradeMessage} This campaign needs ${spendPreview.amountLabel} in advert value but you only have ${formatNaira(availableCredits * 1000)}.`
        : `This campaign needs ${spendPreview.amountLabel} in advert value but you only have ${formatNaira(availableCredits * 1000)}.`,
      upgradeRequired: access.membership.tier === "Standard",
    };
  }

  const ads = getAllAds();
  const createdAt = new Date().toISOString();
  const campaign: AdCampaign = {
    id: `AD-${Date.now()}`,
    owner,
    title: payload.title,
    adType: payload.adType,
    assetUrl: payload.assetUrl,
    destinationUrl: payload.destinationUrl,
    durationDays: payload.durationDays,
    location: payload.location,
    position: payload.position,
    creativeMode: payload.creativeMode || "image",
    subtitle: payload.subtitle || "",
    ctaLabel: payload.ctaLabel || "Learn more",
    spendCredits,
    status: payload.adType === "featured" ? "live" : "pending",
    createdAt,
    approvedAt: payload.adType === "featured" ? createdAt : undefined,
  };

  ads.unshift(campaign);
  persistAds(ads.slice(0, 100));
  void saveAdToServer(campaign);
  setAdCredits(owner, availableCredits - spendCredits);

  return { ok: true, campaign, remainingCredits: availableCredits - spendCredits };
};

export const updateAdStatus = (id: string, status: AdStatus) => {
  if (typeof window === "undefined") return;

  const updated = getAllAds().map((campaign) => {
    if (campaign.id !== id) return campaign;

    if (status === "live") {
      const remainingDurationDays = Math.max(1, getAdRemainingDays(campaign) || campaign.durationDays || 1);
      const now = new Date().toISOString();
      return {
        ...campaign,
        status,
        durationDays: remainingDurationDays,
        remainingDurationDays: undefined,
        approvedAt: now,
      };
    }

    if (status === "paused") {
      return {
        ...campaign,
        status,
        remainingDurationDays: Math.max(0, getAdRemainingDays(campaign)),
      };
    }

    return { ...campaign, status };
  });

  persistAds(updated);
  void syncAdStatusToServer(id, status);
};

export const editAdCampaign = (
  id: string,
  payload: Partial<Pick<AdCampaign, "title" | "assetUrl" | "destinationUrl" | "location" | "position" | "subtitle" | "ctaLabel" | "creativeMode">>,
  user = getAuthState().user,
  role = getAuthState().role,
) => {
  if (typeof window === "undefined") return { ok: false, error: "Ads are only available in the browser." };

  const ads = getAllAds();
  const target = ads.find((campaign) => campaign.id === id);

  if (!target) {
    return { ok: false, error: "Ad campaign not found." };
  }

  if (target.owner !== user && role !== "admin") {
    return { ok: false, error: "You can only edit your own ad campaign." };
  }

  const remainingDurationDays = Math.max(1, getAdRemainingDays(target) || target.durationDays || 1);
  const updatedCampaign: AdCampaign = {
    ...target,
    ...payload,
    durationDays: remainingDurationDays,
    remainingDurationDays,
    status: "pending",
    lastEditedAt: new Date().toISOString(),
    approvedAt: undefined,
    title: payload.title ?? target.title,
    assetUrl: payload.assetUrl ?? target.assetUrl,
    destinationUrl: payload.destinationUrl ?? target.destinationUrl,
    subtitle: payload.subtitle ?? target.subtitle,
    ctaLabel: payload.ctaLabel ?? target.ctaLabel,
    creativeMode: payload.creativeMode ?? target.creativeMode,
  };

  const updated = ads.map((campaign) => campaign.id === id ? updatedCampaign : campaign);
  persistAds(updated);
  void saveAdToServer(updatedCampaign);

  pushNotification({
    user: target.owner,
    title: "Ad update submitted",
    message: `${updatedCampaign.title} has been updated and sent back to admin approval. ${remainingDurationDays} day(s) remain on the campaign.`,
    type: "info",
    link: "/dashboard/ads",
  });

  pushNotification({
    user: "Admin",
    title: "Ad reapproval required",
    message: `${target.owner} edited ${updatedCampaign.title}. Review it before it goes live again.`,
    type: "warning",
    link: "/admin/ads",
  });

  return { ok: true, campaign: updatedCampaign };
};

export const deleteAdCampaign = (id: string, user = getAuthState().user, role = getAuthState().role) => {
  if (typeof window === "undefined") return { ok: false, error: "Ads are only available in the browser." };

  const ads = getAllAds();
  const target = ads.find((campaign) => campaign.id === id);

  if (!target) {
    return { ok: false, error: "Ad campaign not found." };
  }

  if (target.owner !== user && role !== "admin") {
    return { ok: false, error: "You can only delete your own ad campaign." };
  }

  const updated = ads.filter((campaign) => campaign.id !== id);
  browserStorage.setItem(ADS_KEY, JSON.stringify(updated));

  pushNotification({
    user: target.owner,
    title: "Ad deleted",
    message: `${target.title} has been removed from the campaign manager.`,
    type: "info",
    link: role === "admin" ? "/admin/ads" : "/dashboard/ads",
  });

  return { ok: true };
};

const defaultBlogPosts: BlogPost[] = [];

const defaultSupportConfig: SupportConfig = {
  phone: "+234 705 032 4595",
  email: "info@elra.gov.ng",
  whatsapp: "+234 705 032 4595",
};

const defaultWebsiteSettings: WebsiteSettings = {
  websiteName: "Equipment Marketplace",
  websiteLogo: "/favicon.ico",
  websiteIcon: "",
  footerLogo: "/footerlogo.png",
  favicon: "/favicon.ico",
  siteTitle: "Equipment Marketplace",
  metaTags: "equipment leasing, marketplace, Nigeria, heavy equipment, OEMs, lessors, lessees",
  websiteDescription: "Nigeria's trusted marketplace for verified equipment. Connect with OEMs, lessors, lessees, and service providers — all in one place.",
  googleAnalyticsCode: "",
};

export const getBlogPosts = (): BlogPost[] => {
  if (typeof window === "undefined") return defaultBlogPosts;
  return safeParse<BlogPost[]>(browserStorage.getItem(BLOG_KEY), defaultBlogPosts);
};

export const syncBlogPostsFromServer = async () => {
  if (typeof window === "undefined") return defaultBlogPosts;

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/blog-posts`);
    if (!response.ok) {
      throw new Error("Unable to fetch blog posts.");
    }

    const payload = await response.json();
    const posts = Array.isArray(payload?.posts) ? payload.posts as BlogPost[] : [];
    browserStorage.setItem(BLOG_KEY, JSON.stringify(posts));
    return posts;
  } catch {
    return getBlogPosts();
  }
};

export const addBlogPost = (payload: Omit<BlogPost, "id" | "createdAt">) => {
  if (typeof window === "undefined") return undefined;
  const posts = getBlogPosts();
  const nextPost = {
    ...payload,
    status: payload.status || "published",
    id: `blog-${Date.now()}`,
    createdAt: new Date().toISOString().slice(0, 10),
  };
  posts.unshift(nextPost);
  browserStorage.setItem(BLOG_KEY, JSON.stringify(posts.slice(0, 30)));

  fetch(`${PLATFORM_API_BASE}/blog-posts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(nextPost),
  }).catch(() => undefined);

  return nextPost;
};

export const updateBlogPostStatus = (postId: string, status: "draft" | "published") => {
  if (typeof window === "undefined") return;
  const posts = getBlogPosts().map((post) => post.id === postId ? { ...post, status } : post);
  browserStorage.setItem(BLOG_KEY, JSON.stringify(posts));

  fetch(`${PLATFORM_API_BASE}/blog-posts/${encodeURIComponent(postId)}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  }).catch(() => undefined);
};

export const deleteBlogPost = (postId: string) => {
  if (typeof window === "undefined") return;
  const posts = getBlogPosts().filter((post) => post.id !== postId);
  browserStorage.setItem(BLOG_KEY, JSON.stringify(posts));

  fetch(`${PLATFORM_API_BASE}/blog-posts/${encodeURIComponent(postId)}`, {
    method: "DELETE",
  }).catch(() => undefined);
};

export const getSupportConfig = (): SupportConfig => {
  if (typeof window === "undefined") return defaultSupportConfig;
  return safeParse<SupportConfig>(browserStorage.getItem(SUPPORT_KEY), defaultSupportConfig);
};

export const saveSupportConfig = (payload: SupportConfig) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(SUPPORT_KEY, JSON.stringify(payload));

  fetch(`${PLATFORM_API_BASE}/support-config`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
};

const getSupportRequestStore = () => {
  if (typeof window === "undefined") return defaultSupportRequests;
  const stored = safeParse<SupportRequest[]>(browserStorage.getItem(SUPPORT_REQUESTS_KEY), [])
    .filter((item) => !isSeededDemoId(item.id));
  const merged = new Map<string, SupportRequest>();
  defaultSupportRequests.forEach((item) => merged.set(item.id, item));
  stored.forEach((item) => merged.set(item.id, item));
  return [...merged.values()];
};

const saveSupportRequestStore = (requests: SupportRequest[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(SUPPORT_REQUESTS_KEY, JSON.stringify(requests.slice(0, 300)));
};

export const getSupportRequests = () => getSupportRequestStore().sort(
  (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
);

export const syncSupportRequestsFromServer = async () => {
  if (typeof window === "undefined") return getSupportRequests();

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/support-requests`);
    if (!response.ok) return getSupportRequests();

    const payload = await response.json();
    const remoteRequests = Array.isArray(payload?.requests) ? payload.requests as SupportRequest[] : [];
    const localRequests = getSupportRequestStore();
    const merged = [...remoteRequests, ...localRequests].reduce<SupportRequest[]>((accumulator, item) => {
      if (!accumulator.some((entry) => entry.id === item.id)) {
        accumulator.push(item);
      }
      return accumulator;
    }, []);

    saveSupportRequestStore(merged);
    return getSupportRequests();
  } catch {
    return getSupportRequests();
  }
};

export const submitSupportRequest = async ({
  name,
  email,
  phone,
  issueType,
  message,
}: {
  name: string;
  email: string;
  phone?: string;
  issueType: string;
  message: string;
}, user = getAuthState().user) => {
  if (!String(name || "").trim() || !String(email || "").trim() || !String(message || "").trim()) {
    return { ok: false, error: "Name, email, and message are required." };
  }

  const next: SupportRequest = {
    id: `SUP-${Date.now()}`,
    name: String(name).trim(),
    email: String(email).trim().toLowerCase(),
    phone: String(phone || "").trim(),
    issueType: String(issueType || "General support").trim(),
    message: String(message).trim(),
    user: user || undefined,
    status: "New",
    createdAt: new Date().toISOString(),
  };

  saveSupportRequestStore([next, ...getSupportRequestStore()]);

  pushNotification({
    user: "Admin",
    title: "New customer support request",
    message: `${next.name} submitted a ${next.issueType.toLowerCase()} request for review.`,
    type: "info",
    link: "/admin/support",
  });

  try {
    await fetch(`${PLATFORM_API_BASE}/support-requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(next),
    });
  } catch {
    // Keep the local inbox fallback when the API is unavailable.
  }

  return { ok: true, request: next };
};

export const updateSupportRequestStatus = async (requestId: string, status: SupportRequest["status"], adminNote = "") => {
  const requests = getSupportRequestStore();
  const target = requests.find((item) => item.id === requestId);
  if (!target) return { ok: false, error: "Support request not found." };

  const updated = requests.map((item) => item.id === requestId ? { ...item, status, adminNote } : item);
  saveSupportRequestStore(updated);

  if (target.user) {
    pushNotification({
      user: target.user,
      title: "Support request updated",
      message: `Your support request is now marked as ${status.toLowerCase()}.`,
      type: status === "Resolved" ? "success" : "info",
      link: "/support",
    });
  }

  try {
    await fetch(`${PLATFORM_API_BASE}/support-requests/${encodeURIComponent(requestId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, adminNote }),
    });
  } catch {
    // Ignore API sync issues and keep local admin access working.
  }

  return { ok: true, request: updated.find((item) => item.id === requestId) };
};

const defaultSupportRequests: SupportRequest[] = [];

const normalizeWebsiteSettings = (stored: Partial<WebsiteSettings> = {}): WebsiteSettings => ({
  ...defaultWebsiteSettings,
  ...stored,
  websiteName: !stored.websiteName || stored.websiteName === "ELRA Marketplace"
    ? "Equipment Marketplace"
    : stored.websiteName,
  siteTitle: !stored.siteTitle || stored.siteTitle === "ELRA Marketplace" || stored.siteTitle === "ELRA Marketplace - Lease, Buy & Rent Equipment"
    ? "Equipment Marketplace"
    : stored.siteTitle,
  websiteDescription: !stored.websiteDescription || stored.websiteDescription === "Nigeria's leading equipment leasing and marketplace platform. Connecting owners, buyers, and service providers."
    ? "Nigeria's trusted marketplace for verified equipment. Connect with OEMs, lessors, lessees, and service providers — all in one place."
    : stored.websiteDescription,
});

const hasCustomWebsiteSettings = (settings: Partial<WebsiteSettings>) => Boolean(
  settings.websiteLogo ||
  settings.websiteIcon ||
  settings.footerLogo ||
  settings.favicon ||
  (settings.websiteName && settings.websiteName !== defaultWebsiteSettings.websiteName) ||
  (settings.siteTitle && settings.siteTitle !== defaultWebsiteSettings.siteTitle) ||
  (settings.websiteDescription && settings.websiteDescription !== defaultWebsiteSettings.websiteDescription),
);

export const getWebsiteSettings = (): WebsiteSettings => {
  if (typeof window === "undefined") return defaultWebsiteSettings;
  const stored = safeParse<Partial<WebsiteSettings>>(browserStorage.getItem(WEBSITE_SETTINGS_KEY), {});
  return normalizeWebsiteSettings(stored);
};

export const syncWebsiteSettingsFromServer = async () => {
  if (typeof window === "undefined") return defaultWebsiteSettings;

  const localSettings = getWebsiteSettings();
  const cacheKey = "website-settings";

  if (requestSyncCache.has(cacheKey) && requestSyncCache.get(cacheKey)!.expiresAt > Date.now()) {
    return requestSyncCache.get(cacheKey)!.value as WebsiteSettings;
  }

  if (requestSyncPromiseCache.has(cacheKey)) {
    return requestSyncPromiseCache.get(cacheKey) as Promise<WebsiteSettings>;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(`${PLATFORM_API_BASE}/website-settings`);
      if (!response.ok) {
        throw new Error("Unable to fetch website settings from the server.");
      }

      const payload = await response.json();
      const remoteSettings = normalizeWebsiteSettings(payload.settings || {});

      if (hasCustomWebsiteSettings(localSettings) && !hasCustomWebsiteSettings(remoteSettings)) {
        const next = saveWebsiteSettings(localSettings, true);
        setRequestSyncCache(cacheKey, next, 30_000);
        return next;
      }

      const next = saveWebsiteSettings(remoteSettings, false);
      setRequestSyncCache(cacheKey, next, 30_000);
      return next;
    } catch {
      return localSettings;
    } finally {
      requestSyncPromiseCache.delete(cacheKey);
    }
  })();

  requestSyncPromiseCache.set(cacheKey, requestPromise as Promise<unknown>);
  return requestPromise;
};

export const saveWebsiteSettings = (payload: Partial<WebsiteSettings>, syncToServer = true) => {
  if (typeof window === "undefined") return defaultWebsiteSettings;
  const next = normalizeWebsiteSettings({
    ...getWebsiteSettings(),
    ...payload,
  });
  browserStorage.setItem(WEBSITE_SETTINGS_KEY, JSON.stringify(next));
  emitWebsiteSettingsUpdated(next);

  if (syncToServer) {
    fetch(`${PLATFORM_API_BASE}/website-settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(next),
    }).catch(() => undefined);
  }

  return next;
};

const getStoredFavorites = () => {
  if (typeof window === "undefined") return [] as FavoriteEquipment[];
  return safeParse<FavoriteEquipment[]>(browserStorage.getItem(FAVORITES_KEY), []);
};

const getStorefrontCustomizationStore = () => {
  if (typeof window === "undefined") return {} as Record<string, StorefrontCustomization>;
  return safeParse<Record<string, StorefrontCustomization>>(browserStorage.getItem(STOREFRONT_CUSTOMIZATION_KEY), {});
};

export const getUserFavoriteEquipment = (user = getAuthState().user) => {
  if (!user) return [] as FavoriteEquipment[];
  return getStoredFavorites().filter((item) => item.user === user);
};

export const isEquipmentFavorited = (equipmentId: string, user = getAuthState().user) => {
  if (!equipmentId || !user) return false;
  return getUserFavoriteEquipment(user).some((item) => item.id === equipmentId);
};

export const toggleFavoriteEquipment = (equipment: Equipment, user = getAuthState().user) => {
  if (typeof window === "undefined" || !equipment?.id || !user) {
    return { added: false, favorites: [] as FavoriteEquipment[] };
  }

  const favorites = getStoredFavorites();
  const exists = favorites.some((item) => item.user === user && item.id === equipment.id);
  const next = exists
    ? favorites.filter((item) => !(item.user === user && item.id === equipment.id))
    : [{ ...equipment, user, savedAt: new Date().toISOString() }, ...favorites];

  browserStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
  void syncUserDataForUser(user);
  window.dispatchEvent(new Event("elra:favorites-changed"));

  return {
    added: !exists,
    favorites: next.filter((item) => item.user === user),
  };
};

export const buildEquipmentShareLinks = (equipment: Pick<Equipment, "id" | "title" | "location">) => {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://marketplace.nelprac.com";
  const url = `${baseUrl}${buildEquipmentLink({ id: equipment.id, title: equipment.title })}`;
  const text = `Check out ${equipment.title} on Equipment Marketplace in ${equipment.location}.`;

  return {
    url,
    text,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    email: `mailto:?subject=${encodeURIComponent(`ELRA equipment: ${equipment.title}`)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };
};

export const buildStorefrontShareLinks = ({ type, id, name }: { type: string; id: string; name: string }) => {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://marketplace.nelprac.com";
  const url = `${baseUrl}/partners/${type}/${id}`;
  const text = `Explore ${name}'s storefront on Equipment Marketplace.`;

  return {
    url,
    text,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    email: `mailto:?subject=${encodeURIComponent(`${name} storefront`)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };
};

export const getStorefrontCustomization = (profileId: string): StorefrontCustomization => {
  if (typeof window === "undefined" || !profileId) return { updatedAt: new Date().toISOString() };
  const records = getStorefrontCustomizationStore();
  return records[profileId] ?? { updatedAt: new Date().toISOString() };
};

export const saveStorefrontCustomization = (profileId: string, customization: Partial<StorefrontCustomization>) => {
  if (typeof window === "undefined" || !profileId) return null;
  const records = getStorefrontCustomizationStore();
  records[profileId] = {
    ...records[profileId],
    ...customization,
    updatedAt: new Date().toISOString(),
  };
  browserStorage.setItem(STOREFRONT_CUSTOMIZATION_KEY, JSON.stringify(records));
  void syncUserDataForUser(getAuthState().user);
  return records[profileId];
};

export const syncStorefrontCustomizationFromServer = async (profileId: string) => {
  if (typeof window === "undefined" || !profileId) return null;

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/storefront-customizations/${encodeURIComponent(profileId)}`);
    if (!response.ok) {
      throw new Error("Unable to fetch storefront customization.");
    }

    const payload = await response.json();
    if (payload?.customization) {
      const records = getStorefrontCustomizationStore();
      records[profileId] = payload.customization as StorefrontCustomization;
      browserStorage.setItem(STOREFRONT_CUSTOMIZATION_KEY, JSON.stringify(records));
      return records[profileId];
    }
  } catch {
    return null;
  }

  return null;
};

export type AccreditationStatus = "Verified" | "Not Verified" | "Pending Review" | "Cancelled";
export type EquipmentVerificationStatus = "Verified" | "Not verified" | "Pending Review";
export type EquipmentRegistrationStatus = "Registered" | "Not registered";
export type VerificationRequestStatus = "Pending Assignment" | "Assigned" | "Accepted" | "Evidence Submitted" | "Approved";

export interface UserAccreditationProfile {
  user: string;
  certificateId: string;
  accreditationStatus: AccreditationStatus;
  apiMessage: string;
  checkedAt?: string;
  verificationUrl?: string;
  expiryDate?: string;
  source?: "api" | "manual-review";
}

export type KycTierStatus = "not-started" | "pending" | "approved" | "rejected";
export type KycSubmissionType = "personal" | "business";

export interface UserKycTierOne {
  status: KycTierStatus;
  email: string;
  emailVerified: boolean;
  nin: string;
  gender: string;
  dateOfBirth: string;
  address: string;
  landmark: string;
  submittedAt?: string;
  adminNote?: string;
}

export interface UserKycTierTwo {
  status: KycTierStatus;
  submissionType: KycSubmissionType;
  bvn: string;
  idDocumentUrl: string;
  businessDocumentUrl: string;
  tin: string;
  businessAddress: string;
  registeredBusinessName: string;
  rcNumber: string;
  directorName: string;
  directorPhone: string;
  directorBvn: string;
  submittedAt?: string;
  adminNote?: string;
}

export interface UserKycSubmissionSnapshot {
  submittedAt: string;
  fullName: string;
  email: string;
  phone: string;
  role: AppRole;
  location: string;
  business: string;
  nin: string;
  dateOfBirth: string;
  bvn: string;
  governmentIdUrl: string;
}

export interface UserKycProfile {
  user: string;
  autoApproved?: boolean;
  welcomeBonusGranted?: boolean;
  submissionSnapshot?: UserKycSubmissionSnapshot;
  tierOne: UserKycTierOne;
  tierTwo: UserKycTierTwo;
}

export type TransactionRequestType = "purchase" | "lease" | "bnpl" | "negotiation" | "service";
export type TransactionRequestStatus = "Pending" | "Accepted" | "Rejected";

export const getRequestDashboardPath = (requestType: TransactionRequestType = "lease") => {
  if (requestType === "purchase" || requestType === "bnpl") return "/dashboard/purchase-requests";
  if (requestType === "service") return "/dashboard/service-requests";
  return "/dashboard/lease-requests";
};

export interface TransactionRequest {
  id: string;
  backendRequestId?: number;
  requestType: TransactionRequestType;
  pricingMode: "daily lease" | "lease to use" | "lease to own" | "purchase" | "bnpl" | "service";
  equipmentRefId: string;
  equipmentTitle: string;
  owner: string;
  ownerIdentity?: string;
  ownerEmail?: string;
  buyer: string;
  buyerIdentity?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  baseAmount: string;
  requestedAmount: string;
  negotiationPercent?: number;
  proposedAmount?: string;
  discountRequestStatus?: "Pending" | "Accepted" | "Rejected";
  discountRequestedAt?: string;
  discountReviewedAt?: string;
  status: TransactionRequestStatus;
  notes: string;
  escrowProtected?: boolean;
  paymentOption?: "marketplace-secured-wallet" | "direct-offline" | string;
  createdAt: string;
  chatMessages?: ChatMessage[];
}

export interface VerificationEvidence {
  type: "image" | "video" | "document";
  url: string;
  note: string;
  submittedAt: string;
  agent: string;
  fileName?: string;
}

export interface EquipmentVerificationRecord {
  requestId: string;
  equipmentRefId: string;
  equipmentTitle: string;
  equipmentId: string;
  owner: string;
  location: string;
  preferredVerificationDate?: string;
  requestDetails?: string;
  registrationStatus: EquipmentRegistrationStatus;
  verificationStatus: EquipmentVerificationStatus;
  requestStatus: VerificationRequestStatus;
  assignedAgent?: string;
  assignedAgentPhone?: string;
  assignedAgentEmail?: string;
  assignedAgentLocation?: string;
  assignedAt?: string;
  agentAcceptedAt?: string;
  adminDecisionNote?: string;
  lastAgentResponse?: string;
  evidence: VerificationEvidence[];
  equipmentValue: string;
  commissionAmount: string;
  commissionPaid?: boolean;
  verificationFeeAmount?: number;
  paymentStatus?: "Pending" | "Paid";
  paymentMethod?: "Wallet" | "Paystack";
  paymentReference?: string;
  paidAt?: string;
  updatedAt: string;
}

export const VERIFICATION_REQUEST_FEE_NAIRA = 5000;

export interface ServiceReview {
  id: string;
  providerId: string;
  user: string;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface EquipmentReview {
  id: string;
  equipmentId: string;
  user: string;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface MarketplaceRequest {
  id: string;
  requester: string;
  requestType: "equipment" | "service";
  title: string;
  category: string;
  location: string;
  budget: string;
  details: string;
  imageUrl: string;
  status: "pending" | "approved" | "rejected";
  lifecycleStatus?: "live" | "paused" | "cancelled" | "fulfilled";
  fulfilledBy?: string;
  fulfilledAt?: string;
  updatedAt?: string;
  deleted?: boolean;
  deletedAt?: string;
  createdAt: string;
}

export interface MarketplaceRequestResponseData {
  availability: string;
  offerAmount: string;
  timeline: string;
  deliveryPlan: string;
  note: string;
}

export interface MarketplaceRequestConversationMessage {
  id: string;
  sender: "author" | "responder" | "admin";
  senderName: string;
  text: string;
  createdAt: string;
}

export interface MarketplaceRequestConversation {
  id: string;
  requestId: string;
  requestTitle: string;
  author: string;
  responder: string;
  responderRole: AppRole;
  createdAt: string;
  updatedAt: string;
  responseData?: MarketplaceRequestResponseData;
  messages: MarketplaceRequestConversationMessage[];
}

const defaultVerificationRecords: EquipmentVerificationRecord[] = [];

const getAccreditationProfiles = () => {
  if (typeof window === "undefined") return {} as Record<string, UserAccreditationProfile>;
  return safeParse<Record<string, UserAccreditationProfile>>(browserStorage.getItem(ACCREDITATION_KEY), {});
};

export const getUserAccreditationProfile = (user = getAuthState().user): UserAccreditationProfile => {
  const profiles = getAccreditationProfiles();

  return profiles[user] ?? {
    user,
    certificateId: "",
    accreditationStatus: "Not Verified",
    apiMessage: "No ELRA certificate has been checked yet.",
  };
};

export const saveUserAccreditationProfile = (profile: UserAccreditationProfile) => {
  if (typeof window === "undefined") return profile;

  const profiles = getAccreditationProfiles();
  profiles[profile.user] = profile;
  browserStorage.setItem(ACCREDITATION_KEY, JSON.stringify(profiles));
  void syncWorkflowState({ accreditationProfiles: Object.values(profiles) });
  return profile;
};

const syncAccreditationProfilesToServer = async () => {
  const snapshot = await syncWorkflowState({ accreditationProfiles: Object.values(getAccreditationProfiles()) });
  return Boolean(snapshot);
};

const shouldRequireWorkflowPersistence = () => Boolean(getAuthState().token);

const persistAccreditationProfilesToServer = async () => {
  if (!shouldRequireWorkflowPersistence()) {
    return false;
  }

  const saved = await syncAccreditationProfilesToServer();
  if (!saved) {
    throw new Error("Accreditation data could not be saved to the live server. Please try again.");
  }

  return true;
};

export const getAllAccreditationProfiles = () => Object.values(getAccreditationProfiles());

const getKycProfiles = () => {
  if (typeof window === "undefined") return {} as Record<string, UserKycProfile>;
  return safeParse<Record<string, UserKycProfile>>(browserStorage.getItem(USER_KYC_KEY), {});
};

const buildDefaultKycProfile = (user = getAuthState().user): UserKycProfile => {
  const profile = getUserProfile(user);

  return {
    user,
    autoApproved: false,
    welcomeBonusGranted: false,
    tierOne: {
      status: "not-started",
      email: profile.email || "",
      emailVerified: Boolean(profile.email),
      nin: "",
      gender: "",
      dateOfBirth: "",
      address: "",
      landmark: "",
      adminNote: "",
    },
    tierTwo: {
      status: "not-started",
      submissionType: "personal",
      bvn: "",
      idDocumentUrl: "",
      businessDocumentUrl: "",
      tin: "",
      businessAddress: "",
      registeredBusinessName: "",
      rcNumber: "",
      directorName: "",
      directorPhone: "",
      directorBvn: "",
      adminNote: "",
    },
  };
};

export const getUserKycProfile = (user = getAuthState().user): UserKycProfile => {
  const stored = getKycProfiles()[user];
  const fallback = buildDefaultKycProfile(user);

  if (!stored) {
    return fallback;
  }

  const merged: UserKycProfile = {
    ...fallback,
    ...stored,
    autoApproved: stored.autoApproved ?? fallback.autoApproved ?? false,
    welcomeBonusGranted: stored.welcomeBonusGranted ?? fallback.welcomeBonusGranted ?? false,
    tierOne: { ...fallback.tierOne, ...stored.tierOne },
    tierTwo: { ...fallback.tierTwo, ...stored.tierTwo },
  };

  return merged;
};

export const saveUserKycProfile = (profile: UserKycProfile) => {
  if (typeof window === "undefined" || !profile.user) return profile;

  const profiles = getKycProfiles();
  profiles[profile.user] = profile;
  browserStorage.setItem(USER_KYC_KEY, JSON.stringify(profiles));
  void syncWorkflowState({ userKycProfiles: Object.values(profiles) });
  return profile;
};

const syncUserKycProfilesToServer = async () => {
  const user = getAuthState().user;
  const profiles = Object.values(getKycProfiles());
  const snapshot = await syncWorkflowState({ userKycProfiles: profiles });
  if (!snapshot) return false;
  // Verify the current user's profile was actually stored on the server.
  if (user && Array.isArray(snapshot.userKycProfiles)) {
    return (snapshot.userKycProfiles as Array<{ user?: string }>).some((p) => p?.user === user);
  }
  // If the server returned a non-null state but userKycProfiles isn't an array,
  // fall back to truthy check so behaviour degrades gracefully.
  return true;
};

const persistUserKycProfilesToServer = async () => {
  if (!shouldRequireWorkflowPersistence()) {
    return false;
  }

  const saved = await syncUserKycProfilesToServer();
  if (!saved) {
    throw new Error("KYC data could not be saved to the live server. Please try again.");
  }

  return true;
};

const persistUserProfileToServer = async (user: string, token?: string) => {
  if (!canSyncUserData(user, token)) return null;
  // Best-effort sync — never throw. A failed profile sync should not block
  // KYC submission or admin approve/reject actions.
  return syncUserDataForUser(user, token);
};

const persistKycSubmissionToLiveServer = async (user: string, token = getAuthState().token) => {
  if (!shouldRequireWorkflowPersistence() || !token) {
    return;
  }

  const [savedKycProfiles, profileSnapshot] = await Promise.all([
    syncUserKycProfilesToServer(),
    persistUserProfileToServer(user, token),
  ]);

  if (!savedKycProfiles || !profileSnapshot) {
    throw new Error("KYC data could not be saved to the live server. Please try again.");
  }

  invalidateSessionSyncCache(token);
  await hydrateSessionDataFromServer(token);
};

export const resetUserKycProgress = (user = getAuthState().user) => {
  if (typeof window === "undefined" || !user) return null;
  const fallback = buildDefaultKycProfile(user);
  const profiles = getKycProfiles();
  profiles[user] = fallback;
  browserStorage.setItem(USER_KYC_KEY, JSON.stringify(profiles));
  void syncWorkflowState({ userKycProfiles: Object.values(profiles) });
  return fallback;
};

export const getAllUserKycProfiles = () => {
  const users = new Set([
    ...Object.keys(getKycProfiles()),
    ...getAllUserProfiles().map((profile) => profile.user).filter(Boolean),
  ]);

  return Array.from(users).map((user) => getUserKycProfile(user));
};

export const submitTierOneKyc = async (payload: {
  nin: string;
  dateOfBirth: string;
}, user = getAuthState().user) => {
  if (typeof window === "undefined") return { ok: false, error: "KYC is only available in the browser." };
  if (!user) return { ok: false, error: "Please sign in before starting KYC." };
  const current = getUserKycProfile(user);

  if (!payload.nin.trim() || !payload.dateOfBirth.trim()) {
    return { ok: false, error: "Complete the NIN and date of birth fields." };
  }

  const currentProfile = getUserProfile(user);
  const submittedAt = new Date().toISOString();

  saveUserProfile({
    ...currentProfile,
    user,
    nin: payload.nin.trim(),
    dateOfBirth: payload.dateOfBirth.trim(),
  });

  const updated = saveUserKycProfile({
    ...current,
    autoApproved: false,
    submissionSnapshot: {
      submittedAt,
      fullName: currentProfile.fullName || "",
      email: currentProfile.email || "",
      phone: currentProfile.phone || "",
      role: currentProfile.role || "user",
      location: currentProfile.location || "",
      business: currentProfile.business || "",
      nin: payload.nin.trim(),
      dateOfBirth: payload.dateOfBirth.trim(),
      bvn: current.submissionSnapshot?.bvn || currentProfile.bvn || "",
      governmentIdUrl: current.submissionSnapshot?.governmentIdUrl || currentProfile.governmentIdUrl || "",
    },
    tierOne: {
      ...current.tierOne,
      status: "pending",
      email: currentProfile.email || "",
      emailVerified: Boolean(currentProfile.email),
      nin: payload.nin.trim(),
      gender: current.tierOne.gender || "",
      dateOfBirth: payload.dateOfBirth.trim(),
      address: current.tierOne.address || currentProfile.location || "",
      landmark: current.tierOne.landmark || "",
      submittedAt,
      adminNote: "Tier 1 submitted for admin review.",
    },
  });

  await persistKycSubmissionToLiveServer(user);

  pushNotification({
    user: "admin",
    title: "Tier 1 KYC submitted",
    message: `${user} submitted NIN and date of birth for KYC 1 review.`,
    type: "info",
    link: "/admin/id-verification",
  });

  return { ok: true, profile: updated };
};

export const submitTierTwoKyc = async (payload: {
  submissionType: KycSubmissionType;
  bvn?: string;
  idDocumentUrl?: string;
  businessDocumentUrl?: string;
  tin?: string;
  businessAddress?: string;
  registeredBusinessName?: string;
  rcNumber?: string;
  directorName?: string;
  directorPhone?: string;
  directorBvn?: string;
}, user = getAuthState().user) => {
  if (typeof window === "undefined") return { ok: false, error: "KYC is only available in the browser." };
  if (!user) return { ok: false, error: "Please sign in before starting KYC." };

  const current = getUserKycProfile(user);

  if (!payload.bvn?.trim() || !payload.idDocumentUrl?.trim()) {
    return { ok: false, error: "BVN and a government-issued ID upload are required for KYC 2." };
  }

  const currentProfile = getUserProfile(user);
  const submittedAt = new Date().toISOString();

  saveUserProfile({
    ...currentProfile,
    user,
    bvn: payload.bvn.trim(),
    governmentIdUrl: payload.idDocumentUrl.trim(),
  });

  const updated = saveUserKycProfile({
    ...current,
    autoApproved: false,
    submissionSnapshot: {
      submittedAt,
      fullName: currentProfile.fullName || "",
      email: currentProfile.email || "",
      phone: currentProfile.phone || "",
      role: currentProfile.role || "user",
      location: currentProfile.location || "",
      business: currentProfile.business || "",
      nin: current.tierOne.nin || currentProfile.nin || "",
      dateOfBirth: current.tierOne.dateOfBirth || currentProfile.dateOfBirth || "",
      bvn: payload.bvn.trim(),
      governmentIdUrl: payload.idDocumentUrl.trim(),
    },
    tierTwo: {
      ...current.tierTwo,
      status: "pending",
      submissionType: "personal",
      bvn: payload.bvn.trim(),
      idDocumentUrl: payload.idDocumentUrl.trim(),
      businessDocumentUrl: "",
      tin: "",
      businessAddress: "",
      registeredBusinessName: "",
      rcNumber: "",
      directorName: "",
      directorPhone: "",
      directorBvn: "",
      submittedAt,
      adminNote: "KYC 2 submitted for admin review.",
    },
  });

  await persistKycSubmissionToLiveServer(user);

  pushNotification({
    user: "admin",
    title: "Tier 2 KYC submitted",
    message: `${user} submitted BVN and a government-issued ID for KYC 2 review.`,
    type: "info",
    link: "/admin/id-verification",
  });

  return { ok: true, profile: updated };
};

export const submitUnifiedKycForm = async (payload: {
  nin: string;
  dateOfBirth: string;
  bvn: string;
  idDocumentUrl: string;
  businessDocumentUrl?: string;
}, user = getAuthState().user) => {
  if (typeof window === "undefined") return { ok: false, error: "KYC is only available in the browser." };
  if (!user) return { ok: false, error: "Please sign in before submitting KYC." };

  const nin = payload.nin.trim();
  const dateOfBirth = payload.dateOfBirth.trim();
  const bvn = payload.bvn.trim();
  const idDocumentUrl = payload.idDocumentUrl.trim();

  if (!nin || !dateOfBirth || !bvn || !idDocumentUrl) {
    return { ok: false, error: "Complete NIN, date of birth, BVN, and government ID before submitting." };
  }

  const current = getUserKycProfile(user);
  const currentProfile = getUserProfile(user);
  const submittedAt = new Date().toISOString();

  saveUserProfile({
    ...currentProfile,
    user,
    nin,
    dateOfBirth,
    bvn,
    governmentIdUrl: idDocumentUrl,
    businessDocumentUrl: payload.businessDocumentUrl || currentProfile.businessDocumentUrl,
  });

  const updated = saveUserKycProfile({
    ...current,
    autoApproved: false,
    submissionSnapshot: {
      submittedAt,
      fullName: currentProfile.fullName || "",
      email: currentProfile.email || "",
      phone: currentProfile.phone || "",
      role: currentProfile.role || "user",
      location: currentProfile.location || "",
      business: currentProfile.business || "",
      nin,
      dateOfBirth,
      bvn,
      governmentIdUrl: idDocumentUrl,
      businessDocumentUrl: payload.businessDocumentUrl || currentProfile.businessDocumentUrl,
    },
    tierOne: {
      ...current.tierOne,
      status: "pending",
      email: currentProfile.email || "",
      emailVerified: Boolean(currentProfile.email),
      nin,
      dateOfBirth,
      address: current.tierOne.address || currentProfile.location || "",
      submittedAt,
      adminNote: "Unified KYC submission sent for admin review.",
    },
    tierTwo: {
      ...current.tierTwo,
      status: "pending",
      submissionType: "personal",
      bvn,
      idDocumentUrl,
      businessDocumentUrl: payload.businessDocumentUrl || current.tierTwo.businessDocumentUrl,
      tin: "",
      businessAddress: "",
      registeredBusinessName: "",
      rcNumber: "",
      directorName: "",
      directorPhone: "",
      directorBvn: "",
      submittedAt,
      adminNote: "Unified KYC submission sent for admin review.",
    },
  });

  await persistKycSubmissionToLiveServer(user);

  pushNotification({
    user: "admin",
    title: "KYC form submitted",
    message: `${user} submitted a complete KYC data form for admin review.`,
    type: "info",
    link: "/admin/id-verification",
  });

  return { ok: true, profile: updated };
};

export const updateKycTierStatus = (user: string, tier: 1 | 2, status: KycTierStatus, adminNote = "") => {
  const current = getUserKycProfile(user);
  const currentProfile = getUserProfile(user);
  const updated = saveUserKycProfile({
    ...current,
    autoApproved: false,
    tierOne: tier === 1 ? { ...current.tierOne, status, adminNote: adminNote || current.tierOne.adminNote } : current.tierOne,
    tierTwo: tier === 2 ? { ...current.tierTwo, status, adminNote: adminNote || current.tierTwo.adminNote } : current.tierTwo,
  });

  const accreditation = getUserAccreditationProfile(user);
  const approvedByKyc = updated.tierOne.status === "approved" && updated.tierTwo.status === "approved";
  const isVerified = accreditation.accreditationStatus === "Verified" || approvedByKyc;

  saveUserProfile({
    ...currentProfile,
    user,
    listingAccessDisabled: false,
    avatarVerified: isVerified,
  });

  if (status === "approved") {
    maybeAwardWelcomeBonus(user);
    maybeRewardQualifiedReferral(user);
  }

  pushNotification({
    user,
    title: `Tier ${tier} KYC update`,
    message: `Admin marked your Tier ${tier} KYC as ${status}.`,
    type: status === "approved" ? "success" : status === "rejected" ? "warning" : "info",
    link: "/dashboard/accreditation",
  });

  return updated;
};

export const updateKycTierStatusPersisted = async (user: string, tier: 1 | 2, status: KycTierStatus, adminNote = "") => {
  const updated = updateKycTierStatus(user, tier, status, adminNote);
  await persistUserKycProfilesToServer();
  await persistUserProfileToServer(user);
  return updated;
};

export const updateKycSubmissionDecision = (user: string, status: "approved" | "rejected", adminNote = "") => {
  const current = getUserKycProfile(user);
  const currentProfile = getUserProfile(user);
  const note = adminNote || (status === "approved"
    ? "KYC submission approved by admin."
    : "KYC submission rejected by admin. You can resubmit updated details anytime.");

  const updated = saveUserKycProfile({
    ...current,
    autoApproved: false,
    tierOne: {
      ...current.tierOne,
      status,
      adminNote: note,
    },
    tierTwo: {
      ...current.tierTwo,
      status,
      adminNote: note,
    },
  });

  saveUserProfile({
    ...currentProfile,
    user,
    listingAccessDisabled: status === "rejected",
    avatarVerified: status === "approved",
  });

  if (status === "approved") {
    maybeAwardWelcomeBonus(user);
    maybeRewardQualifiedReferral(user);
  }

  pushNotification({
    user,
    title: `KYC ${status}`,
    message: status === "approved"
      ? "Admin approved your KYC submission."
      : "Admin rejected your KYC submission. You can update and resubmit anytime.",
    type: status === "approved" ? "success" : "warning",
    link: "/dashboard/accreditation",
  });

  return updated;
};

export const updateKycSubmissionDecisionPersisted = async (user: string, status: "approved" | "rejected", adminNote = "") => {
  const updated = updateKycSubmissionDecision(user, status, adminNote);
  await persistUserKycProfilesToServer();
  await persistUserProfileToServer(user);
  return updated;
};

const normalizeCertificateId = (certificateId: string) => {
  const trimmed = certificateId.trim().toUpperCase();
  if (!trimmed) return "";
  if (trimmed.startsWith("NLP-")) return trimmed;

  const suffix = trimmed.replace(/^LIC-/i, "");
  return suffix ? `LIC-${suffix}` : "";
};

export const setUserApprovalStatus = (user: string, approvalStatus: ApprovalStatus) => {
  const currentUserProfile = getUserProfile(user);
  const isApproved = approvalStatus === "approved";

  saveUserProfile({
    ...currentUserProfile,
    user,
    approvalStatus,
    banned: isApproved ? false : currentUserProfile.banned,
    avatarVerified: isApproved ? true : currentUserProfile.avatarVerified,
  });

  if (isApproved) {
    recordReferralApproval(user);
    const kyc = getUserKycProfile(user);
    if (!kyc.autoApproved) {
      saveUserKycProfile({ ...kyc, user, autoApproved: true });
    }
  }
};

export const setAccreditationStatusForAdmin = (user: string, accreditationStatus: AccreditationStatus) => {
  const profile = getUserAccreditationProfile(user);
  const currentUserProfile = getUserProfile(user);
  const updated = saveUserAccreditationProfile({
    ...profile,
    accreditationStatus,
    apiMessage: `Admin review updated this account to ${accreditationStatus}.`,
    checkedAt: new Date().toISOString(),
  });

  const isVerified = accreditationStatus === "Verified";
  saveUserProfile({
    user,
    approvalStatus: isVerified ? "approved" : "pending",
    avatarVerified: isVerified,
  });

  return updated;
};

export const setAccreditationStatusForAdminPersisted = async (user: string, accreditationStatus: AccreditationStatus) => {
  const updated = setAccreditationStatusForAdmin(user, accreditationStatus);
  await persistAccreditationProfilesToServer();
  await persistUserKycProfilesToServer();
  return updated;
};

export const verifyUserCertificate = async (user: string, certificateId: string) => {
  const normalizedCertificateId = normalizeCertificateId(certificateId);
  const currentUserProfile = getUserProfile(user);
  const existingUser = getAllAccreditationProfiles().find((profile) => normalizeCertificateId(profile.certificateId) === normalizedCertificateId && profile.user !== user);

  if (existingUser) {
    const duplicate = saveUserAccreditationProfile({
      user,
      certificateId: normalizedCertificateId,
      accreditationStatus: "Not Verified",
      apiMessage: normalizedCertificateId.startsWith("NLP-")
        ? "Another account already uses this NELPRAC ID."
        : "Account already exists for this ELRA certificate ID.",
      checkedAt: new Date().toISOString(),
      source: "manual-review",
    });
    await persistAccreditationProfilesToServer();
    return duplicate;
  }

  const verificationResult = normalizedCertificateId.startsWith("NLP-")
    ? {
        verified: false,
        status: "Pending Review" as AccreditationStatus,
        message: "NELPRAC ID submitted successfully and is awaiting admin review.",
        checkedAt: new Date().toISOString(),
        certificateNumber: normalizedCertificateId,
        verificationUrl: "https://nelrac.com.ng/login",
        expiryDate: undefined,
        source: "manual-review" as const,
      }
    : await verifyCertificateId(normalizedCertificateId);

  const result = {
    certificateNumber: verificationResult.certificateNumber || normalizedCertificateId,
    status: (verificationResult.verified ? "Verified" : verificationResult.status) as AccreditationStatus,
    message: verificationResult.message,
    checkedAt: verificationResult.checkedAt,
    verificationUrl: verificationResult.verificationUrl,
    expiryDate: verificationResult.expiryDate,
    source: verificationResult.source,
  };

  const saved = saveUserAccreditationProfile({
    user,
    certificateId: result.certificateNumber || normalizedCertificateId,
    accreditationStatus: result.status,
    apiMessage: result.message,
    checkedAt: result.checkedAt,
    verificationUrl: result.verificationUrl,
    expiryDate: result.expiryDate,
    source: result.source,
  });

  saveUserProfile({
    user,
    approvalStatus: result.status === "Verified" ? "approved" : "pending",
    avatarVerified: result.status === "Verified",
  });

  pushNotification({
    user: "admin",
    title: normalizedCertificateId.startsWith("NLP-") ? "NELPRAC ID submitted" : "ELRA certificate submitted",
    message: `${user} submitted ${normalizedCertificateId.startsWith("NLP-") ? "a NELPRAC ID" : "an ELRA certificate ID"} for review.`,
    type: "info",
    link: "/admin/id-verification",
  });

  if (result.status === "Verified") {
    const current = getUserKycProfile(user);
    saveUserKycProfile({
      ...current,
      autoApproved: true,
      tierOne: {
        ...current.tierOne,
        status: "approved",
        email: current.tierOne.email || currentUserProfile.email || "",
        emailVerified: true,
        submittedAt: current.tierOne.submittedAt || new Date().toISOString(),
        adminNote: currentUserProfile.role === "agent"
          ? "Approved automatically after verified NELPRAC accreditation."
          : "Approved automatically after verified ELRA certificate validation.",
      },
      tierTwo: {
        ...current.tierTwo,
        status: "approved",
        submittedAt: current.tierTwo.submittedAt || new Date().toISOString(),
        adminNote: currentUserProfile.role === "agent"
          ? "Approved automatically after verified NELPRAC accreditation."
          : "Approved automatically after verified ELRA certificate validation.",
      },
    });
  }

  await persistAccreditationProfilesToServer();

  if (result.status === "Verified") {
    await persistUserKycProfilesToServer();
  }

  return saved;
};

export const resetUserKycProgressPersisted = async (user = getAuthState().user) => {
  const profile = resetUserKycProgress(user);
  await persistUserKycProfilesToServer();
  return profile;
};

export const saveUserAccreditationProfilePersisted = async (profile: UserAccreditationProfile) => {
  const saved = saveUserAccreditationProfile(profile);
  await persistAccreditationProfilesToServer();
  return saved;
};

export const getVerificationApiStatus = () => {
  const profiles = getAllAccreditationProfiles().sort((a, b) => (b.checkedAt || "").localeCompare(a.checkedAt || ""));
  const latest = profiles[0];

  if (!latest) {
    return { status: "Unknown", message: "No verification call has been made yet.", lastCheckedAt: "" };
  }

  if (latest.source === "api") {
    return { status: "Connected", message: "Live ELRA certificate verification is responding normally.", lastCheckedAt: latest.checkedAt || "" };
  }

  return { status: "Manual Review", message: "Verification submissions are queued for manual admin review.", lastCheckedAt: latest.checkedAt || "" };
};

export const getEquipmentVerificationRequests = (): EquipmentVerificationRecord[] => {
  return getStoredEquipmentVerificationRequests();
};

const saveEquipmentVerificationRequests = (records: EquipmentVerificationRecord[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(EQUIPMENT_VERIFICATION_KEY, JSON.stringify(records));
  void syncWorkflowState({ equipmentVerificationRequests: records });
};

const defaultVerificationAgents = [
  { user: "Lagos Field Agent", name: "Lagos Field Agent", location: "Lagos", phone: "+234 800 111 0001", email: "lagos.agent@equipmentmarketplace.local" },
  { user: "Abuja Field Agent", name: "Abuja Field Agent", location: "Abuja", phone: "+234 800 111 0002", email: "abuja.agent@equipmentmarketplace.local" },
  { user: "Port Harcourt Field Agent", name: "Port Harcourt Field Agent", location: "Port Harcourt", phone: "+234 800 111 0003", email: "ph.agent@equipmentmarketplace.local" },
  { user: "Kano Field Agent", name: "Kano Field Agent", location: "Kano", phone: "+234 800 111 0004", email: "kano.agent@equipmentmarketplace.local" },
];

const normalizeVerificationText = (value = "") => String(value || "").trim().toLowerCase();

const cityCoordinates: Record<string, { lat: number; lng: number }> = {
  lagos: { lat: 6.5244, lng: 3.3792 },
  abuja: { lat: 9.0765, lng: 7.3986 },
  "port harcourt": { lat: 4.8156, lng: 7.0498 },
  kano: { lat: 12.0022, lng: 8.592 },
  ibadan: { lat: 7.3775, lng: 3.947 },
  enugu: { lat: 6.4499, lng: 7.5134 },
  kaduna: { lat: 10.5105, lng: 7.4165 },
  benin: { lat: 6.335, lng: 5.6037 },
  owerri: { lat: 5.4833, lng: 7.0333 },
  ilorin: { lat: 8.4966, lng: 4.5421 },
};

const extractLocationTokens = (location = "") => normalizeVerificationText(location)
  .split(/[,/|-]/)
  .map((part) => part.trim())
  .filter(Boolean);

const findKnownCoordinate = (location = "") => {
  const normalized = normalizeVerificationText(location);
  const direct = Object.entries(cityCoordinates).find(([city]) => normalized.includes(city));
  if (direct) return direct[1];

  const tokenMatch = extractLocationTokens(location)
    .map((token) => cityCoordinates[token])
    .find(Boolean);
  return tokenMatch;
};

const calculateDistanceKm = (
  from: { lat: number; lng: number } | undefined,
  to: { lat: number; lng: number } | undefined,
) => {
  if (!from || !to) return undefined;
  const radiusKm = 6371;
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(radiusKm * c);
};

const getAgentLocationScore = (agentLocation = "", requestLocation = "") => {
  const agent = normalizeVerificationText(agentLocation);
  const request = normalizeVerificationText(requestLocation);
  if (!agent || !request) return { score: 0, tier: "Unknown", distanceKm: undefined as number | undefined };

  const agentParts = extractLocationTokens(agentLocation);
  const requestParts = extractLocationTokens(requestLocation);

  const exactCityMatch = request.includes(agent) || agent.includes(request);
  const anyTokenMatch = agentParts.some((part) => requestParts.includes(part));
  const partialTokenMatch = requestParts.some((part) => part.length > 2 && agent.includes(part));

  const requestCoord = findKnownCoordinate(requestLocation);
  const agentCoord = findKnownCoordinate(agentLocation);
  const distanceKm = calculateDistanceKm(requestCoord, agentCoord);

  let distanceScore = 0;
  if (typeof distanceKm === "number") {
    if (distanceKm <= 30) distanceScore = 50;
    else if (distanceKm <= 80) distanceScore = 35;
    else if (distanceKm <= 150) distanceScore = 20;
    else if (distanceKm <= 300) distanceScore = 10;
  }

  const baseScore = exactCityMatch ? 100 : anyTokenMatch ? 70 : partialTokenMatch ? 35 : 0;
  const score = baseScore + distanceScore;
  const tier = score >= 120 ? "Exact city" : score >= 85 ? "State nearby" : score >= 45 ? "Regional" : "Far";

  return { score, tier, distanceKm };
};

export const getVerificationAgentDirectory = (location = "", search = "") => {
  const profileAgents = getAllUserProfiles()
    .filter((profile) => profile.role === "agent")
    .map((profile) => ({
      user: profile.user,
      name: profile.business || profile.fullName || profile.user,
      location: profile.location || "Nigeria",
      phone: profile.phone || "+234 800 ELRA AGENT",
      email: profile.email || `${profile.user}@equipmentmarketplace.local`,
    }));

  const registry = new Map<string, { user: string; name: string; location: string; phone: string; email: string; proximityScore?: number }>();
  [...defaultVerificationAgents, ...profileAgents].forEach((agent) => {
    const key = normalizeVerificationText(agent.user);
    if (!registry.has(key)) {
      registry.set(key, agent);
    } else {
      registry.set(key, { ...registry.get(key)!, ...agent });
    }
  });

  const query = normalizeVerificationText(search);
  return Array.from(registry.values())
    .filter((agent) => !query || [agent.user, agent.name, agent.location, agent.phone, agent.email].some((value) => normalizeVerificationText(value).includes(query)))
    .map((agent) => {
      const rank = getAgentLocationScore(agent.location, location);
      return {
        ...agent,
        proximityScore: rank.score,
        proximityTier: rank.tier,
        distanceKm: rank.distanceKm,
      };
    })
    .sort((a, b) => (b.proximityScore || 0) - (a.proximityScore || 0) || a.name.localeCompare(b.name));
};

export const suggestAgentForLocation = (location: string) => getVerificationAgentDirectory(location)[0]?.user || "Nearest ELRA Agent";

export const getVerificationAgentSummary = (agent = "", location = "") => {
  const match = getVerificationAgentDirectory(location).find((item) => normalizeVerificationText(item.user) === normalizeVerificationText(agent) || normalizeVerificationText(item.name) === normalizeVerificationText(agent));
  const profile = getUserProfile(agent || match?.user || "");

  return {
    user: match?.user || profile.user || agent,
    name: match?.name || profile.business || profile.fullName || agent || "Not assigned",
    location: match?.location || profile.location || "",
    phone: match?.phone || profile.phone || "",
    email: match?.email || profile.email || "",
  };
};

export const submitEquipmentVerificationRequest = (payload: {
  equipmentRefId: string;
  equipmentTitle: string;
  equipmentId: string;
  location: string;
  preferredVerificationDate?: string;
  requestDetails?: string;
  owner?: string;
  paymentStatus?: "Pending" | "Paid";
  paymentMethod?: "Wallet" | "Paystack";
  paymentReference?: string;
  paymentAmount?: number;
  paidAt?: string;
}) => {
  const requests = getEquipmentVerificationRequests();
  const matchedEquipment = featuredEquipment.find((item) => item.id === payload.equipmentRefId);
  const equipmentValue = matchedEquipment?.purchasePrice || matchedEquipment?.pricePerDay || "₦0";

  const request: EquipmentVerificationRecord = {
    requestId: `EVR-${Date.now()}`,
    equipmentRefId: payload.equipmentRefId,
    equipmentTitle: payload.equipmentTitle,
    equipmentId: payload.equipmentId.trim().toUpperCase(),
    owner: payload.owner || getAuthState().user || "Equipment Owner",
    location: payload.location,
    preferredVerificationDate: payload.preferredVerificationDate,
    requestDetails: payload.requestDetails,
    registrationStatus: payload.equipmentId.trim() ? "Registered" : "Not registered",
    verificationStatus: "Pending Review",
    requestStatus: "Pending Assignment",
    evidence: [],
    equipmentValue,
    commissionAmount: formatNaira(2000),
    verificationFeeAmount: payload.paymentAmount || VERIFICATION_REQUEST_FEE_NAIRA,
    paymentStatus: payload.paymentStatus || "Pending",
    paymentMethod: payload.paymentMethod,
    paymentReference: payload.paymentReference,
    paidAt: payload.paidAt,
    updatedAt: new Date().toISOString(),
  };

  const existingIndex = requests.findIndex((item) => item.equipmentRefId === payload.equipmentRefId || item.equipmentId === request.equipmentId);

  if (existingIndex >= 0) {
    requests[existingIndex] = {
      ...requests[existingIndex],
      ...request,
      evidence: requests[existingIndex].evidence || [],
    };
  } else {
    requests.unshift(request);
  }

  saveEquipmentVerificationRequests(requests);

  if (request.paymentStatus === "Paid") {
    pushNotification({
      user: "Admin",
      title: "New equipment verification request",
      message: `${request.owner} submitted ${request.equipmentTitle} for field verification in ${request.location}.`,
      type: "info",
      link: "/admin/listings",
    });

    pushNotification({
      user: request.owner,
      title: "Verification request submitted",
      message: `${request.equipmentTitle} is now waiting for admin review and agent assignment after payment confirmation.`,
      type: "success",
      link: "/dashboard/verification",
    });
  } else {
    pushNotification({
      user: request.owner,
      title: "Verification payment pending",
      message: `Complete payment for ${request.equipmentTitle} to send this request to admin assignment queue.`,
      type: "warning",
      link: "/dashboard/verification",
    });
  }

  return existingIndex >= 0 ? requests[existingIndex] : request;
};

export const assignVerificationAgent = (requestId: string, agent = "Nearest ELRA Agent") => {
  const existing = getEquipmentVerificationRequests().find((item) => item.requestId === requestId);
  if (existing && existing.paymentStatus !== "Paid") {
    return null;
  }

  const agentSummary = getVerificationAgentSummary(agent);
  const updated = getEquipmentVerificationRequests().map((item) => (
    item.requestId === requestId
      ? {
          ...item,
          assignedAgent: agentSummary.user || agent,
          assignedAgentPhone: agentSummary.phone,
          assignedAgentEmail: agentSummary.email,
          assignedAgentLocation: agentSummary.location,
          assignedAt: new Date().toISOString(),
          requestStatus: "Assigned" as VerificationRequestStatus,
          updatedAt: new Date().toISOString(),
        }
      : item
  ));

  saveEquipmentVerificationRequests(updated);
  const target = updated.find((item) => item.requestId === requestId) || null;

  if (target) {
    pushNotification({
      user: target.assignedAgent || agent,
      title: "Verification task assigned",
      message: `You have been assigned to verify ${target.equipmentTitle} in ${target.location}.`,
      type: "info",
      link: "/dashboard/agent/tasks",
    });
    pushNotification({
      user: target.owner,
      title: "Agent assigned",
      message: `${agentSummary.name} has been assigned to your equipment verification request for ${target.equipmentTitle}.`,
      type: "success",
      link: "/dashboard/verification",
    });
  }

  return target;
};

export const acceptVerificationTask = (requestId: string, agent: string) => {
  const updated = getEquipmentVerificationRequests().map((item) => (
    item.requestId === requestId
      ? {
          ...item,
          assignedAgent: agent,
          agentAcceptedAt: new Date().toISOString(),
          requestStatus: "Accepted" as VerificationRequestStatus,
          updatedAt: new Date().toISOString(),
        }
      : item
  ));

  saveEquipmentVerificationRequests(updated);
  const target = updated.find((item) => item.requestId === requestId) || null;

  if (target) {
    pushNotification({
      user: target.owner,
      title: "Agent accepted the task",
      message: `${getVerificationAgentSummary(agent, target.location).name} accepted the verification task for ${target.equipmentTitle}.`,
      type: "success",
      link: "/dashboard/verification",
    });
    pushNotification({
      user: "Admin",
      title: "Verification task accepted",
      message: `${getVerificationAgentSummary(agent, target.location).name} accepted the task for ${target.equipmentTitle}.`,
      type: "info",
      link: "/admin/listings",
    });
  }

  return target;
};

export const rejectVerificationTask = (requestId: string, agent: string, reason = "Agent unavailable for this schedule.") => {
  const updated = getEquipmentVerificationRequests().map((item) => (
    item.requestId === requestId
      ? {
          ...item,
          assignedAgent: "",
          assignedAgentPhone: "",
          assignedAgentEmail: "",
          assignedAgentLocation: "",
          lastAgentResponse: reason,
          requestStatus: "Pending Assignment" as VerificationRequestStatus,
          updatedAt: new Date().toISOString(),
        }
      : item
  ));

  saveEquipmentVerificationRequests(updated);
  const target = updated.find((item) => item.requestId === requestId) || null;

  if (target) {
    pushNotification({
      user: "Admin",
      title: "Agent rejected task",
      message: `${getVerificationAgentSummary(agent, target.location).name} rejected the verification task for ${target.equipmentTitle}.`,
      type: "warning",
      link: "/admin/listings",
    });
    pushNotification({
      user: target.owner,
      title: "Verification task needs reassignment",
      message: `Your request for ${target.equipmentTitle} is back in the assignment queue after an agent declined the task.`,
      type: "warning",
      link: "/dashboard/verification",
    });
  }

  return target;
};

export const submitVerificationEvidence = (requestId: string, evidence: Array<Omit<VerificationEvidence, "submittedAt">>) => {
  const updated = getEquipmentVerificationRequests().map((item) => {
    if (item.requestId !== requestId) return item;

    return {
      ...item,
      evidence: evidence.map((entry) => ({
        ...entry,
        submittedAt: new Date().toISOString(),
      })),
      lastAgentResponse: evidence.map((entry) => entry.note).filter(Boolean).join(" • ") || item.lastAgentResponse,
      requestStatus: "Evidence Submitted" as VerificationRequestStatus,
      updatedAt: new Date().toISOString(),
    };
  });

  saveEquipmentVerificationRequests(updated);
  const target = updated.find((item) => item.requestId === requestId) || null;

  if (target) {
    pushNotification({
      user: "Admin",
      title: "Verification evidence submitted",
      message: `${target.assignedAgent || "Assigned agent"} submitted field evidence for ${target.equipmentTitle}.`,
      type: "info",
      link: "/admin/listings",
    });
    pushNotification({
      user: target.owner,
      title: "Verification sent for admin review",
      message: `Agent evidence for ${target.equipmentTitle} has been sent to admin for final review.`,
      type: "success",
      link: "/dashboard/verification",
    });
  }

  return target;
};

export const approveEquipmentVerification = (requestId: string) => {
  const current = getEquipmentVerificationRequests();
  const target = current.find((item) => item.requestId === requestId);
  if (target && target.paymentStatus !== "Paid") {
    return null;
  }

  const updated = current.map((item) => (
    item.requestId === requestId
      ? {
          ...item,
          verificationStatus: "Verified" as EquipmentVerificationStatus,
          requestStatus: "Approved" as VerificationRequestStatus,
          registrationStatus: "Registered" as EquipmentRegistrationStatus,
          commissionPaid: true,
          adminDecisionNote: "Verification approved by admin.",
          updatedAt: new Date().toISOString(),
        }
      : item
  ));

  saveEquipmentVerificationRequests(updated);

  if (target?.assignedAgent && !target.commissionPaid) {
    const amount = 2000;
    fundWallet(target.assignedAgent, amount, `Verification commission for ${target.equipmentTitle}`);
    addWalletEntry({ user: target.assignedAgent, type: "commission", direction: "credit", amount, note: `Onboarding commission for ${target.equipmentTitle}` });
  }

  const approved = updated.find((item) => item.requestId === requestId) || null;
  if (approved) {
    pushNotification({
      user: approved.owner,
      title: "Equipment verification approved",
      message: `${approved.equipmentTitle} is now verified and approved on the marketplace.`,
      type: "success",
      link: "/dashboard/verification",
    });
    if (approved.assignedAgent) {
      pushNotification({
        user: approved.assignedAgent,
        title: "Verification approved",
        message: `Your field verification for ${approved.equipmentTitle} has been approved and your commission is ready.`,
        type: "success",
        link: "/dashboard/agent/tasks",
      });
    }
  }

  return approved;
};

export const denyEquipmentVerification = (requestId: string, adminNote = "Verification evidence was not sufficient.") => {
  const updated = getEquipmentVerificationRequests().map((item) => (
    item.requestId === requestId
      ? {
          ...item,
          verificationStatus: "Not verified" as EquipmentVerificationStatus,
          requestStatus: "Pending Assignment" as VerificationRequestStatus,
          adminDecisionNote: adminNote,
          assignedAgent: "",
          assignedAgentPhone: "",
          assignedAgentEmail: "",
          assignedAgentLocation: "",
          updatedAt: new Date().toISOString(),
        }
      : item
  ));

  saveEquipmentVerificationRequests(updated);
  const denied = updated.find((item) => item.requestId === requestId) || null;
  if (denied) {
    pushNotification({
      user: denied.owner,
      title: "Verification needs attention",
      message: `${denied.equipmentTitle} was not approved. Admin note: ${adminNote}`,
      type: "warning",
      link: "/dashboard/verification",
    });
  }

  return denied;
};

export const getEquipmentVerificationStatus = (
  equipmentRefId: string,
  fallback?: Partial<Pick<EquipmentVerificationRecord, "equipmentId" | "equipmentTitle" | "location" | "registrationStatus" | "verificationStatus">>,
) => {
  const existing = getEquipmentVerificationRequests().find((item) => item.equipmentRefId === equipmentRefId);

  if (existing) return existing;

  const fallbackValue = featuredEquipment.find((item) => item.id === equipmentRefId)?.purchasePrice || featuredEquipment.find((item) => item.id === equipmentRefId)?.pricePerDay || "₦0";

  return {
    requestId: `fallback-${equipmentRefId}`,
    equipmentRefId,
    equipmentTitle: fallback?.equipmentTitle || featuredEquipment.find((item) => item.id === equipmentRefId)?.title || "Equipment",
    equipmentId: fallback?.equipmentId || "",
    owner: "Equipment Owner",
    location: fallback?.location || "Nigeria",
    registrationStatus: fallback?.registrationStatus || "Not registered",
    verificationStatus: fallback?.verificationStatus || "Not verified",
    requestStatus: "Pending Assignment" as VerificationRequestStatus,
    evidence: [],
    equipmentValue: fallbackValue,
    commissionAmount: formatNaira(2000),
    verificationFeeAmount: VERIFICATION_REQUEST_FEE_NAIRA,
    paymentStatus: "Pending" as const,
    updatedAt: new Date().toISOString(),
  };
};

export const getAgentCommissionSummary = (agent = getAuthState().user) => {
  const records = getEquipmentVerificationRequests().filter((item) => item.assignedAgent === agent || (!item.assignedAgent && getAuthState().role === "agent"));
  const earned = records
    .filter((item) => item.verificationStatus === "Verified")
    .reduce((total, item) => total + parseCurrencyAmount(item.commissionAmount), 0);
  const pipeline = records
    .filter((item) => item.verificationStatus !== "Verified")
    .reduce((total, item) => total + parseCurrencyAmount(item.commissionAmount), 0);

  return {
    earned: formatNaira(earned),
    pipeline: formatNaira(pipeline),
    jobs: records.length,
  };
};

export const getAgentPerformanceOverview = () => {
  const records = getEquipmentVerificationRequests();
  const grouped = new Map<string, { agent: string; jobs: number; verified: number; earned: number }>();

  records.forEach((item) => {
    const agent = item.assignedAgent || "Unassigned";
    const current = grouped.get(agent) ?? { agent, jobs: 0, verified: 0, earned: 0 };
    current.jobs += 1;
    if (item.verificationStatus === "Verified") {
      current.verified += 1;
      current.earned += parseCurrencyAmount(item.commissionAmount);
    }
    grouped.set(agent, current);
  });

  return Array.from(grouped.values()).map((item) => ({
    ...item,
    earnedLabel: formatNaira(item.earned),
  }));
};

const defaultTransactionRequests: TransactionRequest[] = [];

export const getAllTransactionRequests = () => {
  if (typeof window === "undefined") return [...defaultTransactionRequests];
  return [...safeParse<TransactionRequest[]>(browserStorage.getItem(TRANSACTION_REQUESTS_KEY), defaultTransactionRequests)];
};

const saveTransactionRequests = (requests: TransactionRequest[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(TRANSACTION_REQUESTS_KEY, JSON.stringify(requests));
  void syncWorkflowState({ transactionRequests: requests });
};

const removeStoredChatThread = (threadId: string) => {
  if (typeof window === "undefined" || !threadId) return;
  const threads = getStoredChatThreads();
  if (!(threadId in threads)) return;
  delete threads[threadId];
  browserStorage.setItem(CHAT_KEY, JSON.stringify(threads));
  void syncWorkflowState({ chatThreads: threads as Record<string, unknown[]> });
};

type BackendRequestRecord = {
  id: number;
  userId: number;
  data: Record<string, unknown>;
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const normalizeServerRequestStatus = (status: unknown): TransactionRequestStatus => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "approved" || normalized === "accepted") return "Accepted";
  if (normalized === "rejected") return "Rejected";
  return "Pending";
};

const sanitizeServerChatMessages = (messages: unknown): ChatMessage[] => {
  if (!Array.isArray(messages)) return [];

  return messages
    .map((item) => ({
      sender: (["user", "owner", "system"].includes(String((item as { sender?: unknown })?.sender || ""))
        ? String((item as { sender?: unknown })?.sender)
        : "system") as ChatMessage["sender"],
      text: String((item as { text?: unknown })?.text || "").trim(),
      senderName: String((item as { senderName?: unknown })?.senderName || "").trim() || undefined,
      createdAt: (item as { createdAt?: unknown })?.createdAt
        ? new Date(String((item as { createdAt?: unknown })?.createdAt)).toISOString()
        : new Date().toISOString(),
      receipt: typeof (item as { receipt?: unknown })?.receipt === "object" && (item as { receipt?: unknown })?.receipt
        ? (item as { receipt?: TransactionReceiptAttachment }).receipt
        : undefined,
    }))
    .filter((item) => item.text);
};

const toTransactionRequestFromServerRecord = (record: BackendRequestRecord): TransactionRequest | null => {
  const data = record?.data;
  if (!data || typeof data !== "object") return null;

  const requestTypeRaw = String(data.requestType || "").trim().toLowerCase();
  const requestType = (["lease", "purchase", "bnpl", "negotiation", "service"].includes(requestTypeRaw)
    ? requestTypeRaw
    : "lease") as TransactionRequestType;

  const pricingModeRaw = String(data.pricingMode || "").trim().toLowerCase();
  const pricingMode = (["daily lease", "lease to use", "lease to own", "purchase", "bnpl", "service"].includes(pricingModeRaw)
    ? pricingModeRaw
    : (requestType === "service" ? "service" : requestType === "purchase" ? "purchase" : "lease to use")) as TransactionRequest["pricingMode"];

  const id = String(data.transactionRequestId || `SRVREQ-${record.id}`).trim();
  if (!id) return null;

  return {
    id,
    backendRequestId: Number(record.id) || undefined,
    requestType,
    pricingMode,
    equipmentRefId: String(data.equipmentRefId || "").trim() || `request-${record.id}`,
    equipmentTitle: String(data.equipmentTitle || data.title || "Marketplace request").trim() || "Marketplace request",
    owner: String(data.owner || "").trim() || "owner",
    ownerIdentity: String(data.ownerIdentity || "").trim() || undefined,
    ownerEmail: String(data.ownerEmail || "").trim() || undefined,
    buyer: String(data.buyer || "").trim() || "buyer",
    buyerIdentity: String(data.buyerIdentity || "").trim() || undefined,
    buyerPhone: String(data.buyerPhone || "").trim() || undefined,
    buyerEmail: String(data.buyerEmail || "").trim() || undefined,
    baseAmount: String(data.baseAmount || data.budget || "Pending quote").trim() || "Pending quote",
    requestedAmount: String(data.requestedAmount || data.budget || "Pending quote").trim() || "Pending quote",
    status: normalizeServerRequestStatus(data.status || record.status),
    notes: String(data.notes || data.details || "").trim(),
    escrowProtected: Boolean(data.escrowProtected),
    paymentOption: String(data.paymentOption || "").trim() || undefined,
    createdAt: String(data.createdAt || record.createdAt || new Date().toISOString()),
    chatMessages: sanitizeServerChatMessages(data.chatMessages),
  };
};

const mergeChatThreadsFromRequests = (requests: TransactionRequest[]) => {
  if (typeof window === "undefined") return;

  const threads = getStoredChatThreads();
  let changed = false;

  requests.forEach((request) => {
    const serverMessages = Array.isArray(request.chatMessages) ? request.chatMessages : [];
    if (serverMessages.length === 0) return;

    const current = Array.isArray(threads[request.id]) ? threads[request.id] : [];
    if (serverMessages.length >= current.length) {
      threads[request.id] = serverMessages;
      changed = true;
    }
  });

  if (changed) {
    browserStorage.setItem(CHAT_KEY, JSON.stringify(threads));
    void syncWorkflowState({ chatThreads: threads as Record<string, unknown[]> });
  }
};

const toTransactionRequestFromLeaseConversation = (
  conversation: {
    id: string;
    threadId: string;
    equipmentId: string;
    lessorUser: string;
    lesseeUser: string;
    requestType: "lease" | "purchase" | "service" | "bnpl";
    status: "pending" | "accepted" | "rejected" | "active" | "completed";
    baseAmount?: string;
    requestedAmount?: string;
    terms?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    createdAt: string;
  },
  messages: Array<{
    senderRole: "lessor" | "lessee" | "system";
    senderUser: string;
    content: string;
    createdAt: string;
  }> = [],
): TransactionRequest => {
  const metadata = (conversation.metadata && typeof conversation.metadata === "object") ? conversation.metadata : {};
  const terms = (conversation.terms && typeof conversation.terms === "object") ? conversation.terms : {};
  const requestType = (conversation.requestType === "purchase" || conversation.requestType === "service" || conversation.requestType === "bnpl")
    ? conversation.requestType
    : "lease";

  const normalizeStatus = (status: string): TransactionRequestStatus => {
    if (status === "accepted" || status === "active" || status === "completed") return "Accepted";
    if (status === "rejected") return "Rejected";
    return "Pending";
  };

  const pricingMode = normalizeTransactionPricingMode(
    (terms as Record<string, unknown>)?.pricingMode || (metadata as Record<string, unknown>)?.pricingMode,
    requestType,
  );

  const chatMessages = messages
    .map((message) => ({
      sender: message.senderRole === "lessor" ? "owner" : message.senderRole === "lessee" ? "user" : "system",
      senderName: message.senderUser,
      text: String(message.content || "").trim(),
      createdAt: message.createdAt,
    }))
    .filter((message) => message.text.length > 0)
    .sort((left, right) => new Date(left.createdAt || 0).getTime() - new Date(right.createdAt || 0).getTime());

  return {
    id: String(conversation.threadId || conversation.id || `TRX-${Date.now()}`),
    requestType,
    pricingMode,
    equipmentRefId: String(conversation.equipmentId || metadata.equipmentRefId || conversation.id || "lease-request"),
    equipmentTitle: String(metadata.equipmentTitle || metadata.title || conversation.equipmentId || "Marketplace lease request"),
    owner: String(conversation.lessorUser || metadata.owner || "owner"),
    ownerIdentity: String(metadata.ownerIdentity || "").trim() || undefined,
    ownerEmail: String(metadata.ownerEmail || "").trim() || undefined,
    buyer: String(conversation.lesseeUser || metadata.buyer || "buyer"),
    buyerIdentity: String(metadata.buyerIdentity || "").trim() || undefined,
    buyerPhone: String(metadata.buyerPhone || "").trim() || undefined,
    buyerEmail: String(metadata.buyerEmail || "").trim() || undefined,
    baseAmount: String(conversation.baseAmount || metadata.baseAmount || "Pending quote"),
    requestedAmount: String(conversation.requestedAmount || metadata.requestedAmount || conversation.baseAmount || "Pending quote"),
    status: normalizeStatus(String(conversation.status || "pending").toLowerCase()),
    notes: String(metadata.notes || "").trim(),
    escrowProtected: Boolean(metadata.escrowProtected),
    paymentOption: String(metadata.paymentOption || "").trim() || undefined,
    createdAt: String(conversation.createdAt || new Date().toISOString()),
    chatMessages,
  };
};

export const syncTransactionRequestsFromServer = async () => {
  const auth = getAuthState();
  if (!auth.token) return null;

  try {
    const response = await fetch(buildApiUrl("/api/requests/mine"), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      cache: "no-store",
    });

    if (!response.ok) return null;

    const payload = await response.json() as { requests?: BackendRequestRecord[] };
    const serverRequests = Array.isArray(payload.requests)
      ? payload.requests.map(toTransactionRequestFromServerRecord).filter(Boolean) as TransactionRequest[]
      : [];

    let leaseRequests: TransactionRequest[] = [];
    try {
      const { fetchLeaseConversations, fetchLeaseMessages } = await import("@/lib/leaseApi");
      const conversations = await fetchLeaseConversations(auth.token);
      const detailedConversations = await Promise.all(
        conversations.map(async (conversation) => {
          try {
            const messages = await fetchLeaseMessages(auth.token, conversation.id);
            return { conversation, messages };
          } catch {
            return { conversation, messages: [] };
          }
        }),
      );

      leaseRequests = detailedConversations.map(({ conversation, messages }) => (
        toTransactionRequestFromLeaseConversation(conversation, messages)
      ));
    } catch {
      leaseRequests = [];
    }

    if (serverRequests.length === 0 && leaseRequests.length === 0) return null;

    const localRequests = getAllTransactionRequests();
    const mergedById = new Map<string, TransactionRequest>();
    [...localRequests, ...serverRequests, ...leaseRequests].forEach((request) => {
      if (!request?.id) return;
      mergedById.set(request.id, request);
    });

    const merged = Array.from(mergedById.values())
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

    saveTransactionRequests(merged);
    mergeChatThreadsFromRequests(merged);
    return merged;
  } catch {
    return null;
  }
};

const persistTransactionRequestToBackend = async (request: TransactionRequest) => {
  const auth = getAuthState();
  if (!auth.token) return false;

  // Lease requests now use the dedicated /api/lease flow and should not be
  // persisted as admin-moderated marketplace requests.
  if (request.requestType === "lease") {
    return true;
  }

  try {
    const response = await fetch(buildApiUrl("/api/requests"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        data: {
          submissionType: "marketplace-request",
          requestType: request.requestType,
          pricingMode: request.pricingMode,
          equipmentRefId: request.equipmentRefId,
          equipmentTitle: request.equipmentTitle,
          owner: request.owner,
          ownerIdentity: request.ownerIdentity,
          ownerEmail: request.ownerEmail,
          buyer: request.buyer,
          buyerIdentity: request.buyerIdentity,
          buyerPhone: request.buyerPhone,
          buyerEmail: request.buyerEmail,
          baseAmount: request.baseAmount,
          requestedAmount: request.requestedAmount,
          status: request.status,
          notes: request.notes,
          chatMessages: Array.isArray(request.chatMessages) ? request.chatMessages : [],
          transactionRequestId: request.id,
          title: `${request.requestType === "service" ? "Service" : request.requestType === "purchase" ? "Purchase" : "Lease"} request for ${request.equipmentTitle}`,
          category: request.requestType === "service" ? "Service" : "Equipment",
          location: "Nigeria",
          budget: request.requestedAmount,
          details: request.notes,
        },
      }),
    });

    return response.ok;
  } catch {
    return false;
  }
};

const toBackendTransactionStatus = (status: TransactionRequestStatus) => {
  if (status === "Accepted") return "approved";
  if (status === "Rejected") return "rejected";
  return "pending";
};

const normalizeTransactionPricingMode = (
  candidate: unknown,
  fallbackRequestType: TransactionRequestType = "lease",
): TransactionRequest["pricingMode"] => {
  const normalized = String(candidate || "").trim().toLowerCase();
  if (["daily lease", "lease to use", "lease to own", "purchase", "bnpl", "service"].includes(normalized)) {
    return normalized as TransactionRequest["pricingMode"];
  }
  if (fallbackRequestType === "service") return "service";
  if (fallbackRequestType === "purchase") return "purchase";
  if (fallbackRequestType === "bnpl") return "bnpl";
  return "lease to use";
};

const persistTransactionRequestPatchToBackend = async (
  transactionRequestId: string,
  payload: {
    status?: TransactionRequestStatus;
    chatMessages?: ChatMessage[];
    dataUpdates?: Record<string, unknown>;
  },
) => {
  const auth = getAuthState();
  if (!auth.token || !transactionRequestId) return false;

  // New lease flow stores TRX-* as conversation thread ids; route status
  // updates through /api/lease/respond to avoid 404s on legacy request patch.
  if (transactionRequestId.startsWith("TRX-") && payload.status && ["Accepted", "Rejected"].includes(payload.status)) {
    try {
      const { fetchLeaseConversations, respondToLeaseRequestApi } = await import("@/lib/leaseApi");
      const conversations = await fetchLeaseConversations(auth.token);
      const targetConversation = conversations.find((item) => item.threadId === transactionRequestId);

      if (targetConversation?.id) {
        const action = payload.status === "Accepted" ? "accept" : "reject";
        await respondToLeaseRequestApi(auth.token, targetConversation.id, action);
        return true;
      }
    } catch {
      // Fall back to the legacy endpoint below.
    }
  }

  // Persist chat payload for TRX conversation threads through /api/chat/:threadId.
  // This keeps chat + receipt messages durable even when using lease conversations.
  if (transactionRequestId.startsWith("TRX-")) {
    if (Array.isArray(payload.chatMessages) && payload.chatMessages.length > 0) {
      const latestMessage = payload.chatMessages[payload.chatMessages.length - 1];
      if (latestMessage && (latestMessage.text || latestMessage.receipt)) {
        try {
          const response = await fetch(buildApiUrl(`/api/chat/${encodeURIComponent(transactionRequestId)}`), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth.token}`,
            },
            body: JSON.stringify({
              text: String(latestMessage.text || "").trim() || "(attachment)",
              senderRole: latestMessage.sender,
              senderName: latestMessage.senderName,
              ...(latestMessage.receipt ? { receiptJson: JSON.stringify(latestMessage.receipt) } : {}),
            }),
          });

          if (response.ok) {
            return true;
          }
        } catch {
          // Fall through to return true to avoid blocking local UX.
        }
      }
    }

    return true;
  }

  try {
    const response = await fetch(buildApiUrl(`/api/requests/transaction/${encodeURIComponent(transactionRequestId)}`), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        ...(payload.status ? { status: toBackendTransactionStatus(payload.status) } : {}),
        ...(Array.isArray(payload.chatMessages) ? { chatMessages: payload.chatMessages } : {}),
        ...(payload.dataUpdates && typeof payload.dataUpdates === "object" ? { dataUpdates: payload.dataUpdates } : {}),
      }),
    });

    return response.ok;
  } catch {
    return false;
  }
};

const persistTransactionRequestDeleteToBackend = async (transactionRequestId: string) => {
  const auth = getAuthState();
  if (!auth.token || !transactionRequestId) return false;

  if (transactionRequestId.startsWith("TRX-")) {
    return true;
  }

  try {
    const response = await fetch(buildApiUrl(`/api/requests/transaction/${encodeURIComponent(transactionRequestId)}`), {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    });

    return response.ok;
  } catch {
    return false;
  }
};

export const getRentalAgreements = (user = getAuthState().user, role = getAuthState().role) => {
  if (typeof window === "undefined") return [] as RentalAgreement[];
  const agreements = [...safeParse<RentalAgreement[]>(browserStorage.getItem(RENTAL_AGREEMENTS_KEY), [])];
  if (role === "admin") return agreements;

  const normalizedViewer = String(user || "").trim().toLowerCase();
  const viewerProfile = getUserProfile(user);
  const viewerKeys = new Set(
    [user, viewerProfile.user, viewerProfile.email, viewerProfile.fullName]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean),
  );

  return agreements.filter((item) => {
    const ownerProfile = getUserProfile(item.owner);
    const customerProfile = getUserProfile(item.customer);
    const agreementKeys = [
      item.owner,
      ownerProfile.user,
      ownerProfile.email,
      ownerProfile.fullName,
      item.customer,
      customerProfile.user,
      customerProfile.email,
      customerProfile.fullName,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    return agreementKeys.some((value) => viewerKeys.has(value)) || item.owner === user || item.customer === user;
  });
};

const saveRentalAgreements = (agreements: RentalAgreement[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(RENTAL_AGREEMENTS_KEY, JSON.stringify(agreements));
  void syncWorkflowState({ rentalAgreements: agreements });
};

export const getLeaseAgreementCertificates = (user = getAuthState().user, role = getAuthState().role) => {
  if (typeof window === "undefined") return [] as LeaseAgreementCertificate[];
  const certificates = getStoredLeaseAgreementCertificates();
  if (role === "admin") return certificates;
  return certificates.filter((item) => item.user === user);
};

const saveLeaseAgreementCertificates = (certificates: LeaseAgreementCertificate[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(LEASE_AGREEMENT_CERTIFICATES_KEY, JSON.stringify(certificates));
  void syncWorkflowState({ leaseAgreementCertificates: certificates });
};

export const uploadLeaseAgreementCertificate = ({
  agreementId,
  certificateName,
  certificateNumber,
  certificateUrl,
}: {
  agreementId: string;
  certificateName: string;
  certificateNumber: string;
  certificateUrl?: string;
}, user = getAuthState().user) => {
  if (typeof window === "undefined") return Promise.resolve({ ok: false, error: "Only available in the browser." });
  if (!user) return { ok: false, error: "Please sign in first." };

  const agreement = getRentalAgreements("", "admin").find((item) => item.id === agreementId);
  if (!agreement) return { ok: false, error: "Lease agreement not found." };
  if (![agreement.owner, agreement.customer].includes(user)) {
    return { ok: false, error: "Only the lease parties can upload the ELRA agreement certificate." };
  }
  if (!String(certificateNumber || "").trim()) return { ok: false, error: "Enter the ELRA certificate number first." };

  return verifyCertificateId(certificateNumber).then((verification) => {
    if (!verification.verified || verification.status !== "Verified") {
      return {
        ok: false,
        error: verification.message || "ELRA certificate verification failed.",
      };
    }

    const certificates = getLeaseAgreementCertificates("", "admin");
    certificates.unshift({
      id: `LAC-${Date.now()}`,
      agreementId,
      user,
      equipmentTitle: agreement.equipmentTitle,
      certificateName: certificateName.trim() || `${agreement.equipmentTitle} ELRA certificate`,
      certificateNumber: verification.certificateNumber || String(certificateNumber).trim().toUpperCase(),
      certificateUrl: String(certificateUrl || "").trim(),
      verificationStatus: verification.status,
      verificationMessage: verification.message,
      verificationUrl: verification.verificationUrl,
      expiryDate: verification.expiryDate,
      verifiedAt: verification.checkedAt,
      status: verification.status === "Verified" ? "Approved" : "Under review",
      uploadedAt: new Date().toISOString(),
    });
    saveLeaseAgreementCertificates(certificates);

    pushNotification({
      user: "admin",
      title: "Lease agreement certificate submitted",
      message: `${user} uploaded a verified ELRA lease agreement certificate for ${agreement.equipmentTitle}.`,
      type: "info",
      link: "/admin/lease-certificates",
    });

    return { ok: true };
  });
};

export const updateLeaseAgreementCertificateStatus = (certificateId: string, status: LeaseAgreementCertificate["status"], adminNote = "") => {
  const certificates = getLeaseAgreementCertificates("", "admin").map((item) => item.id === certificateId
    ? {
        ...item,
        status,
        adminNote: adminNote || item.adminNote,
        reviewedAt: new Date().toISOString(),
        reviewedBy: "admin",
      }
    : item);

  saveLeaseAgreementCertificates(certificates);
  const updated = certificates.find((item) => item.id === certificateId) ?? null;

  if (updated) {
    pushNotification({
      user: updated.user,
      title: "Lease agreement certificate update",
      message: `${updated.equipmentTitle} certificate is now ${status.toLowerCase()}.`,
      type: status === "Approved" ? "success" : status === "Rejected" ? "warning" : "info",
      link: "/dashboard/lease-agreements",
    });
  }

  return updated;
};

export const getUserReports = () => {
  if (typeof window === "undefined") return [] as UserReport[];
  return getStoredUserReports();
};

const saveUserReports = (reports: UserReport[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(USER_REPORTS_KEY, JSON.stringify(reports));
  void syncWorkflowState({ userReports: reports });
};

const parseCurrencyAmountWithSuffix = (value: string) => {
  const numeric = Number(String(value || "").replace(/[^\d.]/g, "")) || 0;
  const suffix = value.includes("/day")
    ? "/day"
    : value.includes("/week")
      ? "/week"
      : value.includes("/month")
        ? "/month"
        : "";

  return { numeric, suffix };
};

const computeDiscountedRequestAmount = (value: string, percent: number) => {
  const { numeric, suffix } = parseCurrencyAmountWithSuffix(value);
  const discounted = Math.max(0, numeric - (numeric * percent / 100));
  return `${formatNaira(discounted)}${suffix}`;
};

const createRentalAgreementFromRequest = (request: TransactionRequest) => {
  if (!["lease", "purchase", "bnpl", "service"].includes(request.requestType)) return;

  const agreements = getRentalAgreements("", "admin");
  if (agreements.some((item) => item.requestId === request.id)) return;

  const ownerProfile = getUserProfile(request.owner);
  const customerProfile = getUserProfile(request.buyer);

  const startDate = new Date();
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + (request.requestType === "lease" ? 90 : request.requestType === "service" ? 30 : 365));

  agreements.unshift({
    id: `AGR-${Date.now()}`,
    requestId: request.id,
    equipmentRefId: request.equipmentRefId,
    equipmentTitle: request.equipmentTitle,
    owner: request.owner,
    customer: request.buyer,
    possessionHolder: request.buyer,
    status: "Active",
    agreementType: request.requestType,
    baseAmount: request.baseAmount,
    negotiatedAmount: request.requestedAmount,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    leaseTerms: request.requestType === "service"
      ? `Service engagement approved at ${request.requestedAmount}. Scope, delivery timing, and service obligations should follow the conversation summary and final signed agreement.`
      : `Usage approved for ${request.requestType}. Payment reference amount: ${request.requestedAmount}. Return and service conditions apply per Equipment Marketplace policy.`,
    ownerPhone: ownerProfile.phone || request.buyerPhone || "+234 800 ELRA OWNER",
    customerPhone: customerProfile.phone || request.buyerPhone || "+234 800 ELRA USER",
    deliveryLocation: request.notes || "Delivery and inspection details to be agreed in chat.",
    ownerDeliveryStatus: "Pending",
    customerReceiptStatus: "Pending",
    adminAttentionRequired: false,
  });

  saveRentalAgreements(agreements);

  if (request.escrowProtected) {
    const existingEscrow = getEscrowRecords().find((item) => item.requestId === request.id);
    if (!existingEscrow) {
      createEscrowRecord({
        requestId: request.id,
        equipmentRefId: request.equipmentRefId,
        equipmentTitle: request.equipmentTitle,
        owner: request.owner,
        customer: request.buyer,
        amount: request.requestedAmount,
        requestType: request.requestType,
      });
    }
  }
};

export const submitUserReport = ({
  agreementId,
  equipmentTitle,
  targetUser,
  reason,
  severity,
  complaint,
}: Omit<UserReport, "id" | "reportedBy" | "status" | "createdAt">) => {
  const reporter = getAuthState().user;
  if (typeof window === "undefined" || !reporter) return { ok: false, error: "Sign in required." };

  const reports = getUserReports();
  reports.unshift({
    id: `RPT-${Date.now()}`,
    agreementId,
    equipmentTitle,
    reportedBy: reporter,
    targetUser,
    reason,
    severity,
    complaint,
    status: "Open",
    createdAt: new Date().toISOString(),
  });
  saveUserReports(reports);

  pushNotification({
    user: "Admin",
    title: "New user report",
    message: `${reporter} submitted a ${severity.toLowerCase()} report for ${equipmentTitle}.`,
    type: severity === "Critical" ? "warning" : "info",
    link: "/admin/platform",
  });

  return { ok: true };
};

export const updateUserReportStatus = (reportId: string, status: UserReport["status"], assignedAgent?: string) => {
  const reports = getUserReports().map((item) => item.id === reportId ? { ...item, status, assignedAgent } : item);
  saveUserReports(reports);
  const updated = reports.find((item) => item.id === reportId) ?? null;

  if (updated) {
    pushNotification({
      user: updated.reportedBy,
      title: "Report updated",
      message: `${updated.equipmentTitle} report is now marked as ${status.toLowerCase()}.`,
      type: status === "Resolved" ? "success" : "info",
      link: "/dashboard/rentals",
    });
  }

  return updated;
};

export const createTransactionRequest = (payload: Omit<TransactionRequest, "id" | "createdAt" | "status">) => {
  const buyerProfile = getUserProfile(payload.buyer);
  const normalizeParty = (value?: string) => String(value || "").trim().toLowerCase();
  const ownerIdentity = normalizeParty(payload.owner);
  const buyerIdentities = [payload.buyer, buyerProfile.fullName, buyerProfile.business].map(normalizeParty).filter(Boolean);

  if (ownerIdentity && buyerIdentities.some((value) => value === ownerIdentity || value.includes(ownerIdentity) || ownerIdentity.includes(value))) {
    return null;
  }

  const requests = getAllTransactionRequests();
  const createdAt = new Date().toISOString();
  const initialThread: ChatMessage[] = [
    {
      sender: "system",
      text: `${payload.requestType === "service" ? "Service" : payload.requestType === "purchase" ? "Purchase" : "Lease"} request created for ${payload.equipmentTitle}.`,
      createdAt,
    },
    {
      sender: "system",
      text: "This thread is now open. Both parties can chat here end-to-end, including payment and delivery updates.",
      createdAt,
    },
    {
      sender: "system",
      text: payload.escrowProtected
        ? "Escrow protected flow is enabled for this request. Owner/provider can mark delivery, then customer confirms receipt to release funds."
        : "This request is currently outside escrow protection unless both parties switch to the secured wallet flow.",
      createdAt,
    },
  ];

  const next: TransactionRequest = {
    ...payload,
    id: `TRX-${Date.now()}`,
    createdAt,
    status: "Pending",
    chatMessages: initialThread,
  };

  requests.unshift(next);
  saveTransactionRequests(requests);

  pushNotification({
    user: next.owner,
    title: next.requestType === "service" ? "New service request" : next.requestType === "purchase" ? "New purchase request" : "New lease request",
    message: next.discountRequestStatus === "Pending" && next.negotiationPercent && next.proposedAmount
      ? `${next.buyer} submitted a ${next.requestType} request for ${next.equipmentTitle} and requested ${next.negotiationPercent}% off. Proposed amount: ${next.proposedAmount}.`
      : `${next.buyer} submitted a ${next.requestType} request for ${next.equipmentTitle} at ${next.requestedAmount}.`,
    type: "info",
    link: getRequestDashboardPath(next.requestType),
  });

  pushNotification({
    user: next.buyer,
    title: "Request submitted",
    message: `Your ${next.requestType} request for ${next.equipmentTitle} is pending owner review.`,
    type: "success",
    link: getRequestDashboardPath(next.requestType),
  });

  const existingThread = getChatThread(next.id, []);
  if (existingThread.length === 0) {
    saveChatThread(next.id, initialThread);
  }

  void persistTransactionRequestToBackend(next);

  return next;
};

export const updateTransactionRequestStatus = (requestId: string, status: TransactionRequestStatus) => {
  const requests = getAllTransactionRequests().map((item) => (
    item.id === requestId ? { ...item, status } : item
  ));

  saveTransactionRequests(requests);

  const changed = requests.find((item) => item.id === requestId);
  if (changed) {
    const existingThread = getChatThread(changed.id, []);
    const actorLabel = changed.requestType === "service" ? "provider" : "equipment owner";
    const requestLabel = changed.requestType === "purchase" ? "Purchase" : changed.requestType === "service" ? "Service" : "Lease";
    saveChatThread(changed.id, [
      ...existingThread,
      {
        sender: "owner",
        text: `${requestLabel} request ${status.toLowerCase()} by the ${actorLabel}.`,
      },
    ]);

    pushNotification({
      user: changed.buyer,
      title: `${requestLabel} ${status}`,
      message: `${changed.equipmentTitle} has been ${status.toLowerCase()} by the owner.`,
      type: status === "Accepted" ? "success" : status === "Rejected" ? "warning" : "info",
      link: status === "Accepted" ? "/dashboard/lease-agreements" : getRequestDashboardPath(changed.requestType),
    });

    pushNotification({
      user: changed.owner,
      title: `Request ${status.toLowerCase()}`,
      message: `You updated the ${changed.requestType} request for ${changed.equipmentTitle}.`,
      type: "info",
      link: getRequestDashboardPath(changed.requestType),
    });

    void persistTransactionRequestPatchToBackend(changed.id, {
      status,
      chatMessages: getChatThread(changed.id, []),
    });

    if (status === "Accepted") {
      createRentalAgreementFromRequest(changed);
    }
  }
};

export const removeTransactionRequest = async (requestId: string) => {
  if (typeof window === "undefined" || !requestId) return { ok: false, error: "Request id is required." };

  const requests = getAllTransactionRequests();
  const target = requests.find((item) => item.id === requestId);
  if (!target) return { ok: false, error: "Request not found." };

  saveTransactionRequests(requests.filter((item) => item.id !== requestId));
  removeStoredChatThread(requestId);
  await persistTransactionRequestDeleteToBackend(requestId);
  return { ok: true, request: target };
};

export const negotiateTransactionRequest = (requestId: string, percent: number) => {
  const requests = getAllTransactionRequests();
  const current = requests.find((item) => item.id === requestId);
  if (!current) return { ok: false, error: "Request not found." };

  if (!["lease", "purchase", "bnpl"].includes(current.requestType)) {
    return { ok: false, error: "Discount requests are only available for lease and purchase requests." };
  }

  if (current.discountRequestStatus === "Pending") {
    return { ok: false, error: "A discount request is already pending owner review." };
  }

  const baseLabel = current.requestedAmount || current.baseAmount;
  const baseValue = parseCurrencyAmountWithSuffix(baseLabel).numeric;
  if (!baseValue) return { ok: false, error: "A negotiable amount is not available for this request." };

  const proposedAmount = computeDiscountedRequestAmount(baseLabel, percent);
  const nextRequests = requests.map((item) => item.id === requestId
    ? {
        ...item,
        negotiationPercent: percent,
        proposedAmount,
        discountRequestStatus: "Pending" as const,
        discountRequestedAt: new Date().toISOString(),
        discountReviewedAt: undefined,
        notes: `${item.notes} Customer proposed ${percent}% off during the request chat.`,
      }
    : item);

  saveTransactionRequests(nextRequests);
  const updated = nextRequests.find((item) => item.id === requestId)!;
  const thread = getChatThread(updated.id, []);
  saveChatThread(updated.id, [
    ...thread,
    {
      sender: "system",
      text: `${updated.buyer} requested ${percent}% off. Proposed amount: ${proposedAmount}. Waiting for owner approval.`,
    },
  ]);

  pushNotification({
    user: updated.owner,
    title: "Discount request pending",
    message: `${updated.buyer} requested ${percent}% off for ${updated.equipmentTitle}. Proposed amount: ${proposedAmount}.`,
    type: "info",
    link: getRequestDashboardPath(updated.requestType),
  });

  return { ok: true, request: updated };
};

export const reviewTransactionDiscountRequest = (requestId: string, action: "accept" | "reject") => {
  const requests = getAllTransactionRequests();
  const current = requests.find((item) => item.id === requestId);
  if (!current) return { ok: false, error: "Request not found." };
  if (current.discountRequestStatus !== "Pending" || !current.negotiationPercent || !current.proposedAmount) {
    return { ok: false, error: "There is no pending discount request to review." };
  }

  const reviewedAt = new Date().toISOString();
  const nextRequests = requests.map((item) => item.id === requestId
    ? {
        ...item,
        requestedAmount: action === "accept" ? item.proposedAmount || item.requestedAmount : item.requestedAmount,
        discountRequestStatus: action === "accept" ? "Accepted" as const : "Rejected" as const,
        discountReviewedAt: reviewedAt,
      }
    : item);

  saveTransactionRequests(nextRequests);

  const updated = nextRequests.find((item) => item.id === requestId)!;
  const thread = getChatThread(updated.id, []);
  const decisionText = action === "accept"
    ? `The equipment owner accepted the ${updated.negotiationPercent}% discount request. New request amount: ${updated.requestedAmount}.`
    : `The equipment owner cancelled the ${updated.negotiationPercent}% discount request. The request amount stays at ${updated.requestedAmount}.`;

  saveChatThread(updated.id, [
    ...thread,
    {
      sender: "system",
      text: decisionText,
    },
  ]);

  pushNotification({
    user: updated.buyer,
    title: action === "accept" ? "Discount request accepted" : "Discount request cancelled",
    message: action === "accept"
      ? `${updated.owner} accepted your ${updated.negotiationPercent}% discount request for ${updated.equipmentTitle}.`
      : `${updated.owner} cancelled your ${updated.negotiationPercent}% discount request for ${updated.equipmentTitle}.`,
    type: action === "accept" ? "success" : "warning",
    link: getRequestDashboardPath(updated.requestType),
  });

  return { ok: true, request: updated };
};

export const buildTransactionRequestDocumentHtml = (request: TransactionRequest) => {
  const stamp = request.status === "Accepted" ? "APPROVED" : request.status === "Rejected" ? "REJECTED" : "PENDING";
  const stampColor = request.status === "Accepted" ? "#166534" : request.status === "Rejected" ? "#b91c1c" : "#92400e";
  const requestLabel = request.requestType === "purchase" ? "Purchase Request" : request.requestType === "service" ? "Service Request" : "Lease Request";
  const equipmentRecord = featuredEquipment.find((item) => item.id === request.equipmentRefId || item.title === request.equipmentTitle);
  const equipmentImage = equipmentRecord?.image || getEquipmentImage(request.equipmentRefId, request.equipmentTitle) || "";
  const equipmentGallery = [equipmentImage, ...(equipmentRecord?.galleryMedia || [])]
    .map((item) => String(item || "").trim())
    .filter((item, index, list) => item && list.indexOf(item) === index)
    .slice(0, 3);
  const specificationRows = Array.isArray(equipmentRecord?.specificationRows)
    ? equipmentRecord.specificationRows
        .map((entry) => {
          const key = String((entry as { key?: unknown })?.key || "").trim();
          const value = String((entry as { value?: unknown })?.value || "").trim();
          return key && value ? { key, value } : null;
        })
        .filter((entry): entry is { key: string; value: string } => Boolean(entry))
    : [];

  const pricingModeLabel = request.pricingMode === "lease to own"
    ? "Lease to own"
    : request.pricingMode === "lease to use"
      ? "Lease to buy/use"
      : request.pricingMode === "daily lease"
        ? "Daily lease"
        : request.pricingMode === "purchase"
          ? "Outright purchase"
          : request.pricingMode === "bnpl"
            ? "Buy now, pay later"
            : "Service";
  const amountLabel = request.requestType === "purchase" || request.pricingMode === "purchase"
    ? `Total purchase amount: ${request.requestedAmount}`
    : request.pricingMode === "daily lease"
      ? `Daily lease amount: ${request.requestedAmount}`
      : `Agreed amount: ${request.requestedAmount}`;
  const specsHtml = specificationRows.length > 0
    ? `<table class="spec-table"><thead><tr><th>Specification</th><th>Value</th></tr></thead><tbody>${specificationRows.map((row) => `<tr><td>${row.key}</td><td>${row.value}</td></tr>`).join("")}</tbody></table>`
    : `<p class="value">No specification sheet was attached to this listing.</p>`;

  return `<!DOCTYPE html>
  <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>${requestLabel} Document</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; color: #111827; }
        .brand { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; border-bottom:2px solid #0d9303; padding-bottom:16px; }
        .logo { font-size: 24px; font-weight: 800; color: #0d9303; }
        .card { border:1px solid #e5e7eb; border-radius:12px; padding:16px; margin-bottom:16px; }
        .stamp { display:inline-block; padding:10px 16px; border:2px solid ${stampColor}; color:${stampColor}; font-weight:800; border-radius:999px; letter-spacing:0.12em; }
        .hero-image { width:100%; max-height:220px; object-fit:cover; border-radius:12px; margin-bottom:16px; }
        .gallery { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:10px; margin-bottom:16px; }
        .gallery img { width:100%; aspect-ratio:1 / 1; object-fit:cover; border-radius:10px; border:1px solid #e5e7eb; }
        .grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .label { font-size:12px; color:#6b7280; text-transform:uppercase; }
        .value { font-size:14px; font-weight:600; margin-top:4px; }
        .spec-table { width:100%; border-collapse:collapse; margin-top:8px; }
        .spec-table th, .spec-table td { border:1px solid #e5e7eb; padding:8px; font-size:13px; text-align:left; }
        .spec-table th { background:#f8fafc; color:#475569; }
      </style>
    </head>
    <body>
      <div class="brand">
        <div>
          <div class="logo">Equipment Marketplace</div>
          <div>${requestLabel} document</div>
        </div>
        <div class="stamp">${stamp}</div>
      </div>
      ${equipmentImage ? `<img src="${equipmentImage}" alt="${request.equipmentTitle}" class="hero-image" />` : ""}
      ${equipmentGallery.length > 1 ? `<div class="gallery">${equipmentGallery.map((imageUrl, imageIndex) => `<img src="${imageUrl}" alt="${request.equipmentTitle} image ${imageIndex + 1}" />`).join("")}</div>` : ""}
      <div class="card">
        <div class="grid">
          <div><div class="label">Equipment</div><div class="value">${request.equipmentTitle}</div></div>
          <div><div class="label">Request type</div><div class="value">${request.requestType}</div></div>
          <div><div class="label">Lease mode</div><div class="value">${pricingModeLabel}</div></div>
          <div><div class="label">Amount</div><div class="value">${amountLabel}</div></div>
          <div><div class="label">Equipment owner</div><div class="value">${request.owner}</div></div>
          <div><div class="label">Customer</div><div class="value">${request.buyer}</div></div>
          <div><div class="label">Buyer email</div><div class="value">${request.buyerEmail || "Not supplied"}</div></div>
          <div><div class="label">Buyer phone</div><div class="value">${request.buyerPhone || "Not supplied"}</div></div>
        </div>
      </div>
      <div class="card">
        <div class="label">Equipment specifications</div>
        ${specsHtml}
      </div>
      <div class="card">
        <div class="label">Notes and request summary</div>
        <div class="value">${request.notes}</div>
      </div>
      <div class="card">
        <div class="label">Request date</div>
        <div class="value">${new Date(request.createdAt).toLocaleString()}</div>
      </div>
    </body>
  </html>`;
};

type LeaseAgreementViewerParty = "owner" | "customer";

const getAgreementViewerParty = (agreement: RentalAgreement, viewerUser = getAuthState().actualUser || getAuthState().user): LeaseAgreementViewerParty => {
  const normalizedViewer = String(viewerUser || "").trim().toLowerCase();
  const ownerProfile = getUserProfile(agreement.owner);
  const customerProfile = getUserProfile(agreement.customer);
  const ownerKeys = [agreement.owner, ownerProfile.user, ownerProfile.email, ownerProfile.fullName]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const customerKeys = [agreement.customer, customerProfile.user, customerProfile.email, customerProfile.fullName]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  if (ownerKeys.includes(normalizedViewer)) return "owner";
  if (customerKeys.includes(normalizedViewer)) return "customer";

  const effectiveRole = getAuthState().actualRole || getAuthState().role;
  return ["lessor", "oem", "provider"].includes(effectiveRole) ? "owner" : "customer";
};

const buildAgreementParticipant = (userKey: string, phone: string) => {
  const profile = getUserProfile(userKey);
  const accreditation = getUserAccreditationProfile(userKey);

  return {
    userKey,
    fullName: String(profile.fullName || "").trim(),
    businessName: String(profile.business || "").trim(),
    email: String(profile.email || "").trim(),
    phone: String(profile.phone || phone || "").trim(),
    elraId: String(accreditation.certificateId || "").trim(),
  };
};

const buildAgreementParticipantDetailsHtml = (label: string, participant: ReturnType<typeof buildAgreementParticipant>) => {
  const lines = [
    participant.fullName ? `Full name: ${participant.fullName}` : `Account: ${participant.userKey}`,
    participant.businessName ? `Company: ${participant.businessName}` : "",
    participant.elraId ? `ELRA ID: ${participant.elraId}` : "",
    participant.email ? `Email: ${participant.email}` : "",
    participant.phone ? `Phone: ${participant.phone}` : "",
  ].filter(Boolean);

  return `<div><div class="label">${label}</div><div class="value">${lines.join("<br/>")}</div></div>`;
};

const buildAgreementCounterpartySummaryHtml = (label: string, participant: ReturnType<typeof buildAgreementParticipant>) => {
  const lines = [
    participant.fullName || participant.userKey,
    participant.businessName ? `Business: ${participant.businessName}` : "",
    participant.elraId ? `ELRA ID: ${participant.elraId}` : "",
  ].filter(Boolean);

  return `<div class="card"><div class="label">${label}</div><div class="value">${lines.join("<br/>")}</div></div>`;
};

export const buildLeaseAgreementDocumentHtml = (agreement: RentalAgreement, viewerUser = getAuthState().actualUser || getAuthState().user) => {
  const request = getAllTransactionRequests().find((item) => item.id === agreement.requestId);
  const equipmentImage =
    featuredEquipment.find((item) => item.id === agreement.equipmentRefId || item.title === agreement.equipmentTitle)?.image ||
    getEquipmentImage(agreement.equipmentRefId, agreement.equipmentTitle) ||
    "";
  const websiteSettings = getWebsiteSettings();
  const brandLogo = resolveMediaUrl(websiteSettings.websiteLogo || websiteSettings.footerLogo || "");
  const brandName = websiteSettings.websiteName || "Equipment Marketplace";
  const viewerParty = getAgreementViewerParty(agreement, viewerUser);
  const ownerProfile = buildAgreementParticipant(agreement.owner, agreement.ownerPhone);
  const customerProfile = buildAgreementParticipant(agreement.customer, agreement.customerPhone);
  const verification = getEquipmentVerificationStatus(agreement.equipmentRefId);
  const hasEscrowProtection = getEscrowRecords().some((item) => item.requestId === agreement.requestId);
  const agreementLabel = agreement.agreementType === "purchase" ? "Purchase Agreement" : agreement.agreementType === "service" ? "Service Agreement" : "Lease Agreement";
  const ownerLabel = agreement.agreementType === "purchase" ? "Seller details" : agreement.agreementType === "service" ? "Provider details" : "Lessor details";
  const customerLabel = agreement.agreementType === "purchase" ? "Buyer details" : agreement.agreementType === "service" ? "Requester details" : "Lessee details";
  const ownerSignatureLabel = agreement.agreementType === "purchase" ? "Seller" : agreement.agreementType === "service" ? "Provider" : "Equipment owner";
  const customerSignatureLabel = agreement.agreementType === "purchase" ? "Buyer" : agreement.agreementType === "service" ? "Requester" : "Lessee";
  const paymentMode = hasEscrowProtection ? "On website via ELRA escrow" : "Off website / direct settlement";
  const leaseDuration = `${new Date(agreement.startDate).toLocaleDateString()} - ${new Date(agreement.endDate).toLocaleDateString()}`;
  const viewerSignatureLabel = viewerParty === "owner" ? ownerSignatureLabel : customerSignatureLabel;
  const counterpartyLabel = viewerParty === "owner" ? customerSignatureLabel : ownerSignatureLabel;
  const counterparty = viewerParty === "owner" ? customerProfile : ownerProfile;
  const viewerSummary = viewerParty === "owner" ? ownerProfile : customerProfile;
  const viewerIdentityLines = [
    viewerSummary.fullName || viewerSummary.userKey,
    viewerSummary.businessName ? `Business: ${viewerSummary.businessName}` : "",
    viewerSummary.elraId ? `ELRA ID: ${viewerSummary.elraId}` : "",
  ].filter(Boolean);

  return `<!DOCTYPE html>
  <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>${agreementLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; color: #111827; }
        .brand { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; border-bottom:2px solid #16a34a; padding-bottom:16px; }
        .brand-left { display:flex; align-items:center; gap:16px; }
        .logo { font-size: 24px; font-weight: 800; color: #16a34a; }
        .brand-logo { width:64px; height:64px; object-fit:contain; border-radius:12px; }
        .card { border:1px solid #e5e7eb; border-radius:12px; padding:16px; margin-bottom:16px; }
        .grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .label { font-size:12px; color:#6b7280; text-transform:uppercase; }
        .value { font-size:14px; font-weight:600; margin-top:4px; }
        .stamp { display:inline-block; padding:10px 16px; border:2px solid #166534; color:#166534; font-weight:800; border-radius:999px; letter-spacing:0.12em; }
        .signatures { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-top:24px; }
        .line { border-top:1px solid #111827; padding-top:8px; margin-top:40px; }
        .notice { background:#f0fdf4; border:1px solid #86efac; border-radius:12px; padding:14px; color:#14532d; }
        .hero-image { width:100%; max-height:220px; object-fit:cover; border-radius:12px; margin-bottom:16px; }
        .subtle { color:#4b5563; font-size:13px; margin-top:6px; line-height:1.6; }
      </style>
    </head>
    <body>
      <div class="brand">
        <div class="brand-left">
          ${brandLogo ? `<img src="${brandLogo}" alt="${brandName}" class="brand-logo" />` : ""}
          <div>
            <div class="logo">${brandName}</div>
            <div>${agreementLabel}</div>
          </div>
        </div>
        <div class="stamp">${agreement.agreementType === "purchase" ? "ELRA PURCHASE" : agreement.agreementType === "service" ? "ELRA SERVICE" : "ELRA LEASE"}</div>
      </div>
      ${equipmentImage ? `<img src="${equipmentImage}" alt="${agreement.equipmentTitle}" class="hero-image" />` : ""}
      <div class="card">
        <div class="grid">
          <div><div class="label">Equipment details</div><div class="value">${agreement.equipmentTitle}</div></div>
          <div><div class="label">Agreement date</div><div class="value">${new Date().toLocaleDateString()}</div></div>
          <div><div class="label">Amount</div><div class="value">${agreement.negotiatedAmount}</div></div>
          <div><div class="label">Lease duration</div><div class="value">${leaseDuration}</div></div>
          <div><div class="label">Pricing mode</div><div class="value">${request?.pricingMode || agreement.agreementType}</div></div>
          <div><div class="label">Payment mode</div><div class="value">${paymentMode}</div></div>
        </div>
      </div>
      <div class="card">
        <div class="grid">
          ${buildAgreementParticipantDetailsHtml(ownerLabel, ownerProfile)}
          ${buildAgreementParticipantDetailsHtml(customerLabel, customerProfile)}
          <div><div class="label">Equipment verified</div><div class="value">${verification.verificationStatus}</div></div>
          <div><div class="label">Users verified</div><div class="value">Lessor: ${getUserProfile(agreement.owner).approvalStatus === "approved" ? "Yes" : "Pending"} • Lessee: ${getUserProfile(agreement.customer).approvalStatus === "approved" ? "Yes" : "Pending"}</div></div>
        </div>
      </div>
      <div class="card">
        <div class="label">Lease terms and delivery</div>
        <div class="value">${agreement.leaseTerms}</div>
        <div class="value" style="margin-top:12px;">Delivery location: ${agreement.deliveryLocation}</div>
      </div>
      <div class="notice">
        <strong>Important:</strong> Consult a lawyer or adviser before signing this agreement. Register the final ELRA agreement certificate after both parties sign.
      </div>
      ${buildAgreementCounterpartySummaryHtml(`${counterpartyLabel} identity shown on this copy`, counterparty)}
      <div class="signatures">
        <div>
          <div class="line">${viewerSignatureLabel} signature</div>
          <div class="subtle">${viewerIdentityLines.join("<br/>")}</div>
        </div>
        <div>
          <div class="label">Counterparty details</div>
          <div class="value">${counterparty.fullName || counterparty.userKey}</div>
          <div class="subtle">${[
            counterparty.businessName ? `Business: ${counterparty.businessName}` : "",
            counterparty.elraId ? `ELRA ID: ${counterparty.elraId}` : "",
          ].filter(Boolean).join("<br/>") || "No business name or ELRA ID supplied."}</div>
        </div>
      </div>
    </body>
  </html>`;
};

export const downloadLeaseAgreementPdf = (agreementId: string, viewerUser = getAuthState().actualUser || getAuthState().user) => {
  if (typeof window === "undefined") return { ok: false, error: "Only available in the browser." };

  const agreement = getRentalAgreements("", "admin").find((item) => item.id === agreementId);
  if (!agreement) return { ok: false, error: "Lease agreement not found." };
  if (viewerUser && ![agreement.owner, agreement.customer].includes(viewerUser) && (getAuthState().actualRole || getAuthState().role) !== "admin") {
    return { ok: false, error: "Only the lease parties can download this agreement." };
  }

  const popup = window.open("", "_blank", "width=960,height=760,resizable=yes,scrollbars=yes");
  if (!popup) return { ok: false, error: "Allow popups to download the lease agreement." };

  popup.document.write(buildLeaseAgreementDocumentHtml(agreement, viewerUser));
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 350);
  return { ok: true };
};

export const buildPaymentInvoiceHtml = (payment: PaymentRecord) => `<!DOCTYPE html>
  <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>Equipment Marketplace Payment Invoice</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; color: #111827; }
        .brand { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; border-bottom:2px solid #0d9303; padding-bottom:16px; }
        .logo { font-size: 24px; font-weight: 800; color: #0d9303; }
        .card { border:1px solid #e5e7eb; border-radius:12px; padding:16px; margin-bottom:16px; }
        .badge { display:inline-block; padding:8px 14px; border-radius:999px; font-weight:700; background:#fff7ed; color:#c2410c; }
      </style>
    </head>
    <body>
      <div class="brand">
        <div>
          <div class="logo">Equipment Marketplace</div>
          <div>Transaction invoice</div>
        </div>
        <div class="badge">${payment.status}</div>
      </div>
      <div class="card"><strong>Reference:</strong> ${payment.reference}</div>
      <div class="card"><strong>User:</strong> ${payment.user}</div>
      <div class="card"><strong>Purpose:</strong> ${payment.purpose}</div>
      <div class="card"><strong>Amount:</strong> ${payment.amountLabel}</div>
      <div class="card"><strong>Provider:</strong> ${payment.provider}</div>
      <div class="card"><strong>Note:</strong> ${payment.note}</div>
      <div class="card"><strong>Date:</strong> ${new Date(payment.createdAt).toLocaleString()}</div>
    </body>
  </html>`;

export const downloadPaymentInvoice = (reference: string) => {
  if (typeof window === "undefined") return { ok: false, error: "Only available in the browser." };

  const payment = getPaymentRecords().find((item) => item.reference === reference);
  if (!payment) return { ok: false, error: "Payment record not found." };

  const popup = window.open("", "_blank", "width=960,height=760,resizable=yes,scrollbars=yes");
  if (!popup) return { ok: false, error: "Allow popups to open the invoice." };

  popup.document.write(buildPaymentInvoiceHtml(payment));
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 350);
  return { ok: true };
};

export const downloadTransactionRequestPdf = (requestId: string) => {
  if (typeof window === "undefined") return { ok: false, error: "Only available in the browser." };

  const request = getAllTransactionRequests().find((item) => item.id === requestId);
  if (!request) return { ok: false, error: "Request not found." };
  const popup = window.open("", "_blank", "width=960,height=760,resizable=yes,scrollbars=yes");
  if (!popup) return { ok: false, error: "Allow popups to download the document." };

  popup.document.write(buildTransactionRequestDocumentHtml(request));
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 350);
  return { ok: true };
};

export const getTransactionRequestsForUser = (user = getAuthState().user, role = getAuthState().role) => {
  const requests = getAllTransactionRequests();
  const viewerProfile = getUserProfile(user);
  const normalizeIdentity = (value?: string) => String(value || "").trim().toLowerCase();
  const viewerKeys = new Set(
    [
      user,
      viewerProfile.user,
      viewerProfile.email,
      viewerProfile.fullName,
      viewerProfile.business,
    ]
      .map(normalizeIdentity)
      .filter(Boolean),
  );

  const requestMatchesViewer = (item: TransactionRequest) => {
    const ownerProfile = getUserProfile(item.owner);
    const buyerProfile = getUserProfile(item.buyer);
    const participantKeys = [
      item.owner,
      ownerProfile.user,
      ownerProfile.email,
      ownerProfile.fullName,
      ownerProfile.business,
      item.buyer,
      buyerProfile.user,
      buyerProfile.email,
      buyerProfile.fullName,
      buyerProfile.business,
    ]
      .map(normalizeIdentity)
      .filter(Boolean);

    return participantKeys.some((key) => viewerKeys.has(key));
  };

  if (role === "admin") {
    return requests;
  }

  if (role === "lessor") {
    return requests.filter((item) => requestMatchesViewer(item));
  }

  if (role === "provider") {
    return requests.filter((item) => (item.requestType === "service" && requestMatchesViewer(item)) || requestMatchesViewer(item));
  }

  return requests.filter((item) => requestMatchesViewer(item));
};

export const findActiveTransactionRequest = (params: {
  equipmentRefId: string;
  owner: string;
  buyer: string;
  requestType?: TransactionRequestType;
}) => {
  const normalizeValue = (value?: string) => String(value || "").trim().toLowerCase();
  const equipmentKey = normalizeValue(params.equipmentRefId);
  const ownerKey = normalizeValue(params.owner);
  const buyerKey = normalizeValue(params.buyer);

  return getAllTransactionRequests().find((item) => {
    if (item.status !== "Pending") return false;
    if (params.requestType && item.requestType !== params.requestType) return false;
    return normalizeValue(item.equipmentRefId) === equipmentKey
      && normalizeValue(item.owner) === ownerKey
      && normalizeValue(item.buyer) === buyerKey;
  }) || null;
};

export const findLatestPendingRequestForEquipment = (
  equipmentRefId: string,
  viewerUser = getAuthState().user,
) => {
  const normalizeValue = (value?: string) => String(value || "").trim().toLowerCase();
  const equipmentKey = normalizeValue(equipmentRefId);
  const viewerKey = normalizeValue(viewerUser);

  return getAllTransactionRequests()
    .filter((item) => (
      item.status === "Pending"
      && normalizeValue(item.equipmentRefId) === equipmentKey
      && [normalizeValue(item.owner), normalizeValue(item.buyer)].includes(viewerKey)
    ))
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0] || null;
};

const defaultServiceReviews: ServiceReview[] = [];

const defaultEquipmentReviews: EquipmentReview[] = [];

const getAllStoredServiceReviews = () => {
  if (typeof window === "undefined") {
    return defaultServiceReviews;
  }
  const stored = safeParse<ServiceReview[]>(browserStorage.getItem(SERVICE_REVIEW_KEY), [])
    .filter((item) => !isSeededDemoId(item.id));
  return mergeDemoRecords(defaultServiceReviews, stored);
};

const saveServiceReviewStore = (reviews: ServiceReview[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(SERVICE_REVIEW_KEY, JSON.stringify(reviews));
};

const getAllStoredEquipmentReviews = () => {
  if (typeof window === "undefined") {
    return defaultEquipmentReviews;
  }
  const stored = safeParse<EquipmentReview[]>(browserStorage.getItem(EQUIPMENT_REVIEW_KEY), [])
    .filter((item) => !isSeededDemoId(item.id));
  return mergeDemoRecords(defaultEquipmentReviews, stored);
};

const saveEquipmentReviewStore = (reviews: EquipmentReview[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(EQUIPMENT_REVIEW_KEY, JSON.stringify(reviews));
};

const defaultMarketplaceRequests: MarketplaceRequest[] = [];

export const getServiceReviews = (providerId: string) => {
  return getAllStoredServiceReviews().filter((item) => item.providerId === providerId);
};

export const syncServiceReviewsFromServer = async (providerId: string) => {
  if (typeof window === "undefined" || !providerId) return [] as ServiceReview[];

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/reviews/service/${encodeURIComponent(providerId)}`);
    if (!response.ok) {
      throw new Error("Unable to fetch service reviews.");
    }

    const payload = await response.json();
    const remoteReviews = Array.isArray(payload?.reviews) ? payload.reviews as ServiceReview[] : [];
    const existing = getAllStoredServiceReviews().filter((item) => item.providerId !== providerId);
    saveServiceReviewStore([...remoteReviews, ...existing]);
    return remoteReviews;
  } catch {
    return getServiceReviews(providerId);
  }
};

export const canUserReviewProvider = (providerId: string, user = getAuthState().user, role = getAuthState().role) => {
  if (!user) return false;
  if (["provider", "agent", "admin", "oem"].includes(role)) return false;

  const hasRequested = getAllTransactionRequests().some(
    (item) => item.requestType === "service" && item.equipmentRefId === `service-${providerId}` && item.buyer === user,
  );

  const hasAlreadyReviewed = getServiceReviews(providerId).some((item) => item.user === user);
  return hasRequested && !hasAlreadyReviewed;
};

export const submitServiceReview = (providerId: string, user: string, rating: number, comment: string) => {
  if (typeof window === "undefined" || !providerId || !user) return { ok: false, error: "Reviews are only available in the browser." };

  if (!canUserReviewProvider(providerId, user, getAuthState().role)) {
    return { ok: false, error: "Only normal users who have requested this service can leave a review." };
  }

  const reviews = getAllStoredServiceReviews();
  reviews.unshift({
    id: `SRV-${Date.now()}`,
    providerId,
    user,
    rating: Math.min(5, Math.max(1, rating)),
    comment: comment.trim(),
    createdAt: new Date().toISOString(),
  });
  saveServiceReviewStore(reviews);
  void syncUserDataForUser(user);

  return { ok: true };
};

export const getEquipmentReviews = (equipmentId: string) => {
  return getAllStoredEquipmentReviews().filter((item) => item.equipmentId === equipmentId);
};

export const syncEquipmentReviewsFromServer = async (equipmentId: string) => {
  if (typeof window === "undefined" || !equipmentId) return [] as EquipmentReview[];

  try {
    const response = await fetch(`${PLATFORM_API_BASE}/reviews/equipment/${encodeURIComponent(equipmentId)}`);
    if (!response.ok) {
      throw new Error("Unable to fetch equipment reviews.");
    }

    const payload = await response.json();
    const remoteReviews = Array.isArray(payload?.reviews) ? payload.reviews as EquipmentReview[] : [];
    const existing = getAllStoredEquipmentReviews().filter((item) => item.equipmentId !== equipmentId);
    saveEquipmentReviewStore([...remoteReviews, ...existing]);
    return remoteReviews;
  } catch {
    return getEquipmentReviews(equipmentId);
  }
};

export const canUserReviewEquipment = (equipmentId: string, user = getAuthState().user, role = getAuthState().role) => {
  if (!user) return false;
  if (["provider", "agent", "admin", "oem"].includes(role)) return false;

  const hasRequested = getAllTransactionRequests().some(
    (item) => ["lease", "purchase", "bnpl", "negotiation"].includes(item.requestType) && item.equipmentRefId === equipmentId && item.buyer === user,
  );

  const hasAlreadyReviewed = getEquipmentReviews(equipmentId).some((item) => item.user === user);
  return hasRequested && !hasAlreadyReviewed;
};

export const submitEquipmentReview = (equipmentId: string, user: string, rating: number, comment: string) => {
  if (typeof window === "undefined" || !equipmentId || !user) return { ok: false, error: "Reviews are only available in the browser." };

  if (!canUserReviewEquipment(equipmentId, user, getAuthState().role)) {
    return { ok: false, error: "Only signed-in users with an equipment request can leave a review." };
  }

  const reviews = getAllStoredEquipmentReviews();
  reviews.unshift({
    id: `EQR-${Date.now()}`,
    equipmentId,
    user,
    rating: Math.min(5, Math.max(1, rating)),
    comment: comment.trim(),
    createdAt: new Date().toISOString(),
  });
  saveEquipmentReviewStore(reviews);
  void syncUserDataForUser(user);

  return { ok: true };
};

const normalizeMarketplaceRequest = (request: MarketplaceRequest): MarketplaceRequest => ({
  ...request,
  lifecycleStatus: request.lifecycleStatus ?? "live",
  updatedAt: request.updatedAt ?? request.createdAt,
  deleted: request.deleted ?? false,
});

const saveStoredMarketplaceRequestConversations = (conversations: MarketplaceRequestConversation[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(MARKETPLACE_REQUEST_THREADS_KEY, JSON.stringify(conversations));
};

const saveMarketplaceRequests = (requests: MarketplaceRequest[]) => {
  if (typeof window === "undefined") return;
  browserStorage.setItem(MARKETPLACE_REQUESTS_KEY, JSON.stringify(requests));
};

const persistMarketplaceRequestConversations = async (conversations: MarketplaceRequestConversation[]) => {
  saveStoredMarketplaceRequestConversations(conversations);

  const auth = getAuthState();
  if (!auth.token) return true;

  return Boolean(await syncWorkflowState({ marketplaceRequestConversations: conversations }));
};

const persistMarketplaceRequests = async (requests: MarketplaceRequest[]) => {
  saveMarketplaceRequests(requests);

  const auth = getAuthState();
  if (!auth.token) return true;

  return Boolean(await syncWorkflowState({ marketplaceRequests: requests }));
};

export const getMarketplaceRequests = (
  status: "pending" | "approved" | "rejected" | "all" = "approved",
  options?: { includeDeleted?: boolean },
) => {
  const requests = getStoredMarketplaceRequests().map(normalizeMarketplaceRequest);

  const visibleRequests = options?.includeDeleted ? requests : requests.filter((item) => !item.deleted);
  return status === "all" ? visibleRequests : visibleRequests.filter((item) => item.status === status);
};

export const getMarketplaceRequestById = (id: string) => (
  getMarketplaceRequests("all", { includeDeleted: true }).find((item) => item.id === id) || null
);

export const getUserPostedMarketplaceRequests = (user = getAuthState().user) => {
  if (!user) return [] as MarketplaceRequest[];
  return getMarketplaceRequests("all").filter((item) => item.requester === user);
};

export const getMarketplaceRequestConversations = (requestId: string) => {
  if (!requestId) return [] as MarketplaceRequestConversation[];
  return getStoredMarketplaceRequestConversations()
    .filter((item) => item.requestId === requestId)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};

export const getUserMarketplaceRequestConversations = (user = getAuthState().user) => {
  if (!user) return [] as MarketplaceRequestConversation[];
  return getStoredMarketplaceRequestConversations()
    .filter((item) => item.author === user || item.responder === user)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};

export const submitMarketplaceRequest = async (payload: {
  requestType: "equipment" | "service";
  title: string;
  category: string;
  location: string;
  budget: string;
  details: string;
  imageUrl?: string;
}) => {
  if (typeof window === "undefined") {
    return { ok: false, error: "Requests can only be posted in the browser." };
  }

  const auth = getAuthState();
  if (!auth.loggedIn || !auth.user) {
    return { ok: false, error: "Please sign in before posting a request." };
  }

  const createdAt = new Date().toISOString();
  const request: MarketplaceRequest = {
    id: `MPR-${Date.now()}`,
    requester: auth.user,
    requestType: payload.requestType,
    title: payload.title.trim() || `${payload.requestType === "service" ? "Service" : "Equipment"} request`,
    category: payload.category.trim() || (payload.requestType === "service" ? "Service request" : "Equipment request"),
    location: payload.location.trim() || "Nigeria",
    budget: payload.budget.trim() || "Open to quote",
    details: payload.details.trim() || "No additional request details supplied.",
    imageUrl: payload.imageUrl || featuredEquipment[0]?.image || "",
    status: "pending",
    lifecycleStatus: "live",
    updatedAt: createdAt,
    deleted: false,
    createdAt,
  };

  const previousRequests = getMarketplaceRequests("all", { includeDeleted: true });
  const requests = [request, ...previousRequests];
  const persisted = await persistMarketplaceRequests(requests);
  if (!persisted) {
    saveMarketplaceRequests(previousRequests);
    return { ok: false, error: "Unable to save this request to the live database right now. Please try again." };
  }

  pushNotification({
    user: "admin",
    title: "New marketplace request",
    message: `${request.title} was submitted and is awaiting approval.`,
    type: "info",
    link: "/admin/requests",
  });

  return { ok: true, request };
};

export const updateMarketplaceRequestStatus = async (id: string, status: "approved" | "rejected" | "pending") => {
  if (typeof window === "undefined" || !id) return null;

  const previousRequests = getMarketplaceRequests("all", { includeDeleted: true });
  const requests = [...previousRequests];
  const index = requests.findIndex((item) => item.id === id);
  if (index === -1) return null;

  requests[index] = {
    ...normalizeMarketplaceRequest(requests[index]),
    status,
    updatedAt: new Date().toISOString(),
  };
  const persisted = await persistMarketplaceRequests(requests);
  if (!persisted) {
    saveMarketplaceRequests(previousRequests);
    return null;
  }

  pushNotification({
    user: requests[index].requester,
    title: status === "approved" ? "Request approved" : "Request update",
    message: status === "approved"
      ? `${requests[index].title} is now live in Explore Requests.`
      : `${requests[index].title} has been ${status}.`,
    type: status === "approved" ? "success" : "warning",
    link: "/dashboard/requests",
  });

  return requests[index];
};

export const updateMarketplaceRequestLifecycle = async (
  id: string,
  lifecycleStatus: "live" | "paused" | "cancelled" | "fulfilled",
  fulfilledBy?: string,
) => {
  if (typeof window === "undefined" || !id) return null;

  const previousRequests = getMarketplaceRequests("all", { includeDeleted: true });
  const requests = [...previousRequests];
  const index = requests.findIndex((item) => item.id === id);
  if (index === -1) return null;

  const now = new Date().toISOString();
  requests[index] = {
    ...normalizeMarketplaceRequest(requests[index]),
    lifecycleStatus,
    updatedAt: now,
    fulfilledBy: lifecycleStatus === "fulfilled" ? (fulfilledBy || requests[index].fulfilledBy || getAuthState().user) : undefined,
    fulfilledAt: lifecycleStatus === "fulfilled" ? now : undefined,
  };
  const persisted = await persistMarketplaceRequests(requests);
  if (!persisted) {
    saveMarketplaceRequests(previousRequests);
    return null;
  }

  const lifecycleTitle = lifecycleStatus === "fulfilled"
    ? "Request fulfilled"
    : lifecycleStatus === "paused"
      ? "Request paused"
      : lifecycleStatus === "cancelled"
        ? "Request cancelled"
        : "Request reopened";

  const lifecycleMessage = lifecycleStatus === "fulfilled"
    ? `${requests[index].title} was marked as fulfilled${requests[index].fulfilledBy ? ` by ${requests[index].fulfilledBy}` : ""}.`
    : lifecycleStatus === "paused"
      ? `${requests[index].title} has been paused and is temporarily hidden from new responses.`
      : lifecycleStatus === "cancelled"
        ? `${requests[index].title} has been cancelled and is no longer open for responses.`
        : `${requests[index].title} is now live again.`;

  pushNotification({
    user: requests[index].requester,
    title: lifecycleTitle,
    message: lifecycleMessage,
    type: lifecycleStatus === "fulfilled" ? "success" : lifecycleStatus === "cancelled" ? "warning" : "info",
    link: "/dashboard/requests",
  });

  return requests[index];
};

export const deleteMarketplaceRequest = async (id: string, user = getAuthState().user) => {
  if (typeof window === "undefined" || !id) return null;

  const auth = getAuthState();
  const previousRequests = getMarketplaceRequests("all", { includeDeleted: true });
  const requests = [...previousRequests];
  const index = requests.findIndex((item) => item.id === id);
  if (index === -1) return null;
  if (auth.role !== "admin" && user && requests[index].requester !== user) return null;

  const now = new Date().toISOString();
  requests[index] = {
    ...normalizeMarketplaceRequest(requests[index]),
    deleted: true,
    deletedAt: now,
    updatedAt: now,
  };
  const persisted = await persistMarketplaceRequests(requests);
  if (!persisted) {
    saveMarketplaceRequests(previousRequests);
    return null;
  }
  return requests[index];
};

export const respondToMarketplaceRequest = async (
  requestId: string,
  payload: string | Partial<MarketplaceRequestResponseData>,
) => {
  if (typeof window === "undefined") return { ok: false, error: "Request responses are only available in the browser." };

  const auth = getAuthState();
  if (!auth.loggedIn || !auth.user) {
    return { ok: false, error: "Please sign in before responding to a request." };
  }

  if (!["lessor", "provider", "oem", "admin"].includes(auth.role)) {
    return { ok: false, error: "Only equipment owners, OEMs, and service providers can respond to marketplace requests." };
  }

  const request = getMarketplaceRequestById(requestId);
  if (!request || request.deleted) {
    return { ok: false, error: "This request is no longer available for responses." };
  }

  if (request.status === "pending") {
    return { ok: false, error: "This request is waiting for admin approval before responses can be sent." };
  }

  if (request.status !== "approved") {
    return { ok: false, error: "This request is no longer available for responses." };
  }

  if (request.requester === auth.user) {
    return { ok: false, error: "You cannot respond to your own request." };
  }

  if (request.lifecycleStatus === "paused") {
    return { ok: false, error: "This request is currently paused by admin and cannot receive responses." };
  }

  if (request.lifecycleStatus === "cancelled") {
    return { ok: false, error: "This request has been cancelled and is no longer open for responses." };
  }

  if (request.lifecycleStatus === "fulfilled") {
    return { ok: false, error: "This request has already been fulfilled." };
  }

  const responseData: MarketplaceRequestResponseData = typeof payload === "string"
    ? {
        availability: "Available on request",
        offerAmount: "Quote on request",
        timeline: "To be agreed",
        deliveryPlan: "To be confirmed",
        note: payload.trim(),
      }
    : {
        availability: payload.availability?.trim() || "Available on request",
        offerAmount: payload.offerAmount?.trim() || "Quote on request",
        timeline: payload.timeline?.trim() || "To be agreed",
        deliveryPlan: payload.deliveryPlan?.trim() || "To be confirmed",
        note: payload.note?.trim() || "",
      };

  if (!responseData.note && !responseData.offerAmount && !responseData.availability) {
    return { ok: false, error: "Enter the response details before sending." };
  }

  const conversations = getStoredMarketplaceRequestConversations();
  const now = new Date().toISOString();
  const summaryText = [
    `Availability: ${responseData.availability}`,
    `Offer: ${responseData.offerAmount}`,
    `Timeline: ${responseData.timeline}`,
    `Plan: ${responseData.deliveryPlan}`,
    responseData.note,
  ].filter(Boolean).join(" • ");

  const message: MarketplaceRequestConversationMessage = {
    id: `REQMSG-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    sender: "responder",
    senderName: auth.user,
    text: summaryText,
    createdAt: now,
  };

  const existingIndex = conversations.findIndex((item) => item.requestId === requestId && item.responder === auth.user);

  if (existingIndex >= 0) {
    conversations[existingIndex] = {
      ...conversations[existingIndex],
      updatedAt: now,
      responseData,
      messages: [...conversations[existingIndex].messages, message],
    };
  } else {
    conversations.unshift({
      id: `REQCHAT-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      requestId,
      requestTitle: request.title,
      author: request.requester,
      responder: auth.user,
      responderRole: auth.role,
      createdAt: now,
      updatedAt: now,
      responseData,
      messages: [message],
    });
  }

  const persisted = await persistMarketplaceRequestConversations(conversations);
  if (!persisted) {
    return { ok: false, error: "Unable to save this request response to the live database right now. Please try again." };
  }

  pushNotification({
    user: request.requester,
    title: "New response to your request",
    message: `${auth.user} responded to ${request.title}.`,
    type: "info",
    link: "/dashboard/requests",
  });

  return { ok: true, conversation: conversations.find((item) => item.requestId === requestId && item.responder === auth.user) };
};

export const sendMarketplaceRequestConversationMessage = async (conversationId: string, text: string) => {
  if (typeof window === "undefined") return { ok: false, error: "Conversation replies are only available in the browser." };

  const auth = getAuthState();
  if (!auth.loggedIn || !auth.user) {
    return { ok: false, error: "Please sign in before sending a message." };
  }

  const note = text.trim();
  if (!note) return { ok: false, error: "Enter a message first." };

  const conversations = getStoredMarketplaceRequestConversations();
  const index = conversations.findIndex((item) => item.id === conversationId);
  if (index === -1) return { ok: false, error: "Conversation not found." };

  const current = conversations[index];
  if (auth.role !== "admin" && auth.user !== current.author && auth.user !== current.responder) {
    return { ok: false, error: "You do not have access to this conversation." };
  }

  const now = new Date().toISOString();
  const message: MarketplaceRequestConversationMessage = {
    id: `REQMSG-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    sender: auth.role === "admin" ? "admin" : auth.user === current.author ? "author" : "responder",
    senderName: auth.user,
    text: note,
    createdAt: now,
  };

  conversations[index] = {
    ...current,
    updatedAt: now,
    messages: [...current.messages, message],
  };
  const persisted = await persistMarketplaceRequestConversations(conversations);
  if (!persisted) {
    return { ok: false, error: "Unable to save this message to the live database right now. Please try again." };
  }

  const recipient = message.sender === "author" ? current.responder : current.author;
  pushNotification({
    user: recipient,
    title: "New request chat message",
    message: `${current.requestTitle}: ${note.slice(0, 70)}`,
    type: "info",
    link: "/dashboard/requests",
  });

  return { ok: true, conversation: conversations[index] };
};

export interface ChatMessage {
  sender: "user" | "owner" | "system";
  text: string;
  senderName?: string;
  createdAt?: string;
  receipt?: TransactionReceiptAttachment;
}

export const getChatThread = (equipmentId: string, fallback: ChatMessage[] = []) => {
  if (typeof window === "undefined") return fallback;
  const threads = getStoredChatThreads();
  return threads[equipmentId] ?? fallback;
};

export const saveChatThread = (equipmentId: string, messages: ChatMessage[]) => {
  if (typeof window === "undefined") return;
  const threads = getStoredChatThreads();
  threads[equipmentId] = messages;
  browserStorage.setItem(CHAT_KEY, JSON.stringify(threads));
  void syncWorkflowState({ chatThreads: threads as Record<string, unknown[]> });

  const lastMessage = messages[messages.length - 1];
  const request = getAllTransactionRequests().find((item) => item.id === equipmentId || item.equipmentRefId === equipmentId);
  if (!lastMessage || !request) return;

  if (lastMessage.sender !== "system") {
    const recipient = lastMessage.sender === "user" ? request.owner : request.buyer;
    pushNotification({
      user: recipient,
      title: "New message",
      message: `${request.equipmentTitle}: ${lastMessage.text.slice(0, 70)}`,
      type: "info",
      link: getRequestDashboardPath(request.requestType),
    });
  }

  void persistTransactionRequestPatchToBackend(request.id, {
    chatMessages: messages,
  });
};

  /**
   * Create a lease request using the new dedicated lease API endpoint.
   * Falls back to local createTransactionRequest if backend is unavailable.
   */
  export const createLeaseRequestWithApi = async (
    payload: Omit<TransactionRequest, "id" | "createdAt" | "status">,
  ): Promise<TransactionRequest | null> => {
    const auth = getAuthState();
    const existingPendingRequest = findActiveTransactionRequest({
      equipmentRefId: payload.equipmentRefId,
      owner: payload.owner,
      buyer: payload.buyer,
      requestType: payload.requestType,
    });

    if (existingPendingRequest) {
      return existingPendingRequest;
    }
  
    // Try backend API first if available
    if (auth.token) {
      try {
        const { createLeaseRequestApi } = await import("@/lib/leaseApi");
        const result = await createLeaseRequestApi(auth.token, {
          equipmentId: payload.equipmentRefId,
          lessorUser: payload.owner,
          baseAmount: payload.baseAmount,
          requestedAmount: payload.requestedAmount,
          requestType: payload.requestType as any,
          terms: {
            pricingMode: payload.pricingMode,
            notes: payload.notes,
          },
          notes: payload.notes,
          metadata: {
            equipmentTitle: payload.equipmentTitle,
            owner: payload.owner,
            ownerIdentity: payload.ownerIdentity,
            ownerEmail: payload.ownerEmail,
            buyer: payload.buyer,
            buyerIdentity: payload.buyerIdentity,
            buyerPhone: payload.buyerPhone,
            buyerEmail: payload.buyerEmail,
            baseAmount: payload.baseAmount,
            requestedAmount: payload.requestedAmount,
            pricingMode: payload.pricingMode,
            escrowProtected: payload.escrowProtected,
            paymentOption: payload.paymentOption,
          },
        });

        // Merge API result into local storage for continuity
        const localRequest: TransactionRequest = {
          ...payload,
          id: result.conversation.threadId,
          createdAt: result.conversation.createdAt,
          status: "Pending",
          chatMessages: [
            {
              sender: "system",
              text: result.initialMessage.content,
              createdAt: result.initialMessage.createdAt,
            },
          ],
        };

        saveTransactionRequests([...getAllTransactionRequests(), localRequest]);
        saveChatThread(localRequest.id, localRequest.chatMessages);
        return localRequest;
      } catch (error) {
        console.warn("[lease] backend API failed, falling back to local storage:", error);
        // Fall through to local creation
      }
    }

    // Fallback to local creation
    return createTransactionRequest(payload);
  };
